import { BRAND_BLUE } from "@/assets/brand/colors";
import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from "react";
import { Link, useParams } from "react-router-dom";
import { CLIENT_PORTAL_PATH } from "@/features/clientPortal/routes";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  ArrowUpDown,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock,
  Eye,
  FileText,
  Info,
  Layers,
  Layout,
  Mail,
  MapPin,
  MessageSquare,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  Search,
  Settings,
  Trash2,
  Upload,
  User,
  X,
  type LucideIcon
} from "lucide-react";
import { toast } from "sonner";
import { LiaPassportSolid } from "@/components/icons/legacy";
import { BrandLogo, LoadingState } from "@/components/brand";
import { formatDateOnly } from "@/lib/dateOnly";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge } from "@/components/ui/status-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { portalI18n } from "@/features/clientPortal/i18n";
import type { SnippetAction } from "@/features/caseIde/pdfSnippet/toolbar";
import {
  hexToRgb,
  isDarkColor,
  sampleImageBackgroundColor
} from "../../features/foreignNationalOutgoingIntakes/components/intakeLogoColor";
import { ErrorState } from "../../components/common/ErrorState";
import { PdfPreview } from "../../components/common/PdfPreview";
import {
  PublicOutgoingIntakesAPI,
  type PublicIntakePrefill,
  type PublicIntakeRevisionEntry
} from "../../api/publicOutgoingIntakesApi";
import { IMMIGRATION_STATUS_OPTIONS_FLAT } from "../../constants/immigrationStatusOptions";
import { PreferenceCategory } from "../../api/types/case";
import type {
  OutgoingIntakeAnswer,
  OutgoingIntakeDuplicateMatch,
  OutgoingIntakeFile,
  OutgoingIntakeFormNode,
  OutgoingIntakeOption,
  OutgoingIntakeReviewDecision,
  OutgoingIntakeSubmission,
  WelcomeVideoTrim
} from "../../api/types/outgoingIntake";
import {
  buildHydratedAnswerState,
  createServerDraftSnapshot,
  mergeGroupInstances,
  pickPreferredDraft,
  readLocalDraftSnapshot,
  toMillis,
  type GroupInstance
} from "./hydration";
import { TypeformOptionCard } from "./TypeformOptionCard";
import { LinkGoneState, linkGoneFrom, type LinkGone } from "./LinkGoneState";
import { Uploader, UploaderLayout } from "../../components/Uploader";
import {
  findStepIndexForNode,
  flattenIntakeStepNodes,
  isDocumentsSectionNode
} from "../../features/foreignNationalOutgoingIntakes/intakeStepFlattening";
import { isNodeVisible } from "../../features/foreignNationalOutgoingIntakes/conditional";
import { attentionReason } from "../../features/requestsIde/editor/reviewModel";
import { WelcomeVideoPlayer } from "../../features/foreignNationalOutgoingIntakes/components/WelcomeVideoPlayer";
import {
  AskCasey,
  AssistantAnswerBadge,
  answerWithProposal,
  groupsWithProposal
} from "../../features/intakeAssistant";
import type {
  CaseyProposal,
  IntakeControlsLayout
} from "../../features/intakeAssistant";
import {
  contextualGroupQuestionTitle,
  formatDocumentRequestTitle,
  formatGroupAnswerValue,
  formatInformationRequestTitle,
  groupChildQuestions,
  groupEntryContextLabel,
  groupEntryIsCurrent,
  groupEntryLabel,
  groupEntrySortKey,
  groupEntrySummary,
  informationRequestDescription
} from "../../features/foreignNationalOutgoingIntakes/utils/intakeReviewPhrasing";
import { FileTypeIcon } from "../../components/common/FileTypeIcon";
import { IntakeLanguageBar } from "./IntakeLanguageBar";
import { IntakeDateInput } from "./IntakeDateInput";
import { translateIntake, useIntakeLanguage } from "./intakeLanguage";
import {
  dateInWords,
  placeholderKey,
  prefillDisplay,
  sectionByStep,
  sectionOutline
} from "./intakeModel";
import { PrefillConfirm } from "./PrefillConfirm";
import {
  answeredIdsOf,
  intakeEstimate
} from "../../features/foreignNationalOutgoingIntakes/intakeEstimate";

countries.registerLocale(enLocale);

export type IntakePayload = {
  outgoing_intake_id: string;
  title: string;
  status: string;
  schema: OutgoingIntakeFormNode[];
  expires_at?: unknown;
  latest_submission_id?: string | null;
  law_firm_name?: string | null;
  law_firm_logo_url?: string | null;
  enable_whitelabel_foreign_national_intake?: boolean | null;
  enable_firm_branding?: boolean | null;
  intake_background_color?: string | null;
  foreign_national_first_name?: string | null;
  foreign_national_last_name?: string | null;
  foreign_national_avatar_url?: string | null;
  attorney_name?: string | null;
  attorney_avatar_url?: string | null;
  attorney_note?: string | null;
  welcome_video_url?: string | null;
  welcome_video_trim?: WelcomeVideoTrim | null;
  client_language?: string | null;
  client_title?: string | null;
  prefill?: Record<string, PublicIntakePrefill> | null;
  viewer?: "client" | "firm" | null;
};

type Props = {
  /**
   * Preview mode renders the exact public intake UI but:
   * - skips API loading (uses `initialData`)
   * - disables upload/submit persistence
   */
  mode?: "public" | "preview";
  tokenOverride?: string;
  initialData?: {
    outgoing_intake: Partial<IntakePayload> & {
      schema: OutgoingIntakeFormNode[];
    };
    latest_submission?: OutgoingIntakeSubmission | null;
    /** Prior rounds, so per-question comment threads render in a preview. */
    revision_history?: PublicIntakeRevisionEntry[] | null;
  };
  /**
   * Hides the top bar (law firm/CaseBlink branding + "Preview" badge).
   * Used when this is embedded inside another screen that already has its
   * own chrome, e.g. the Review & Send preview panel.
   */
  hideTopBar?: boolean;
  /**
   * When true (with mode="preview"), renders at natural height inside a
   * parent scroll container instead of filling a fixed-height panel.
   */
  embeddedPreview?: boolean;
  /**
   * Responsive preview fills its parent viewport so narrow builder rails use
   * the same responsive layout a client would see at that width.
   */
  previewViewport?: "natural" | "responsive";
  /**
   * Preview-only: drive the active step from a specific schema node id. When
   * set, the preview jumps to the step whose `node_id` matches. Used by the
   * builder's left rail so selecting a question focuses the preview on it.
   */
  activeNodeId?: string | null;
  /**
   * Review-only: when the focused node is a repeatable group rendered as a
   * stack of entries, scroll the matching entry card into view. Paired with
   * `activeNodeId` (the group id) so the review TOC can target a single entry.
   */
  activeInstanceId?: string | null;
  /**
   * Review-only: when the focused node is a repeatable-group entry, scroll the
   * matching sub-question into view. Paired with `activeNodeId` (group id) and
   * `activeInstanceId` (entry id) so the review TOC can target one sub-answer.
   */
  activeQuestionId?: string | null;
  /**
   * Preview-only: skip the welcome step so the focused question shows
   * immediately (used together with `activeNodeId` in the builder editor).
   */
  focusSingleNode?: boolean;
  /**
   * Attorney review mode (used with mode="preview"). Renders the submitted
   * answers read-only and swaps the client Skip/OK CTA for Accept / Request
   * Changes controls, tints the active answer green/red by decision, and shows
   * a per-question comment history beneath the input.
   */
  /**
   * Renders the client experience as a historical record: every input is
   * read-only and nothing persists, but none of the attorney review chrome
   * that `reviewMode` adds is shown.
   */
  viewOnly?: boolean;
  /**
   * Pins the layout to the one-page form and skips the welcome screen, so the
   * whole intake is readable by scrolling instead of stepping through it.
   */
  forceSinglePage?: boolean;
  reviewMode?: boolean;
  /** Per-question decision status keyed by `decisionKey`. */
  reviewDecisionByKey?: Record<string, "pending" | "accepted" | "rejected">;
  /** Per-question in-progress attorney note keyed by `decisionKey`. */
  reviewNoteByKey?: Record<string, string>;
  /** Per-question derived comment thread keyed by `decisionKey`. */
  reviewCommentsByKey?: Record<string, ReviewCommentThreadItem[]>;
  /** Toggle/set a per-question decision. */
  onReviewDecision?: (
    key: string,
    status: "pending" | "accepted" | "rejected"
  ) => void;
  /** Emitted when the attorney posts their requested-change note for a question. */
  onReviewNoteChange?: (key: string, note: string) => void;
  /** Emitted when the active question changes (e.g. caret nav) so an external
   * table of contents can stay in sync. */
  onActiveNodeChange?: (nodeId: string | null) => void;
};

/** One entry in a question's cross-revision comment history. */
export type ReviewCommentThreadItem = {
  author: "client" | "attorney";
  name: string;
  text: string;
  at?: unknown;
  revisionNumber?: number;
  avatarUrl?: string | null;
  timeLabel?: string;
};

type IntakeFileReviewCheckItem = {
  type: "file_integrity" | "readability" | "duplicate";
  label: string;
  status?: string;
  message: string;
};

type PendingIntakeFileDelete = {
  file: OutgoingIntakeFile;
};

type AddressValue = {
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  country?: string | null;
};

/** Intake chrome copy in the client's language (the portal's strings). */
const tr = (key: string, options?: Record<string, unknown>): string =>
  String(portalI18n.t(`intake.${key}`, options));
const INTAKE_FILE_DELETE_FADE_MS = 420;
const GLASS_SURFACE_BG = "rgba(255, 255, 255, 0.58)";
const GLASS_SURFACE_BORDER = "rgba(255, 255, 255, 0.72)";
const GLASS_SURFACE_SHADOW =
  "0 24px 80px rgba(32, 28, 209, 0.14), inset 0 1px 0 rgba(255, 255, 255, 0.82)";
const SORT_PILL_TRIGGER_CLASS =
  "flex max-w-full min-w-0 cursor-pointer items-center gap-1 rounded-md bg-transparent p-0 text-subtext-color transition-colors hover:text-default-font focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-300";
const SORT_PILL_ICON_CLASS = "h-3.5 w-3.5 shrink-0 text-subtext-color";
const COUNTRY_OPTIONS = Object.values(
  countries.getNames("en", { select: "official" })
).sort((a, b) => (a as string).localeCompare(b as string)) as string[];

const toIntakeOption = (value: string): OutgoingIntakeOption => ({
  option_id: value,
  label: value
});

const COUNTRY_INTAKE_OPTIONS = COUNTRY_OPTIONS.map(toIntakeOption);
const IMMIGRATION_STATUS_INTAKE_OPTIONS =
  IMMIGRATION_STATUS_OPTIONS_FLAT.map(toIntakeOption);
const PREFERENCE_CATEGORY_INTAKE_OPTIONS =
  Object.values(PreferenceCategory).map(toIntakeOption);

type UploadedDocumentSortDir = "asc" | "desc";
type UploadedDocumentSortBy = "created_at" | "filename";
type UploadedDocumentSortSelection = {
  sortBy: UploadedDocumentSortBy;
  sortDir: UploadedDocumentSortDir;
};

const UPLOADED_DOCUMENT_SORT_OPTIONS: Array<{
  value: UploadedDocumentSortSelection;
  label: string;
}> = [
  {
    value: { sortBy: "created_at", sortDir: "desc" },
    label: "Upload Time (Newest)"
  },
  {
    value: { sortBy: "created_at", sortDir: "asc" },
    label: "Upload Time (Oldest)"
  },
  {
    value: { sortBy: "filename", sortDir: "asc" },
    label: "Filename (A-Z)"
  },
  {
    value: { sortBy: "filename", sortDir: "desc" },
    label: "Filename (Z-A)"
  }
];

const uploadedDocumentSortLabel = (
  sortBy: UploadedDocumentSortBy,
  sortDir: UploadedDocumentSortDir
) =>
  UPLOADED_DOCUMENT_SORT_OPTIONS.find(
    (option) =>
      option.value.sortBy === sortBy && option.value.sortDir === sortDir
  )?.label ?? "Upload Time (Newest)";

type TypeformSearchableSelectOption = {
  value: string;
  label: string;
};

type TypeformSearchableSelectProps = {
  options: TypeformSearchableSelectOption[];
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  /** Brand page background the dropdown sits on (hex). */
  pageBg?: string;
  /** Legible foreground as an "r, g, b" triple derived from `pageBg`. */
  fgRgb?: string;
};

const TypeformSearchableSelect: React.FC<TypeformSearchableSelectProps> = ({
  options,
  value,
  placeholder,
  disabled = false,
  onChange,
  pageBg = BRAND_BLUE,
  fgRgb = "255, 255, 255"
}) => {
  const listboxId = useId();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value]
  );
  const normalizedQuery = query.trim().toLowerCase();
  const filteredOptions = useMemo(() => {
    if (!normalizedQuery) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(normalizedQuery)
    );
  }, [normalizedQuery, options]);
  const inputValue = isOpen ? query : selectedOption?.label ?? "";

  useEffect(() => {
    setHighlightedIndex(0);
  }, [normalizedQuery]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = ({ target }: PointerEvent) => {
      if (target instanceof Node && containerRef.current?.contains(target)) {
        return;
      }
      setIsOpen(false);
      setQuery("");
    };

    window.addEventListener("pointerdown", handlePointerDown);
    return () => window.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen]);

  const chooseOption = (option: TypeformSearchableSelectOption) => {
    if (disabled) return;
    onChange(option.value);
    setIsOpen(false);
    setQuery("");
  };

  // Derive the dropdown surface from the brand background rather than a fixed
  // blue so the menu stays cohesive with any firm background. The panel matches
  // the page color; option pills/text use luminance-aware foreground overlays.
  const fg = (alpha: number) => `rgba(${fgRgb}, ${alpha})`;

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative w-full">
        <input
          type="text"
          disabled={disabled}
          role="combobox"
          aria-controls={listboxId}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          className="typeform-select-input h-14 w-full border-x-0 border-b border-t-0 border-solid bg-transparent px-0 pr-10 text-2xl leading-tight outline-hidden transition disabled:cursor-not-allowed disabled:opacity-60"
          style={{
            borderBottomColor: isOpen ? fg(0.85) : fg(0.55),
            color: fg(1)
          }}
          placeholder={placeholder ?? tr("typeOrSelect")}
          value={inputValue}
          onFocus={() => {
            if (disabled) return;
            setIsOpen(true);
            setQuery("");
          }}
          onChange={(event) => {
            if (disabled) return;
            setQuery(event.target.value);
            setIsOpen(true);
          }}
          onKeyDown={(event) => {
            if (disabled) return;
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setIsOpen(true);
              setHighlightedIndex((current) =>
                Math.min(current + 1, Math.max(filteredOptions.length - 1, 0))
              );
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              setHighlightedIndex((current) => Math.max(current - 1, 0));
            } else if (event.key === "Enter" && isOpen) {
              const option = filteredOptions[highlightedIndex];
              if (option) {
                event.preventDefault();
                chooseOption(option);
              }
            } else if (event.key === "Escape") {
              setIsOpen(false);
              setQuery("");
            }
          }}
        />
        <button
          type="button"
          disabled={disabled}
          className="absolute right-0 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center transition disabled:cursor-not-allowed disabled:opacity-60"
          style={{ color: fg(0.78) }}
          aria-label={isOpen ? "Close options" : "Open options"}
          onClick={() => {
            if (disabled) return;
            setIsOpen((current) => !current);
            setQuery("");
          }}
        >
          <IntakeIcon
            className="text-body font-body"
            name={isOpen ? "FeatherSearch" : "FeatherChevronDown"}
          />
        </button>
      </div>

      <div
        className={[
          "overflow-hidden transition-[max-height,margin,opacity] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]",
          isOpen
            ? "mb-3 mt-3 max-h-95 opacity-100"
            : "pointer-events-none mb-0 mt-0 max-h-0 opacity-0"
        ].join(" ")}
      >
        <div
          id={listboxId}
          role="listbox"
          aria-hidden={!isOpen}
          className={[
            "relative z-10 max-h-85 overflow-y-auto rounded-2xl border border-solid p-3 shadow-[0_24px_80px_rgba(0,0,0,0.30)] backdrop-blur-xl",
            "transition-[transform,opacity] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]",
            isOpen ? "translate-y-0 scale-100" : "-translate-y-2 scale-[0.98]"
          ].join(" ")}
          style={{
            backgroundColor: pageBg,
            borderColor: fg(0.12)
          }}
        >
          <div className="flex w-full flex-col gap-2">
            {filteredOptions.length ? (
              filteredOptions.map((option, index) => {
                const isSelected = option.value === value;
                const isHighlighted = index === highlightedIndex;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={isOpen ? 0 : -1}
                    className="flex min-h-11 w-full items-center gap-3 rounded-lg border border-solid px-4 py-2.5 text-left transition"
                    style={{
                      color: fg(1),
                      borderColor: isSelected
                        ? fg(0.45)
                        : isHighlighted
                        ? fg(0.26)
                        : fg(0.1),
                      backgroundColor: isSelected
                        ? fg(0.18)
                        : isHighlighted
                        ? fg(0.12)
                        : fg(0.06),
                      boxShadow: isSelected
                        ? `0 0 0 1px ${fg(0.16)}`
                        : undefined
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => chooseOption(option)}
                  >
                    <span
                      className="min-w-0 flex-1 truncate text-body font-body"
                      style={{ color: fg(1) }}
                    >
                      {option.label}
                    </span>
                    {isSelected ? (
                      <IntakeIcon
                        className="text-body font-body"
                        style={{ color: fg(1) }}
                        name="FeatherCheck"
                      />
                    ) : null}
                  </button>
                );
              })
            ) : (
              <div
                className="rounded-lg border border-solid px-4 py-3 text-body font-body"
                style={{
                  borderColor: fg(0.1),
                  backgroundColor: fg(0.04),
                  color: fg(0.7)
                }}
              >
                {tr("noMatches")}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const getOutgoingIntakeFileUploadTimestamp = (file: OutgoingIntakeFile) => {
  const uploadedAt = file.uploaded_at as unknown;
  if (!uploadedAt) return 0;
  if (typeof uploadedAt === "string") {
    const parsed = Date.parse(uploadedAt);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (typeof uploadedAt === "number") return uploadedAt;
  if (uploadedAt instanceof Date) return uploadedAt.getTime();
  if (typeof uploadedAt === "object") {
    const timestamp = uploadedAt as {
      seconds?: number;
      nanoseconds?: number;
      [key: string]: unknown;
    };
    const fallbackSeconds = Reflect.get(timestamp, "_seconds");
    const fallbackNanoseconds = Reflect.get(timestamp, "_nanoseconds");
    const seconds =
      timestamp.seconds ??
      (typeof fallbackSeconds === "number" ? fallbackSeconds : undefined);
    const nanoseconds =
      timestamp.nanoseconds ??
      (typeof fallbackNanoseconds === "number" ? fallbackNanoseconds : 0);
    if (typeof seconds === "number") {
      return seconds * 1000 + Math.floor(nanoseconds / 1000000);
    }
  }
  return 0;
};

const isActionableCheckStatus = (status?: string | null) =>
  status === "warning" || status === "failed";

const getFallbackWarningMessage = (
  file: OutgoingIntakeFile,
  type: IntakeFileReviewCheckItem["type"]
) => file.reviewWarnings?.find((warning) => warning.type === type)?.message;

const getIntakeFileReviewCheckItems = (
  file: OutgoingIntakeFile,
  suppressedDuplicateMatchIds?: Set<string>
): IntakeFileReviewCheckItem[] => {
  const checks: IntakeFileReviewCheckItem[] = [];
  if (isActionableCheckStatus(file.fileIntegrity?.status)) {
    checks.push({
      type: "file_integrity",
      label: "File integrity",
      status: file.fileIntegrity?.status,
      message:
        file.fileIntegrity?.message ||
        getFallbackWarningMessage(file, "file_integrity") ||
        "This file may be broken."
    });
  }
  if (isActionableCheckStatus(file.readability?.status)) {
    checks.push({
      type: "readability",
      label: "Readability",
      status: file.readability?.status,
      message:
        file.readability?.message ||
        getFallbackWarningMessage(file, "readability") ||
        "This file may be difficult to read."
    });
  }
  const visibleDuplicateMatches = (file.duplicate?.matches ?? []).filter(
    (match) => !suppressedDuplicateMatchIds?.has(match.id)
  );
  if (
    isActionableCheckStatus(file.duplicate?.status) &&
    visibleDuplicateMatches.length > 0
  ) {
    checks.push({
      type: "duplicate",
      label: "Duplicate",
      status: file.duplicate?.status,
      message:
        file.duplicate?.message ||
        getFallbackWarningMessage(file, "duplicate") ||
        "Possible duplicate evidence."
    });
  }
  return checks;
};

const getPrimaryDuplicateMatch = (
  file: OutgoingIntakeFile,
  suppressedDuplicateMatchIds?: Set<string>
): OutgoingIntakeDuplicateMatch | null => {
  const match = file.duplicate?.matches?.find(
    (candidate) => !suppressedDuplicateMatchIds?.has(candidate.id)
  );
  if (!match || typeof match.id !== "string") return null;
  return {
    id: match.id,
    source: typeof match.source === "string" ? match.source : undefined,
    docNames: typeof match.docNames === "string" ? match.docNames : undefined
  };
};

const getDuplicatePreviewKey = (match: OutgoingIntakeDuplicateMatch) =>
  `${match.source || "unknown"}:${match.id}`;

const canPreviewIntakeFile = (file: OutgoingIntakeFile) =>
  file.previewStatus === "ready" || Boolean(file.pdfFilePath);

const isIntakeFilePreviewGenerating = (file: OutgoingIntakeFile) =>
  !canPreviewIntakeFile(file) && file.previewStatus === "pending";

const getIntakeFilePreviewUnavailableMessage = (file: OutgoingIntakeFile) => {
  if (file.previewStatus === "failed") {
    return file.previewError || "Preview could not be generated for this file.";
  }
  if (file.previewStatus === "skipped") {
    return file.previewError || "Preview is not available for this file type.";
  }
  return "Preview is not available for this file.";
};

function normalizeText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

const INTAKE_ICONS = {
  FeatherAlertCircle: AlertCircle,
  FeatherAlertTriangle: AlertTriangle,
  FeatherArrowLeft: ArrowLeft,
  FeatherArrowRight: ArrowRight,
  FeatherArrowUpDown: ArrowUpDown,
  FeatherCheck: Check,
  FeatherCheckCircle: CheckCircle,
  FeatherChevronDown: ChevronDown,
  FeatherChevronRight: ChevronRight,
  FeatherChevronUp: ChevronUp,
  FeatherClock: Clock,
  FeatherEye: Eye,
  FeatherFileText: FileText,
  FeatherInfo: Info,
  FeatherLayers: Layers,
  FeatherLayout: Layout,
  FeatherMail: Mail,
  FeatherMapPin: MapPin,
  FeatherMessageSquare: MessageSquare,
  FeatherPencil: Pencil,
  FeatherPlus: Plus,
  FeatherRotateCcw: RotateCcw,
  FeatherSave: Save,
  FeatherSearch: Search,
  FeatherSettings: Settings,
  FeatherTrash2: Trash2,
  FeatherUpload: Upload,
  FeatherUser: User,
  FeatherX: X
} satisfies Record<string, LucideIcon>;

type IntakeIconName = keyof typeof INTAKE_ICONS;

/**
 * A lucide glyph sized at `1em` inside an inline-flex span, so the intake's
 * `text-*` classes (and the theme overrides keyed on them) keep sizing and
 * coloring icons, including inside `Button`, whose svg size and color rules
 * would otherwise win.
 */
function IntakeIcon({
  name,
  className,
  ...props
}: Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> & {
  name: IntakeIconName;
}) {
  const Glyph = INTAKE_ICONS[name];
  return (
    <span
      className={cn("inline-flex shrink-0 items-center", className)}
      {...props}
    >
      <Glyph
        className="text-current"
        style={{ width: "1em", height: "1em" }}
        strokeWidth={2}
        aria-hidden
      />
    </span>
  );
}

type IntakeToastVariant = "brand" | "neutral" | "error" | "success";

function toastIconForVariant(variant: IntakeToastVariant): IntakeIconName {
  if (variant === "error") return "FeatherAlertCircle";
  if (variant === "neutral") return "FeatherInfo";
  if (variant === "brand") return "FeatherInfo";
  return "FeatherCheckCircle";
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false
  );
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    )
      return undefined;
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, [query]);
  return matches;
}

/**
 * Nested style rules for the intake's runtime-themed CSS. Keys are CSS
 * properties (camelCase), nested selectors (`&` is the scoped element; a key
 * without `&` targets descendants), `@media` blocks or `@keyframes`.
 */
type IntakeStyleRules = { [key: string]: string | number | IntakeStyleRules };

const cssProperty = (prop: string): string =>
  prop.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

function compileIntakeCss(
  rules: IntakeStyleRules,
  selector: string,
  out: string[],
  media?: string
): void {
  const decls: string[] = [];
  const declSlot = out.length;
  out.push("");
  for (const [key, value] of Object.entries(rules)) {
    if (typeof value !== "object") {
      decls.push(`${cssProperty(key)}:${value}`);
    } else if (key.startsWith("@keyframes")) {
      const frames = Object.entries(value)
        .map(([frame, frameDecls]) => {
          const body =
            typeof frameDecls === "object"
              ? Object.entries(frameDecls)
                  .filter(([, v]) => typeof v !== "object")
                  .map(([p, v]) => `${cssProperty(p)}:${String(v)}`)
                  .join(";")
              : "";
          return `${frame}{${body}}`;
        })
        .join("");
      out.push(`${key.trim()}{${frames}}`);
    } else if (key.startsWith("@media")) {
      const query = key.slice(6).trim();
      compileIntakeCss(
        value,
        selector,
        out,
        media ? `${media} and ${query}` : query
      );
    } else {
      for (const part of key.split(",")) {
        const trimmed = part.trim();
        const nested = trimmed.includes("&")
          ? trimmed.replace(/&/g, selector)
          : `${selector} ${trimmed}`;
        compileIntakeCss(value, nested, out, media);
      }
    }
  }
  if (decls.length) {
    const rule = `${selector}{${decls.join(";")}}`;
    out[declSlot] = media ? `@media ${media}{${rule}}` : rule;
  }
}

/**
 * A div whose `styles` compile to a `<style>` scoped to it. Carries the
 * intake's runtime brand colors and selector-based overrides, which Tailwind
 * classes can't express.
 */
function IntakeScopedDiv({
  styles,
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & { styles: IntakeStyleRules }) {
  const reactId = useId();
  const scopeClass = `intake-scope-${reactId.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const out: string[] = [];
  compileIntakeCss(styles, `.${scopeClass}`, out);
  const cssText = out.filter(Boolean).join("\n");
  return (
    <div className={cn(scopeClass, className)} {...props}>
      <style>{cssText}</style>
      {children}
    </div>
  );
}

function IntakeAvatar({
  image,
  variant = "neutral",
  size = "xs",
  className,
  children
}: {
  image?: string;
  variant?: "neutral" | "error";
  size?: "xs" | "sm";
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <Avatar size={size} className={className}>
      {image ? <AvatarImage src={image} alt="" draggable={false} /> : null}
      <AvatarFallback
        className={
          variant === "error" ? "bg-danger-soft text-danger" : undefined
        }
      >
        {children}
      </AvatarFallback>
    </Avatar>
  );
}

function IntakeTextField({
  label,
  error = false,
  className,
  ...inputProps
}: React.ComponentProps<"input"> & { label?: string; error?: boolean }) {
  const inputId = useId();
  return (
    <div className={cn("flex flex-col items-start gap-1", className)}>
      {label ? (
        <Label
          htmlFor={inputId}
          className="text-caption-bold font-caption-bold text-default-font"
        >
          {label}
        </Label>
      ) : null}
      <Input id={inputId} aria-invalid={error || undefined} {...inputProps} />
    </div>
  );
}

function asNodes(value: unknown): OutgoingIntakeFormNode[] {
  return Array.isArray(value) ? (value as OutgoingIntakeFormNode[]) : [];
}

function flatQuestions(
  nodes: OutgoingIntakeFormNode[]
): OutgoingIntakeFormNode[] {
  const out: OutgoingIntakeFormNode[] = [];
  const walk = (n: OutgoingIntakeFormNode) => {
    if (n.node_type === "question") out.push(n);
    (n.children ?? []).forEach(walk);
  };
  nodes.forEach(walk);
  return out;
}

function decisionKey(d: {
  question_id: string;
  repeatable_group_id?: string | null;
  repeatable_group_instance_id?: string | null;
}): string {
  return [
    String(d.question_id ?? "").trim(),
    String(d.repeatable_group_id ?? "").trim(),
    String(d.repeatable_group_instance_id ?? "").trim()
  ].join("|");
}

function asAddressValue(value: unknown): AddressValue {
  if (!value || typeof value !== "object") return {};
  const v = value as Record<string, unknown>;
  return {
    street: typeof v.street === "string" ? v.street : null,
    city: typeof v.city === "string" ? v.city : null,
    state: typeof v.state === "string" ? v.state : null,
    zip_code: typeof v.zip_code === "string" ? v.zip_code : null,
    country: typeof v.country === "string" ? v.country : null
  };
}

// Human-readable label for a submitted answer value, used for the "You
// submitted:" line on the revision comment card. Mirrors how each question
// type renders its value elsewhere in the flow.
function formatAnswerValueLabel(
  q: OutgoingIntakeFormNode,
  answer: OutgoingIntakeAnswer | null
): string {
  if (!answer) return "—";
  const files0 = answer.files ?? [];
  const v0 = answer.value;

  const optionLabel = (id: unknown): string => {
    const raw = String(id ?? "");
    const opt = (q.options ?? []).find((o) => String(o.option_id) === raw);
    return String(opt?.label ?? raw);
  };

  if (q.question_type === "file") {
    if (files0.length === 0) return "—";
    if (files0.length === 1) return String(files0[0]?.filename ?? "File");
    return `${files0.length} files`;
  }
  if (q.question_type === "address") {
    const addr = asAddressValue(v0);
    const parts = [
      (addr.street ?? "").trim(),
      (addr.city ?? "").trim(),
      (addr.state ?? "").trim(),
      (addr.zip_code ?? "").trim(),
      (addr.country ?? "").trim()
    ].filter(Boolean);
    return parts.length ? parts.join(", ") : "—";
  }
  if (q.question_type === "boolean") {
    return v0 ? tr("yes") : tr("no");
  }
  if (q.question_type === "single_select") {
    return typeof v0 === "string" ? optionLabel(v0) || "—" : "—";
  }
  if (q.question_type === "multi_select") {
    const arr = Array.isArray(v0) ? v0 : [];
    const labels = arr.map(optionLabel).filter(Boolean);
    return labels.length ? labels.join(", ") : "—";
  }
  if (q.question_type === "date") {
    return (typeof v0 === "string" && formatDateOnly(v0)) || "—";
  }
  if (typeof v0 === "string") return v0.trim() || "—";
  if (typeof v0 === "number") return String(v0);
  if (v0 == null) return "—";
  try {
    return JSON.stringify(v0);
  } catch {
    return String(v0);
  }
}

function hasMeaningfulValue(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === "boolean") return true;
  if (typeof value === "number") return !Number.isNaN(value);
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") {
    const rec = value as Record<string, unknown>;
    return Object.values(rec).some(
      (v) => typeof v === "string" && v.trim().length > 0
    );
  }
  return false;
}

function isAnswerProvided(ans: OutgoingIntakeAnswer | null): boolean {
  if (!ans) return false;
  if ((ans.files ?? []).length > 0) return true;
  return hasMeaningfulValue(ans.value);
}

const SECTION_HEADER_TEXT_CLASSNAME =
  "text-caption-bold font-caption-bold text-subtext-color uppercase";

const QUESTION_LABEL_TEXT_CLASSNAME =
  "text-body-bold font-body-bold text-default-font";

const QUESTION_VALUE_LABEL_TEXT_CLASSNAME =
  "text-body-bold font-body-bold text-default-font";

const INTAKE_TABLE_HEAD_CLASSNAME =
  "h-8 text-caption-bold font-caption-bold text-subtext-color";

const INTAKE_TABLE_ROW_CLASSNAME =
  "border-t border-b-0 border-solid border-neutral-border hover:bg-transparent";

const INTAKE_TABLE_CELL_CLASSNAME = "h-auto p-0 whitespace-normal";

const INTAKE_TABLE_BODY_CLASSNAME =
  "border-b border-solid border-neutral-border";

const INTAKE_DISPLAY_MENU_CLASSNAME =
  "intake-display-menu z-2147483647 flex w-auto min-w-56 flex-col gap-0.5 rounded-xl bg-elevated p-1.5 shadow-[0_16px_40px_rgba(15,23,42,0.18)]";

const INTAKE_DISPLAY_MENU_LABEL_CLASSNAME =
  "px-3 pb-1 pt-1.5 text-xs font-semibold uppercase tracking-[0.07em] text-fg-4";

const INTAKE_DISPLAY_MENU_ITEM_CLASSNAME =
  "h-auto w-full cursor-pointer gap-3 rounded-lg px-2.5 py-2 transition-colors hover:bg-fill-1 focus:bg-fill-1";

function toTitleCase(raw: string): string {
  const input = String(raw ?? "").trim();
  if (!input) return "";
  // Title-case each "word", preserving common separators and acronyms/digit tokens.
  return input
    .split(/\s+/)
    .map((token) => {
      const parts = token.split(/([/'-])/g);
      return parts
        .map((p) => {
          if (p === "-" || p === "/" || p === "'") return p;
          if (!p) return p;
          // Preserve tokens with digits or already-uppercase tokens (acronyms).
          if (/\d/.test(p) || p.toUpperCase() === p) return p;
          return `${p[0]?.toUpperCase() ?? ""}${p.slice(1).toLowerCase()}`;
        })
        .join("");
    })
    .join(" ");
}

function isUscisFormNode(
  node: OutgoingIntakeFormNode | null | undefined
): boolean {
  return normalizeText(node?.template_source).startsWith("uscis_form:");
}

function isCustomQuestionNode(
  node: OutgoingIntakeFormNode | null | undefined
): boolean {
  return normalizeText(node?.template_source) === "custom";
}

function displayQuestionLabel(
  node: OutgoingIntakeFormNode,
  fallback: string
): string {
  const raw = String(node.prompt ?? node.title ?? fallback).trim();
  return isUscisFormNode(node) || isCustomQuestionNode(node)
    ? raw
    : toTitleCase(raw);
}

function displayOptionLabel(
  node: OutgoingIntakeFormNode,
  label: string
): string {
  return isUscisFormNode(node) ? label : toTitleCase(label);
}

function getForcedSingleSelectOptions(
  node: OutgoingIntakeFormNode
): OutgoingIntakeOption[] | null {
  const fieldKey =
    normalizeText(node.model_path) || normalizeText(node.node_id);
  if (fieldKey === "foreign_national.country_of_birth") {
    return COUNTRY_INTAKE_OPTIONS;
  }
  if (fieldKey === "foreign_national.country_of_citizenship") {
    return COUNTRY_INTAKE_OPTIONS;
  }
  if (fieldKey === "foreign_national.current_immigration_status") {
    return IMMIGRATION_STATUS_INTAKE_OPTIONS;
  }
  if (fieldKey === "foreign_national.preference_category") {
    return PREFERENCE_CATEGORY_INTAKE_OPTIONS;
  }
  return null;
}

function getTypeformInputPlaceholder(node: OutgoingIntakeFormNode): string {
  const key = placeholderKey(node);
  return key ? tr(`ph.${key}`) : "";
}

function sectionHeaderIcon(title: string): React.ReactNode {
  const t = (title || "").trim().toLowerCase();
  if (t === "biographical information") {
    return (
      <IntakeIcon
        className="text-body font-body text-subtext-color"
        name="FeatherUser"
        aria-hidden
      />
    );
  }
  if (t === "contact information") {
    return (
      <IntakeIcon
        className="text-body font-body text-subtext-color"
        name="FeatherMail"
        aria-hidden
      />
    );
  }
  if (t === "current addresses") {
    return (
      <IntakeIcon
        className="text-body font-body text-subtext-color"
        name="FeatherMapPin"
        aria-hidden
      />
    );
  }
  if (t === "immigration information") {
    return <LiaPassportSolid className="h-4 w-4 shrink-0 text-subtext-color" />;
  }
  return null;
}

function addLabelForGroup(groupId: string, title?: string | null): string {
  if (groupId === "address_history") return "Add Address";
  if (groupId === "employment_history") return "Add Employment";
  if (groupId === "immigration_status_history") return "Add Status";
  if (groupId === "immigration_entry_exit_history") return "Add Travel";
  const base = (title ?? "").trim() || "Entry";
  return `Add ${base}`;
}

/**
 * A single full-screen "step" in the client-facing Typeform flow. Repeatable
 * groups are expanded from one static step into a dynamic sequence of screens:
 * an optional lead-in yes/no, one screen per sub-question per entry, and an
 * "add another?" decision screen between entries. `stepIndex` is the index of
 * the owning top-level step (from `flattenIntakeStepNodes`) so the progress
 * rail can still group a repeatable group under a single item.
 */
type TypeformScreen =
  | {
      kind: "question";
      key: string;
      stepIndex: number;
      node: OutgoingIntakeFormNode;
    }
  | {
      kind: "group-lead-in";
      key: string;
      stepIndex: number;
      group: OutgoingIntakeFormNode;
    }
  | {
      kind: "group-question";
      key: string;
      stepIndex: number;
      group: OutgoingIntakeFormNode;
      instanceId: string;
      entryIndex: number;
      node: OutgoingIntakeFormNode;
    }
  | {
      kind: "group-add-another";
      key: string;
      stepIndex: number;
      group: OutgoingIntakeFormNode;
      entryIndex: number;
    }
  | {
      kind: "group-review";
      key: string;
      stepIndex: number;
      group: OutgoingIntakeFormNode;
    }
  | {
      // Attorney review only: the whole repeatable group rendered as one screen
      // with every entry stacked as its own card (not one sub-question at a
      // time), so each submitted entry is an obvious, individually reviewable
      // unit.
      kind: "group-stack";
      key: string;
      stepIndex: number;
      group: OutgoingIntakeFormNode;
    }
  | {
      kind: "final-review";
      key: string;
      stepIndex: number;
    };

function createGroupInstanceId(): string {
  return `i_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

/**
 * Canonical statement-style question title for a repeatable group, so it reads
 * like any other question (e.g. "Tell us about your employment history").
 */
function groupQuestionTitle(group: OutgoingIntakeFormNode): string {
  const groupId = String(group.node_id ?? "");
  if (groupId === "address_history") return tr("group.tellUsAddresses");
  if (groupId === "employment_history") return tr("group.tellUsEmployment");
  if (groupId === "immigration_status_history") {
    return tr("group.tellUsStatuses");
  }
  if (groupId === "immigration_entry_exit_history") {
    return tr("group.tellUsTrips");
  }
  if (groupId === "dependents") return tr("group.tellUsDependents");
  const base = normalizeText(group.title) || tr("group.entries");
  return tr("group.tellUs", { name: base.toLowerCase() });
}

/** Lead-in yes/no prompt shown before a min_items===0 group's entries. */
function groupLeadInPrompt(group: OutgoingIntakeFormNode): string {
  const groupId = String(group.node_id ?? "");
  if (groupId === "address_history") return tr("group.leadInAddresses");
  if (groupId === "employment_history") return tr("group.leadInEmployment");
  if (groupId === "immigration_status_history") {
    return tr("group.leadInStatuses");
  }
  if (groupId === "immigration_entry_exit_history") {
    return tr("group.leadInTrips");
  }
  const base = normalizeText(group.title) || tr("group.entries");
  return tr("group.leadIn", { name: base.toLowerCase() });
}

/** "Add another …?" prompt shown after each completed entry of a group. */
function groupAddAnotherPrompt(group: OutgoingIntakeFormNode): string {
  const groupId = String(group.node_id ?? "");
  if (groupId === "address_history") return tr("group.addAnotherAddress");
  if (groupId === "employment_history") return tr("group.addAnotherEmployer");
  if (groupId === "immigration_status_history") {
    return tr("group.addAnotherStatus");
  }
  if (groupId === "immigration_entry_exit_history") {
    return tr("group.addAnotherTrip");
  }
  const base = groupEntryLabel(group);
  return tr("group.addAnother", { name: base.toLowerCase() });
}

/**
 * Animated container for a review entry's detail list. Keeps the content
 * mounted and transitions its measured height (and opacity) so entries expand
 * and collapse smoothly. Height is measured from the content so the collapse
 * animation ends at the correct point regardless of how many rows an entry has.
 */
function ReviewEntryDetails({
  id,
  isExpanded,
  children
}: {
  id: string;
  isExpanded: boolean;
  children: React.ReactNode;
}) {
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [maxHeight, setMaxHeight] = useState(0);

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    setMaxHeight(isExpanded ? el.scrollHeight : 0);
  }, [isExpanded, children]);

  return (
    <div
      id={id}
      style={{ maxHeight: `${maxHeight}px` }}
      className={`w-full overflow-hidden transition-[max-height,opacity] duration-200 ease-out ${
        isExpanded ? "opacity-100" : "opacity-0"
      }`}
    >
      <div ref={innerRef}>{children}</div>
    </div>
  );
}

/** Title for the review screen that lists all of a group's entries. */
function groupReviewPrompt(group: OutgoingIntakeFormNode): string {
  const groupId = String(group.node_id ?? "");
  if (groupId === "address_history") return tr("group.reviewAddresses");
  if (groupId === "employment_history") return tr("group.reviewEmployers");
  if (groupId === "immigration_status_history") {
    return tr("group.reviewStatuses");
  }
  if (groupId === "immigration_entry_exit_history") {
    return tr("group.reviewTrips");
  }
  const base = normalizeText(group.title) || tr("group.entries");
  return tr("group.review", { name: base.toLowerCase() });
}

export function PublicOutgoingIntake({
  mode = "public",
  tokenOverride,
  initialData,
  hideTopBar = false,
  embeddedPreview = false,
  previewViewport = "natural",
  activeNodeId = null,
  activeInstanceId = null,
  activeQuestionId = null,
  focusSingleNode = false,
  viewOnly = false,
  forceSinglePage = false,
  reviewMode = false,
  reviewDecisionByKey,
  reviewNoteByKey,
  reviewCommentsByKey,
  onReviewDecision,
  onReviewNoteChange,
  onActiveNodeChange
}: Props = {}) {
  const { token } = useParams<{ token: string }>();
  const safeToken = String(tokenOverride ?? token ?? "").trim();

  const previewKey = useMemo(() => {
    try {
      return (
        new URLSearchParams(window.location.search).get("previewKey") ?? ""
      ).trim();
    } catch {
      return "";
    }
  }, []);

  const urlPreviewData = useMemo(() => {
    if (!previewKey) return null;
    if (safeToken !== "__preview__") return null;
    try {
      const raw = window.localStorage.getItem(previewKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as unknown;
      if (!parsed || typeof parsed !== "object") return null;
      return parsed as Props["initialData"];
    } catch {
      return null;
    }
  }, [previewKey, safeToken]);

  // If user opened "/intakes/__preview__?previewKey=..." treat it as preview mode
  // even if storage is missing (we'll show an error instead of calling the API).
  const isUrlPreview = safeToken === "__preview__" && Boolean(previewKey);
  const isPreview =
    mode === "preview" || isUrlPreview || Boolean(urlPreviewData);
  const isAttorneyReadOnly = useMemo(() => {
    if (viewOnly) return true;
    try {
      return (
        (
          new URLSearchParams(window.location.search).get("readonly") ?? ""
        ).trim() === "1"
      );
    } catch {
      return false;
    }
  }, [viewOnly]);
  const effectiveInitialData = initialData ?? urlPreviewData ?? undefined;
  const fromPortal = useMemo(() => {
    try {
      return (
        new URLSearchParams(window.location.search).get("from") === "portal"
      );
    } catch {
      return false;
    }
  }, []);

  const showToast = useCallback(
    ({
      variant,
      title,
      description,
      icon,
      durationMs = 3000
    }: {
      variant: IntakeToastVariant;
      title: string;
      description?: string;
      icon?: IntakeIconName;
      durationMs?: number;
    }) => {
      const options = {
        description: normalizeText(description) || "Done.",
        icon: <IntakeIcon name={icon ?? toastIconForVariant(variant)} />,
        duration: durationMs,
        position: "bottom-right" as const
      };
      if (variant === "error") toast.error(title, options);
      else if (variant === "success") toast.success(title, options);
      else toast.info(title, options);
    },
    []
  );

  const [isLoading, setIsLoading] = useState<boolean>(() => {
    if (isPreview && effectiveInitialData) return false;
    return true;
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [linkGone, setLinkGone] = useState<LinkGone | null>(null);
  const [sourceIntake, setIntake] = useState<IntakePayload | null>(null);
  // An attorney opening the client's own link: it previews, nothing saves.
  const isFirmViewer = !isPreview && sourceIntake?.viewer === "firm";
  const isPersistenceDisabled = isPreview || isAttorneyReadOnly || isFirmViewer;
  const intakeLanguage = useIntakeLanguage({
    token: isPreview ? null : safeToken || null,
    clientLanguage: sourceIntake?.client_language
  });
  const intake = useMemo(
    () => translateIntake(sourceIntake, intakeLanguage.strings),
    [sourceIntake, intakeLanguage.strings]
  );
  const [latestSubmission, setLatestSubmission] =
    useState<OutgoingIntakeSubmission | null>(null);
  // Trimmed prior submissions used to render the per-question cross-revision
  // comment thread (client comments + attorney requested-change notes).
  const [revisionHistory, setRevisionHistory] = useState<
    PublicIntakeRevisionEntry[]
  >([]);
  // Which question's comment composer the client has explicitly opened (keyed
  // by decisionKey). The composer also opens implicitly when a draft comment
  // already exists for the active question.
  const [commentComposerOpenKey, setCommentComposerOpenKey] = useState<
    string | null
  >(null);
  // Local, uncommitted compose drafts keyed by decisionKey. The textarea binds
  // to the draft and only writes to the canonical store on an explicit "Post";
  // "Cancel" discards the draft. Presence of a key here means the composer for
  // that question is in the editing state.
  const [clientCommentDrafts, setClientCommentDrafts] = useState<
    Record<string, string>
  >({});
  const [reviewNoteDrafts, setReviewNoteDrafts] = useState<
    Record<string, string>
  >({});
  const [isBrandBackgroundResolving, setIsBrandBackgroundResolving] =
    useState(false);

  const [answersById, setAnswersById] = useState<
    Record<string, OutgoingIntakeAnswer>
  >({});
  const [groups, setGroups] = useState<Record<string, GroupInstance[]>>({});
  // Prefilled questions the client chose to change, so the confirm card stays
  // away even if they clear the field.
  const [prefillDismissed, setPrefillDismissed] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  // "Yes, that's right" moves on once the confirmed value is in state.
  const advanceAfterPrefillRef = useRef(false);
  // Explicit lead-in yes/no decision per repeatable group (min_items===0).
  // Needed because zero instances alone can't distinguish "not answered yet"
  // from "declined".
  const [groupDecisions, setGroupDecisions] = useState<
    Record<string, "yes" | "no">
  >({});
  // Transient yes/no selection on the current "Add another?" screen, committed
  // when the user advances.
  const [addAnotherChoice, setAddAnotherChoice] = useState<
    Record<string, "yes" | "no">
  >({});
  // After the screen list grows/shrinks (add/remove an entry from the review
  // screen), snap navigation to the screen identified by this stable key.
  const [pendingScreenKey, setPendingScreenKey] = useState<string | null>(null);
  // When set, the next forward advance returns to this screen key instead of
  // the normal next screen. Used to send the user back to the final review
  // after they edit a single answer from it.
  const [pendingReviewReturnKey, setPendingReviewReturnKey] = useState<
    string | null
  >(null);
  // Which review entries are expanded to show their full field list. Collapsed
  // by default so the review stays scannable with many entries.
  const [reviewExpanded, setReviewExpanded] = useState<Record<string, boolean>>(
    {}
  );
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const [uploadingCountByKey, setUploadingCountByKey] = useState<
    Record<string, number>
  >({});
  const [pendingUploadsByKey, setPendingUploadsByKey] = useState<
    Record<string, { id: string; filename: string }[]>
  >({});
  const [uploadedDocumentSort, setUploadedDocumentSort] =
    useState<UploadedDocumentSortSelection>({
      sortBy: "created_at",
      sortDir: "desc"
    });
  const [previewFile, setPreviewFile] = useState<OutgoingIntakeFile | null>(
    null
  );
  const baselineAnswerByKeyRef = useRef<Map<string, OutgoingIntakeAnswer>>(
    new Map()
  );
  const [isQuickPreviewOverlayVisible, setIsQuickPreviewOverlayVisible] =
    useState(false);
  const [quickPreviewTransformOriginY, setQuickPreviewTransformOriginY] =
    useState(260);
  const uploadedDocumentCardRefs = useRef<
    Record<string, HTMLButtonElement | null>
  >({});
  const contentScrollRef = useRef<HTMLDivElement | null>(null);
  const quickPreviewCloseTimeoutRef = useRef<number | null>(null);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [previewLoadingIds, setPreviewLoadingIds] = useState<
    Record<string, boolean>
  >({});
  const [previewErrors, setPreviewErrors] = useState<Record<string, string>>(
    {}
  );
  const previewUrlsRef = useRef<Record<string, string>>({});
  const [duplicatePreviewUrls, setDuplicatePreviewUrls] = useState<
    Record<string, string>
  >({});
  const [duplicatePreviewLoadingIds, setDuplicatePreviewLoadingIds] = useState<
    Record<string, boolean>
  >({});
  const [duplicatePreviewErrors, setDuplicatePreviewErrors] = useState<
    Record<string, string>
  >({});
  const duplicatePreviewUrlsRef = useRef<Record<string, string>>({});
  const [deletingIntakeFileIds, setDeletingIntakeFileIds] = useState<
    Set<string>
  >(() => new Set());
  const deletingIntakeFileIdsRef = useRef<Set<string>>(new Set());
  const [recentlyInsertedIntakeFileIds, setRecentlyInsertedIntakeFileIds] =
    useState<Set<string>>(() => new Set());
  const [intakeFileInsertedAtById, setIntakeFileInsertedAtById] = useState<
    Record<string, number>
  >({});
  const insertedIntakeFileTimeoutRefs = useRef<Record<string, number>>({});
  const [suppressedDuplicateMatchIds, setSuppressedDuplicateMatchIds] =
    useState<Set<string>>(() => new Set());
  const deletedIntakeFileTimeoutRefs = useRef<Record<string, number>>({});
  const [intakeFilePendingDelete, setIntakeFilePendingDelete] =
    useState<PendingIntakeFileDelete | null>(null);
  const [isDeletingIntakeFile, setIsDeletingIntakeFile] = useState(false);

  const updateQuickPreviewTransformOrigin = useCallback((fileId: string) => {
    const root = contentScrollRef.current;
    const card = uploadedDocumentCardRefs.current[fileId];
    if (!root || !card) return;

    const rootRect = root.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const panelTop = rootRect.top + 32;
    const cardCenter = cardRect.top + cardRect.height / 2;
    setQuickPreviewTransformOriginY(Math.max(24, cardCenter - panelTop));
  }, []);

  const closeQuickPreviewOverlay = useCallback(() => {
    if (quickPreviewCloseTimeoutRef.current) {
      window.clearTimeout(quickPreviewCloseTimeoutRef.current);
      quickPreviewCloseTimeoutRef.current = null;
    }
    setIsQuickPreviewOverlayVisible(false);
    quickPreviewCloseTimeoutRef.current = window.setTimeout(() => {
      setPreviewFile(null);
      quickPreviewCloseTimeoutRef.current = null;
    }, 260);
  }, []);

  const openQuickPreviewOverlay = useCallback(
    (file: OutgoingIntakeFile) => {
      if (
        previewFile?.file_id === file.file_id &&
        isQuickPreviewOverlayVisible
      ) {
        closeQuickPreviewOverlay();
        return;
      }

      if (quickPreviewCloseTimeoutRef.current) {
        window.clearTimeout(quickPreviewCloseTimeoutRef.current);
        quickPreviewCloseTimeoutRef.current = null;
      }

      updateQuickPreviewTransformOrigin(file.file_id);
      setPreviewFile(file);
      setIsQuickPreviewOverlayVisible(false);
      const frameId = window.requestAnimationFrame(() => {
        updateQuickPreviewTransformOrigin(file.file_id);
        setIsQuickPreviewOverlayVisible(true);
      });
      window.setTimeout(() => window.cancelAnimationFrame(frameId), 0);
    },
    [
      closeQuickPreviewOverlay,
      isQuickPreviewOverlayVisible,
      previewFile?.file_id,
      updateQuickPreviewTransformOrigin
    ]
  );

  useEffect(
    () => () => {
      if (quickPreviewCloseTimeoutRef.current) {
        window.clearTimeout(quickPreviewCloseTimeoutRef.current);
      }
      Object.values(deletedIntakeFileTimeoutRefs.current).forEach((timeoutId) =>
        window.clearTimeout(timeoutId)
      );
      Object.values(insertedIntakeFileTimeoutRefs.current).forEach(
        (timeoutId) => window.clearTimeout(timeoutId)
      );
    },
    []
  );

  useEffect(() => {
    deletingIntakeFileIdsRef.current = deletingIntakeFileIds;
  }, [deletingIntakeFileIds]);

  useEffect(() => {
    if (!previewFile?.file_id || !safeToken) return;
    if (
      previewUrls[previewFile.file_id] ||
      previewLoadingIds[previewFile.file_id]
    ) {
      return;
    }

    setPreviewLoadingIds((currentIds) => ({
      ...currentIds,
      [previewFile.file_id]: true
    }));
    setPreviewErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors[previewFile.file_id];
      return nextErrors;
    });

    PublicOutgoingIntakesAPI.getFilePreview(safeToken, previewFile.file_id)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        setPreviewUrls((currentUrls) => {
          if (currentUrls[previewFile.file_id]) {
            URL.revokeObjectURL(url);
            return currentUrls;
          }
          return {
            ...currentUrls,
            [previewFile.file_id]: url
          };
        });
      })
      .catch((error) => {
        setPreviewErrors((currentErrors) => ({
          ...currentErrors,
          [previewFile.file_id]:
            error instanceof Error
              ? error.message
              : "Failed to load document preview."
        }));
      })
      .finally(() => {
        setPreviewLoadingIds((currentIds) => ({
          ...currentIds,
          [previewFile.file_id]: false
        }));
      });
  }, [previewFile?.file_id, previewLoadingIds, previewUrls, safeToken]);

  useEffect(() => {
    previewUrlsRef.current = previewUrls;
  }, [previewUrls]);

  useEffect(() => {
    duplicatePreviewUrlsRef.current = duplicatePreviewUrls;
  }, [duplicatePreviewUrls]);

  const quickPreviewBlobUrl = previewFile
    ? previewUrls[previewFile.file_id] ?? null
    : null;
  const isQuickPreviewLoading = previewFile
    ? Boolean(previewLoadingIds[previewFile.file_id])
    : false;
  const quickPreviewError = previewFile
    ? previewErrors[previewFile.file_id] ?? null
    : null;

  useEffect(
    () => () => {
      Object.values(previewUrlsRef.current).forEach((url) =>
        URL.revokeObjectURL(url)
      );
      Object.values(duplicatePreviewUrlsRef.current).forEach((url) =>
        URL.revokeObjectURL(url)
      );
    },
    []
  );

  const previewDuplicateMatch = useMemo(
    () =>
      previewFile
        ? getPrimaryDuplicateMatch(previewFile, suppressedDuplicateMatchIds)
        : null,
    [previewFile, suppressedDuplicateMatchIds]
  );
  const previewDuplicateKey = previewDuplicateMatch
    ? getDuplicatePreviewKey(previewDuplicateMatch)
    : null;

  useEffect(() => {
    if (!previewFile?.file_id || !safeToken || !previewDuplicateMatch) return;
    const duplicateKey = getDuplicatePreviewKey(previewDuplicateMatch);
    if (
      duplicatePreviewUrls[duplicateKey] ||
      duplicatePreviewLoadingIds[duplicateKey] ||
      duplicatePreviewErrors[duplicateKey]
    ) {
      return;
    }

    setDuplicatePreviewLoadingIds((currentIds) => ({
      ...currentIds,
      [duplicateKey]: true
    }));
    setDuplicatePreviewErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors[duplicateKey];
      return nextErrors;
    });

    PublicOutgoingIntakesAPI.getDuplicateFilePreview(
      safeToken,
      previewFile.file_id,
      previewDuplicateMatch
    )
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        setDuplicatePreviewUrls((currentUrls) => {
          if (currentUrls[duplicateKey]) {
            URL.revokeObjectURL(url);
            return currentUrls;
          }
          return {
            ...currentUrls,
            [duplicateKey]: url
          };
        });
      })
      .catch((error) => {
        setDuplicatePreviewErrors((currentErrors) => ({
          ...currentErrors,
          [duplicateKey]:
            error instanceof Error
              ? error.message
              : "Unable to load duplicate preview."
        }));
      })
      .finally(() => {
        setDuplicatePreviewLoadingIds((currentIds) => ({
          ...currentIds,
          [duplicateKey]: false
        }));
      });
  }, [
    duplicatePreviewErrors,
    duplicatePreviewLoadingIds,
    duplicatePreviewUrls,
    previewDuplicateMatch,
    previewFile?.file_id,
    safeToken
  ]);

  const mergeRefreshedIntakeFile = useCallback((file: OutgoingIntakeFile) => {
    const fileId = String(file.file_id ?? "");
    if (!fileId) return;

    const replaceFiles = (files?: OutgoingIntakeFile[]) => {
      let changed = false;
      const nextFiles = (files ?? []).map((existing) => {
        if (existing.file_id !== fileId) return existing;
        changed = true;
        return { ...existing, ...file };
      });
      return { changed, nextFiles };
    };

    setAnswersById((prev) => {
      let changed = false;
      const next: Record<string, OutgoingIntakeAnswer> = {};
      Object.entries(prev).forEach(([questionId, answer]) => {
        const result = replaceFiles(answer.files);
        next[questionId] = result.changed
          ? { ...answer, files: result.nextFiles }
          : answer;
        changed = changed || result.changed;
      });
      return changed ? next : prev;
    });

    setGroups((prev) => {
      let changed = false;
      const next: Record<string, GroupInstance[]> = {};
      Object.entries(prev).forEach(([groupId, instances]) => {
        next[groupId] = instances.map((instance) => {
          let instanceChanged = false;
          const nextAnswers: Record<string, OutgoingIntakeAnswer> = {};
          Object.entries(instance.answers).forEach(([questionId, answer]) => {
            const result = replaceFiles(answer.files);
            nextAnswers[questionId] = result.changed
              ? { ...answer, files: result.nextFiles }
              : answer;
            instanceChanged = instanceChanged || result.changed;
          });
          changed = changed || instanceChanged;
          return instanceChanged
            ? { ...instance, answers: nextAnswers }
            : instance;
        });
      });
      return changed ? next : prev;
    });

    setPreviewFile((current) =>
      current?.file_id === fileId ? { ...current, ...file } : current
    );
  }, []);

  const removeIntakeFileFromAnswers = useCallback((fileId: string) => {
    setAnswersById((prev) => {
      let changed = false;
      const next: Record<string, OutgoingIntakeAnswer> = {};
      Object.entries(prev).forEach(([questionId, answer]) => {
        const files = answer.files ?? [];
        const nextFiles = files.filter((file) => file.file_id !== fileId);
        changed = changed || nextFiles.length !== files.length;
        next[questionId] =
          nextFiles.length !== files.length
            ? { ...answer, files: nextFiles }
            : answer;
      });
      return changed ? next : prev;
    });

    setGroups((prev) => {
      let changed = false;
      const next: Record<string, GroupInstance[]> = {};
      Object.entries(prev).forEach(([groupId, instances]) => {
        next[groupId] = instances.map((instance) => {
          let instanceChanged = false;
          const nextAnswers: Record<string, OutgoingIntakeAnswer> = {};
          Object.entries(instance.answers).forEach(([questionId, answer]) => {
            const files = answer.files ?? [];
            const nextFiles = files.filter((file) => file.file_id !== fileId);
            instanceChanged =
              instanceChanged || nextFiles.length !== files.length;
            nextAnswers[questionId] =
              nextFiles.length !== files.length
                ? { ...answer, files: nextFiles }
                : answer;
          });
          changed = changed || instanceChanged;
          return instanceChanged
            ? { ...instance, answers: nextAnswers }
            : instance;
        });
      });
      return changed ? next : prev;
    });
  }, []);

  const clearIntakeFilePreviewState = useCallback((fileId: string) => {
    setPreviewUrls((currentUrls) => {
      const url = currentUrls[fileId];
      if (url) URL.revokeObjectURL(url);
      const nextUrls = { ...currentUrls };
      delete nextUrls[fileId];
      return nextUrls;
    });
    setPreviewErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors[fileId];
      return nextErrors;
    });
    setPreviewLoadingIds((currentIds) => {
      const nextIds = { ...currentIds };
      delete nextIds[fileId];
      return nextIds;
    });
    setDuplicatePreviewUrls((currentUrls) => {
      let changed = false;
      const nextUrls = { ...currentUrls };
      Object.entries(nextUrls).forEach(([key, url]) => {
        if (key.endsWith(`:${fileId}`)) {
          URL.revokeObjectURL(url);
          delete nextUrls[key];
          changed = true;
        }
      });
      return changed ? nextUrls : currentUrls;
    });
    setDuplicatePreviewErrors((currentErrors) => {
      let changed = false;
      const nextErrors = { ...currentErrors };
      Object.keys(nextErrors).forEach((key) => {
        if (key.endsWith(`:${fileId}`)) {
          delete nextErrors[key];
          changed = true;
        }
      });
      return changed ? nextErrors : currentErrors;
    });
    setDuplicatePreviewLoadingIds((currentIds) => {
      let changed = false;
      const nextIds = { ...currentIds };
      Object.keys(nextIds).forEach((key) => {
        if (key.endsWith(`:${fileId}`)) {
          delete nextIds[key];
          changed = true;
        }
      });
      return changed ? nextIds : currentIds;
    });
  }, []);

  const pendingReviewFileIds = useMemo(() => {
    const ids = new Set<string>();
    const collect = (answer?: OutgoingIntakeAnswer) => {
      for (const file of answer?.files ?? []) {
        if (
          isIntakeFilePreviewGenerating(file) ||
          file.reviewStatus === "checking"
        ) {
          ids.add(file.file_id);
        }
      }
    };
    Object.values(answersById).forEach(collect);
    Object.values(groups).forEach((instances) => {
      instances.forEach((instance) =>
        Object.values(instance.answers).forEach(collect)
      );
    });
    return Array.from(ids);
  }, [answersById, groups]);

  useEffect(() => {
    if (!safeToken || pendingReviewFileIds.length === 0) return undefined;

    let isCancelled = false;
    const refresh = () => {
      pendingReviewFileIds.forEach((fileId) => {
        PublicOutgoingIntakesAPI.getFileStatus(safeToken, fileId)
          .then((file) => {
            if (!isCancelled) mergeRefreshedIntakeFile(file);
          })
          .catch(() => undefined);
      });
    };

    refresh();
    const intervalId = window.setInterval(refresh, 3000);
    return () => {
      isCancelled = true;
      window.clearInterval(intervalId);
    };
  }, [mergeRefreshedIntakeFile, pendingReviewFileIds, safeToken]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSavedAtIso, setLastSavedAtIso] = useState<string | null>(null);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  // Mirrors activeStepIndex so effects can compare against the current step
  // without depending on it, which would let them fight caret navigation.
  const activeStepIndexRef = useRef(activeStepIndex);
  useEffect(() => {
    activeStepIndexRef.current = activeStepIndex;
  }, [activeStepIndex]);
  const [resumeChoiceActive, setResumeChoiceActive] = useState(false);
  // Client-facing choice between the default screen-by-screen "Typeform" flow
  // and a traditional single-page form. Persisted per token so it survives
  // reloads/resume. Preview never reads/writes localStorage (always defaults).
  const layoutModeStorageKey = safeToken
    ? `public_outgoing_intake_layout:${safeToken}`
    : "";
  const [layoutMode, setLayoutMode] = useState<"typeform" | "single_page">(
    () => {
      if (forceSinglePage) return "single_page";
      if (mode === "preview" || !layoutModeStorageKey) return "typeform";
      try {
        return window.localStorage.getItem(layoutModeStorageKey) ===
          "single_page"
          ? "single_page"
          : "typeform";
      } catch {
        return "typeform";
      }
    }
  );
  useEffect(() => {
    // A caller-pinned layout is not the reader's choice, so don't let it
    // overwrite the preference a real client set on this intake.
    if (mode === "preview" || forceSinglePage || !layoutModeStorageKey) return;
    try {
      window.localStorage.setItem(layoutModeStorageKey, layoutMode);
    } catch {
      // Ignore storage failures (private mode, quota, etc.).
    }
  }, [layoutMode, layoutModeStorageKey, mode]);
  const [stepTransitionDirection, setStepTransitionDirection] = useState<
    "forward" | "backward"
  >("forward");
  // At lg+ the review file preview shows as a right-side panel; below that it
  // renders inline under the clicked tile. Selecting one layout avoids mounting
  // two PdfPreview instances.
  const isReviewPreviewDesktop = useMediaQuery("(min-width: 62em)");
  const isMdUp = useMediaQuery("(min-width: 48em)");
  const [showLoadingFadeOverlay, setShowLoadingFadeOverlay] = useState(true);

  useEffect(() => {
    if (isLoading) {
      setShowLoadingFadeOverlay(true);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setShowLoadingFadeOverlay(false);
    }, 900);
    return () => window.clearTimeout(timeoutId);
  }, [isLoading]);

  const lastSavedLabel = useMemo(() => {
    if (!lastSavedAtIso) return null;
    try {
      const d = new Date(lastSavedAtIso);
      if (Number.isNaN(d.getTime())) return null;
      const now = new Date();
      const timeZoneName =
        new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
          .formatToParts(d)
          .find((part) => part.type === "timeZoneName")?.value ?? "";
      const isSameDay =
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate();
      const locale = intakeLanguage.language;
      const time = isSameDay
        ? d.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" })
        : d.toLocaleString(locale, {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit"
          });
      return tr("savedAt", {
        time: `${time}${timeZoneName ? ` ${timeZoneName}` : ""}`
      });
    } catch {
      return null;
    }
  }, [lastSavedAtIso, intakeLanguage.language]);

  const writeDraftLocal = useCallback(
    (opts?: { silent?: boolean }) => {
      if (isPersistenceDisabled) return false;
      if (!safeToken) return false;
      try {
        const key = `public_outgoing_intake_draft:${safeToken}`;
        const payload = {
          saved_at: new Date().toISOString(),
          answersById,
          groups
        };
        window.localStorage.setItem(key, JSON.stringify(payload));
        setLastSavedAtIso(payload.saved_at);
        if (!opts?.silent) {
          showToast({
            variant: "success",
            icon: "FeatherSave",
            title: tr("saved"),
            description: tr("savedBody"),
            durationMs: 2500
          });
        }
        return true;
      } catch (e) {
        if (!opts?.silent) {
          showToast({
            variant: "error",
            title: tr("couldntSave"),
            description: e instanceof Error ? e.message : tr("pleaseTryAgain")
          });
        }
        return false;
      }
    },
    [answersById, groups, isPersistenceDisabled, safeToken, showToast]
  );

  const collectMeaningfulAnswers = useCallback((): OutgoingIntakeAnswer[] => {
    const answers: OutgoingIntakeAnswer[] = [];
    for (const a of Object.values(answersById)) {
      if (a) {
        const hasFiles = (a.files ?? []).length > 0;
        const hasValue = hasMeaningfulValue(a.value);
        const hasComment = Boolean(normalizeText(a.comment));
        if (hasFiles || hasValue || hasComment) answers.push(a);
      }
    }
    for (const instances of Object.values(groups)) {
      for (const inst of instances) {
        for (const a of Object.values(inst.answers)) {
          const hasFiles = (a.files ?? []).length > 0;
          const hasValue = hasMeaningfulValue(a.value);
          const hasComment = Boolean(normalizeText(a.comment));
          if (hasFiles || hasValue || hasComment) answers.push(a);
        }
      }
    }
    return answers;
  }, [answersById, groups]);

  /**
   * Writes an answer Casey suggested and the client accepted.
   *
   * Stamped `source: "assistant"` with the citation attached, so the reviewing
   * attorney can tell a value the client typed from one a model read off a
   * document, and can check it without opening the file.
   */
  const applyAssistantProposal = useCallback(
    (proposal: CaseyProposal) => {
      const {
        question_id: questionId,
        repeatable_group_id: groupId,
        repeatable_group_instance_id: instanceId
      } = proposal;
      if (!questionId) return;

      const withProposal = (previous: OutgoingIntakeAnswer | undefined) =>
        answerWithProposal(previous, proposal);

      if (groupId && instanceId) {
        setGroups((prev) => groupsWithProposal(prev, proposal));
        // A group the client had declined, or not reached, stays collapsed
        // until its lead-in says yes -- which would hide what they just accepted.
        setGroupDecisions((prev) =>
          prev[groupId] === "yes" ? prev : { ...prev, [groupId]: "yes" }
        );
      } else {
        setAnswersById((prev) => ({
          ...prev,
          [questionId]: withProposal(prev[questionId])
        }));
      }

      showToast({
        variant: "success",
        icon: "FeatherCheck",
        title: "Added to your form",
        description: normalizeText(proposal.prompt) || undefined
      });
    },
    [showToast]
  );

  const saveProgressRemote = useCallback(
    async (opts?: { silent?: boolean; skipIfUploading?: boolean }) => {
      if (isPersistenceDisabled) {
        if (!opts?.silent) {
          showToast({
            variant: "neutral",
            icon: "FeatherInfo",
            title: "Read-only",
            description: "Saving is disabled in read-only mode."
          });
        }
        return;
      }
      if (!safeToken || !intake) return;
      // If uploads are still in-flight, the latest files may not have been added to state yet.
      // Ask the user to wait so we don't persist an incomplete snapshot (e.g. N-1 files).
      if (Object.values(uploadingCountByKey ?? {}).some((n) => (n ?? 0) > 0)) {
        if (opts?.skipIfUploading) return;
        if (!opts?.silent) {
          showToast({
            variant: "neutral",
            icon: "FeatherUpload",
            title: "Uploads in progress",
            description: "Please wait for uploads to finish before saving."
          });
        }
        return;
      }
      // Give React a tick to flush any upload-completion state updates before collecting answers.
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, 0);
      });
      const answers = collectMeaningfulAnswers();
      await PublicOutgoingIntakesAPI.saveProgress(safeToken, { answers });
      // Keep local draft in sync so reopening the link doesn't restore stale local state.
      writeDraftLocal({ silent: true });
      setLastSavedAtIso(new Date().toISOString());
      if (!opts?.silent) {
        showToast({
          variant: "success",
          icon: "FeatherSave",
          title: tr("saved"),
          description: tr("savedBody"),
          durationMs: 2500
        });
      }
    },
    [
      collectMeaningfulAnswers,
      intake,
      isPersistenceDisabled,
      safeToken,
      showToast,
      uploadingCountByKey,
      writeDraftLocal
    ]
  );

  const autosaveDraftLocal = useCallback(() => {
    // Silent autosave used for upload completion events.
    writeDraftLocal({ silent: true });
  }, [writeDraftLocal]);

  const rejectedByKey = useMemo(() => {
    const map = new Map<string, OutgoingIntakeReviewDecision>();
    for (const d of latestSubmission?.decisions ?? []) {
      if (d.status === "rejected") map.set(decisionKey(d), d);
    }
    return map;
  }, [latestSubmission?.decisions]);

  const hasRejections = useMemo(() => {
    return (latestSubmission?.decisions ?? []).some(
      (d) => d.status === "rejected"
    );
  }, [latestSubmission?.decisions]);

  // Per-question cross-revision comment thread for the client, derived from the
  // trimmed revision history: their own answer comments plus the attorney's
  // requested-change notes, in chronological order (oldest first). Mirrors the
  // attorney-side derivation in features/requestsIde/editor/useSubmissionReview so both sides show
  // the same conversation.
  const clientThreadByKey = useMemo(() => {
    const out: Record<string, ReviewCommentThreadItem[]> = {};
    if (!revisionHistory.length) return out;
    const clientLabel =
      [
        normalizeText(intake?.foreign_national_first_name),
        normalizeText(intake?.foreign_national_last_name)
      ]
        .filter(Boolean)
        .join(" ") || tr("you");
    const clientAvatar = intake?.foreign_national_avatar_url ?? null;
    const attorneyLabel =
      normalizeText(intake?.attorney_name) || tr("yourAttorney");
    const attorneyAvatar = intake?.attorney_avatar_url ?? null;
    const timeLabelFor = (at: unknown): string | undefined => {
      if (!at) return undefined;
      const d = new Date(typeof at === "string" ? at : String(at ?? ""));
      if (Number.isNaN(d.getTime())) return undefined;
      return d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZoneName: "short"
      });
    };
    const ordered = [...revisionHistory].sort(
      (a, b) => (a.revision_number ?? 0) - (b.revision_number ?? 0)
    );
    const push = (key: string, item: ReviewCommentThreadItem) => {
      (out[key] = out[key] ?? []).push(item);
    };
    for (const rev of ordered) {
      const revisionNumber =
        typeof rev.revision_number === "number"
          ? rev.revision_number
          : undefined;
      for (const ans of rev.answers ?? []) {
        const comment = normalizeText(ans.comment);
        if (comment) {
          const key = decisionKey({
            question_id: ans.question_id,
            repeatable_group_id: ans.repeatable_group_id ?? null,
            repeatable_group_instance_id:
              ans.repeatable_group_instance_id ?? null
          });
          push(key, {
            author: "client",
            name: clientLabel,
            text: comment,
            at: rev.submitted_at,
            revisionNumber,
            avatarUrl: clientAvatar,
            timeLabel: timeLabelFor(rev.submitted_at)
          });
        }
      }
      for (const dec of rev.decisions ?? []) {
        const note = normalizeText(dec.note);
        if (dec.status === "rejected" && note) {
          const key = decisionKey({
            question_id: dec.question_id,
            repeatable_group_id: dec.repeatable_group_id ?? null,
            repeatable_group_instance_id:
              dec.repeatable_group_instance_id ?? null
          });
          push(key, {
            author: "attorney",
            name: attorneyLabel,
            text: note,
            at: rev.reviewed_at,
            revisionNumber,
            avatarUrl: attorneyAvatar,
            timeLabel: timeLabelFor(rev.reviewed_at)
          });
        }
      }
    }
    return out;
  }, [
    revisionHistory,
    intake?.foreign_national_first_name,
    intake?.foreign_national_last_name,
    intake?.foreign_national_avatar_url,
    intake?.attorney_name,
    intake?.attorney_avatar_url
  ]);

  const intakeStatus = String(intake?.status ?? "").trim();
  const isAccepted = intakeStatus === "accepted";
  const isRevisionRequested =
    intakeStatus === "revision_requested" ||
    latestSubmission?.review_status === "revision_requested";
  const isAwaitingReview =
    latestSubmission?.review_status === "awaiting_review" &&
    !hasRejections &&
    !isRevisionRequested;

  const autosaveProgress = useCallback(() => {
    if (mode === "preview" || isAccepted || isAwaitingReview) return;
    saveProgressRemote({ silent: true, skipIfUploading: true }).catch(() => {
      writeDraftLocal({ silent: true });
    });
  }, [isAccepted, isAwaitingReview, mode, saveProgressRemote, writeDraftLocal]);

  // Single-page mode has no per-step navigation to hook autosave onto, so
  // persist answers on a debounce whenever they change. Skips the run that
  // fires when the mode is first entered (no edits yet).
  const singlePageAutosaveReady = useRef(false);
  useEffect(() => {
    if (layoutMode !== "single_page" || mode === "preview") return undefined;
    if (!singlePageAutosaveReady.current) {
      singlePageAutosaveReady.current = true;
      return undefined;
    }
    const timeoutId = window.setTimeout(() => autosaveProgress(), 1500);
    return () => window.clearTimeout(timeoutId);
  }, [answersById, groups, layoutMode, mode, autosaveProgress]);

  // Flush a save when the tab is hidden or when leaving single-page mode.
  useEffect(() => {
    if (layoutMode !== "single_page" || mode === "preview") return undefined;
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") autosaveProgress();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      autosaveProgress();
    };
  }, [layoutMode, mode, autosaveProgress]);

  const handleRequestDeleteIntakeFile = useCallback(
    (file: OutgoingIntakeFile, event?: React.MouseEvent) => {
      event?.stopPropagation();
      setIntakeFilePendingDelete({ file });
    },
    []
  );

  const handleCloseDeleteIntakeFileDialog = useCallback(() => {
    if (isDeletingIntakeFile) return;
    setIntakeFilePendingDelete(null);
  }, [isDeletingIntakeFile]);

  const handleConfirmDeleteIntakeFile = useCallback(async () => {
    if (!safeToken || !intakeFilePendingDelete) return;
    const fileToDelete = intakeFilePendingDelete.file;
    const fileId = fileToDelete.file_id;
    setIsDeletingIntakeFile(true);
    try {
      await PublicOutgoingIntakesAPI.deleteFile(safeToken, fileId);
      setIntakeFilePendingDelete(null);
      setDeletingIntakeFileIds((currentIds) => {
        const nextIds = new Set(currentIds);
        nextIds.add(fileId);
        deletingIntakeFileIdsRef.current = nextIds;
        return nextIds;
      });
      setSuppressedDuplicateMatchIds((currentIds) => {
        const nextIds = new Set(currentIds);
        nextIds.add(fileId);
        return nextIds;
      });
      if (previewFile?.file_id === fileId) {
        closeQuickPreviewOverlay();
      }
      clearIntakeFilePreviewState(fileId);

      if (deletedIntakeFileTimeoutRefs.current[fileId]) {
        window.clearTimeout(deletedIntakeFileTimeoutRefs.current[fileId]);
      }
      deletedIntakeFileTimeoutRefs.current[fileId] = window.setTimeout(() => {
        removeIntakeFileFromAnswers(fileId);
        setDeletingIntakeFileIds((currentIds) => {
          const nextIds = new Set(currentIds);
          nextIds.delete(fileId);
          deletingIntakeFileIdsRef.current = nextIds;
          return nextIds;
        });
        delete deletedIntakeFileTimeoutRefs.current[fileId];
        window.setTimeout(() => autosaveProgress(), 0);
      }, INTAKE_FILE_DELETE_FADE_MS);
    } catch (error) {
      showToast({
        variant: "error",
        title: "Unable to delete file",
        description:
          error instanceof Error ? error.message : tr("pleaseTryAgain")
      });
    } finally {
      setIsDeletingIntakeFile(false);
    }
  }, [
    autosaveProgress,
    clearIntakeFilePreviewState,
    closeQuickPreviewOverlay,
    intakeFilePendingDelete,
    previewFile?.file_id,
    removeIntakeFileFromAnswers,
    safeToken,
    showToast
  ]);

  const load = useCallback(async () => {
    if (isPreview && !effectiveInitialData && isUrlPreview) {
      setIntake(null);
      setLatestSubmission(null);
      setErrorMessage(
        "This preview link has expired. Go back to the builder and click Preview again."
      );
      setIsLoading(false);
      return;
    }

    if (isPreview && effectiveInitialData) {
      const schema = asNodes(
        effectiveInitialData.outgoing_intake?.schema ?? []
      );
      const payload: IntakePayload = {
        outgoing_intake_id: String(
          effectiveInitialData.outgoing_intake?.outgoing_intake_id ?? "preview"
        ),
        title: String(effectiveInitialData.outgoing_intake?.title ?? "Intake"),
        status: String(effectiveInitialData.outgoing_intake?.status ?? "sent"),
        schema,
        expires_at: effectiveInitialData.outgoing_intake?.expires_at,
        latest_submission_id:
          effectiveInitialData.outgoing_intake?.latest_submission_id ?? null,
        law_firm_name:
          effectiveInitialData.outgoing_intake?.law_firm_name ?? null,
        law_firm_logo_url:
          effectiveInitialData.outgoing_intake?.law_firm_logo_url ?? null,
        enable_whitelabel_foreign_national_intake:
          effectiveInitialData.outgoing_intake
            ?.enable_whitelabel_foreign_national_intake ?? null,
        enable_firm_branding:
          effectiveInitialData.outgoing_intake?.enable_firm_branding ?? null,
        intake_background_color:
          effectiveInitialData.outgoing_intake?.intake_background_color ?? null,
        foreign_national_first_name:
          effectiveInitialData.outgoing_intake?.foreign_national_first_name ??
          null,
        foreign_national_last_name:
          effectiveInitialData.outgoing_intake?.foreign_national_last_name ??
          null,
        foreign_national_avatar_url:
          effectiveInitialData.outgoing_intake?.foreign_national_avatar_url ??
          null,
        attorney_name:
          effectiveInitialData.outgoing_intake?.attorney_name ?? null,
        attorney_avatar_url:
          effectiveInitialData.outgoing_intake?.attorney_avatar_url ?? null,
        attorney_note:
          effectiveInitialData.outgoing_intake?.attorney_note ?? null,
        welcome_video_url:
          effectiveInitialData.outgoing_intake?.welcome_video_url ?? null,
        welcome_video_trim:
          effectiveInitialData.outgoing_intake?.welcome_video_trim ?? null
      };
      setIntake(payload);
      setLatestSubmission(effectiveInitialData.latest_submission ?? null);
      setRevisionHistory(
        Array.isArray(effectiveInitialData.revision_history)
          ? effectiveInitialData.revision_history
          : []
      );
      // A preview payload carrying a submission is asking to render that
      // submission's answers (attorney review, point-in-time snapshots), so
      // hydrate them into answer state rather than leaving the form blank.
      const previewAnswers = effectiveInitialData.latest_submission?.answers;
      if (previewAnswers && previewAnswers.length > 0) {
        const hydrated = buildHydratedAnswerState(previewAnswers);
        setAnswersById(hydrated.answersById);
        setGroups(hydrated.groups);
      }
      setErrorMessage(null);
      setIsLoading(false);
      return;
    }

    if (!safeToken) return;
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const resp = await PublicOutgoingIntakesAPI.get(safeToken);
      const schema = asNodes(resp.outgoing_intake?.schema ?? []);
      const payload: IntakePayload = {
        outgoing_intake_id: String(
          resp.outgoing_intake?.outgoing_intake_id ?? ""
        ),
        title: String(
          resp.outgoing_intake?.client_title ||
            resp.outgoing_intake?.title ||
            "Intake"
        ),
        status: String(resp.outgoing_intake?.status ?? ""),
        schema,
        expires_at: resp.outgoing_intake?.expires_at,
        latest_submission_id:
          resp.outgoing_intake?.latest_submission_id ?? null,
        law_firm_name: resp.outgoing_intake?.law_firm_name ?? null,
        law_firm_logo_url: resp.outgoing_intake?.law_firm_logo_url ?? null,
        enable_whitelabel_foreign_national_intake:
          resp.outgoing_intake?.enable_whitelabel_foreign_national_intake ??
          null,
        enable_firm_branding:
          resp.outgoing_intake?.enable_firm_branding ?? null,
        intake_background_color:
          resp.outgoing_intake?.intake_background_color ?? null,
        foreign_national_first_name:
          resp.outgoing_intake?.foreign_national_first_name ?? null,
        foreign_national_last_name:
          resp.outgoing_intake?.foreign_national_last_name ?? null,
        foreign_national_avatar_url:
          resp.outgoing_intake?.foreign_national_avatar_url ?? null,
        attorney_name: resp.outgoing_intake?.attorney_name ?? null,
        attorney_avatar_url: resp.outgoing_intake?.attorney_avatar_url ?? null,
        attorney_note: resp.outgoing_intake?.attorney_note ?? null,
        welcome_video_url: resp.outgoing_intake?.welcome_video_url ?? null,
        welcome_video_trim: resp.outgoing_intake?.welcome_video_trim ?? null,
        client_language: resp.outgoing_intake?.client_language ?? null,
        client_title: resp.outgoing_intake?.client_title ?? null,
        prefill: resp.outgoing_intake?.prefill ?? null,
        viewer: resp.outgoing_intake?.viewer ?? null
      };
      setIntake(payload);
      setLatestSubmission(resp.latest_submission ?? null);
      setRevisionHistory(
        Array.isArray(resp.revision_history) ? resp.revision_history : []
      );
      setLastSavedAtIso(
        typeof resp.outgoing_intake?.progress_saved_at === "string"
          ? resp.outgoing_intake.progress_saved_at
          : null
      );

      const submissionState = buildHydratedAnswerState(
        resp.latest_submission?.answers ?? []
      );
      const serverDraft = createServerDraftSnapshot(
        resp.outgoing_intake?.progress_answers,
        resp.outgoing_intake?.progress_saved_at
      );

      // Snapshot the server-provided answers so revision-requested UI can show
      // "You submitted:" even after the user edits their response.
      try {
        const latestReviewStatus = String(
          resp.latest_submission?.review_status ?? ""
        ).trim();
        const intakeStatus = String(resp.outgoing_intake?.status ?? "").trim();
        const hasRejectedDecisions = (
          resp.latest_submission?.decisions ?? []
        ).some((d) => d.status === "rejected");
        const isRevision =
          intakeStatus === "revision_requested" ||
          latestReviewStatus === "revision_requested";
        if (isRevision) {
          const rejectedKeys = new Set<string>();
          for (const d of resp.latest_submission?.decisions ?? []) {
            if (d.status === "rejected") {
              rejectedKeys.add(
                decisionKey({
                  question_id: d.question_id,
                  repeatable_group_id: d.repeatable_group_id ?? null,
                  repeatable_group_instance_id:
                    d.repeatable_group_instance_id ?? null
                })
              );
            }
          }

          // Keep prior non-rejected answers so resubmissions can be full snapshots,
          // but blank rejected answers so the user must explicitly re-enter them.
          const nextAnswersById: Record<string, OutgoingIntakeAnswer> = {};
          for (const [questionId, ans] of Object.entries(
            submissionState.answersById
          )) {
            const k = decisionKey({
              question_id: ans.question_id,
              repeatable_group_id: null,
              repeatable_group_instance_id: null
            });
            if (!rejectedKeys.has(k)) nextAnswersById[questionId] = ans;
          }

          const nextRevisionGroups: Record<string, GroupInstance[]> = {};
          for (const [groupId, items] of Object.entries(
            submissionState.groups
          )) {
            nextRevisionGroups[groupId] = (items ?? []).map((it) => {
              const filteredAnswers: Record<string, OutgoingIntakeAnswer> = {};
              for (const [qid, ans] of Object.entries(it.answers ?? {})) {
                const k = decisionKey({
                  question_id: ans.question_id,
                  repeatable_group_id: groupId,
                  repeatable_group_instance_id: it.instanceId
                });
                if (!rejectedKeys.has(k)) filteredAnswers[qid] = ans;
              }
              return { instanceId: it.instanceId, answers: filteredAnswers };
            });
          }
          // Ensure any rejected repeatable-group instances exist so the client can revise them.
          for (const d of resp.latest_submission?.decisions ?? []) {
            if (d.status === "rejected") {
              const groupId = String(d.repeatable_group_id ?? "").trim();
              const instanceId = String(
                d.repeatable_group_instance_id ?? ""
              ).trim();
              if (groupId && instanceId) {
                nextRevisionGroups[groupId] = nextRevisionGroups[groupId] ?? [];
                if (
                  !nextRevisionGroups[groupId].some(
                    (x) => x.instanceId === instanceId
                  )
                ) {
                  nextRevisionGroups[groupId].push({ instanceId, answers: {} });
                }
              }
            }
          }

          // Layer autosaved edits from *this* revision cycle back on top of the
          // blanked baseline so reloading mid-revision doesn't discard them.
          // Drafts saved before the attorney finished reviewing belong to the
          // previous cycle: restoring those would re-fill the very answers the
          // client was asked to redo, so they are ignored.
          const cycleStartMs =
            toMillis(resp.latest_submission?.reviewed_at) ??
            toMillis(resp.latest_submission?.submitted_at);
          const revisionLocalDraft = isPersistenceDisabled
            ? null
            : readLocalDraftSnapshot(
                window.localStorage.getItem(
                  `public_outgoing_intake_draft:${safeToken}`
                )
              );
          const revisionDraft = (() => {
            const preferred = pickPreferredDraft(
              serverDraft,
              revisionLocalDraft
            );
            if (!preferred) return null;
            if (cycleStartMs === null) return preferred;
            if (preferred.savedAtMs === null) return null;
            return preferred.savedAtMs > cycleStartMs ? preferred : null;
          })();

          if (revisionDraft) {
            setAnswersById({
              ...nextAnswersById,
              ...revisionDraft.answersById
            });
            setGroups(
              mergeGroupInstances(nextRevisionGroups, revisionDraft.groups)
            );
            if (revisionDraft.savedAtIso) {
              setLastSavedAtIso(revisionDraft.savedAtIso);
            }
          } else {
            setAnswersById(nextAnswersById);
            setGroups(nextRevisionGroups);
          }

          const m = new Map<string, OutgoingIntakeAnswer>();
          for (const a of resp.latest_submission?.answers ?? []) {
            const k = decisionKey({
              question_id: a.question_id,
              repeatable_group_id: a.repeatable_group_id ?? null,
              repeatable_group_instance_id:
                a.repeatable_group_instance_id ?? null
            });
            m.set(k, a);
          }
          baselineAnswerByKeyRef.current = m;
        } else {
          const shouldClearLocalDraft =
            latestReviewStatus === "awaiting_review" &&
            !hasRejectedDecisions &&
            intakeStatus !== "revision_requested";
          const draftKey = `public_outgoing_intake_draft:${safeToken}`;
          const localDraft =
            isPersistenceDisabled || shouldClearLocalDraft
              ? null
              : readLocalDraftSnapshot(window.localStorage.getItem(draftKey));
          const preferredDraft = shouldClearLocalDraft
            ? null
            : pickPreferredDraft(serverDraft, localDraft);
          const resolvedState = preferredDraft ?? submissionState;

          setAnswersById(resolvedState.answersById);
          setGroups(resolvedState.groups);
          baselineAnswerByKeyRef.current = new Map();
          if (preferredDraft?.savedAtIso) {
            setLastSavedAtIso(preferredDraft.savedAtIso);
          }

          // Once submitted, clear any stale local draft instead of letting it
          // overwrite the shared server-side progress on the next visit.
          if (!isPersistenceDisabled && shouldClearLocalDraft) {
            window.localStorage.removeItem(draftKey);
          }
        }
      } catch {
        setAnswersById(submissionState.answersById);
        setGroups(submissionState.groups);
        baselineAnswerByKeyRef.current = new Map();
      }
    } catch (err) {
      const gone = linkGoneFrom(err);
      if (gone) {
        setLinkGone(gone);
        return;
      }
      const msg = err instanceof Error ? err.message : tr("loadFailed");
      setErrorMessage(msg);
    } finally {
      setIsLoading(false);
    }
  }, [effectiveInitialData, isPreview, isUrlPreview, reviewMode, safeToken]);

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  useEffect(() => {
    const lawFirmLogoUrl = normalizeText(intake?.law_firm_logo_url);
    if (!intake || !lawFirmLogoUrl) {
      setIsBrandBackgroundResolving(false);
      return undefined;
    }

    const brandingEnabled =
      intake.enable_firm_branding === true ||
      (intake.enable_firm_branding == null && lawFirmLogoUrl.length > 0);
    if (!brandingEnabled) {
      setIsBrandBackgroundResolving(false);
      return undefined;
    }

    const currentBackground = normalizeText(intake.intake_background_color);
    if (
      currentBackground &&
      currentBackground.toLowerCase() !== BRAND_BLUE.toLowerCase()
    ) {
      setIsBrandBackgroundResolving(false);
      return undefined;
    }

    let cancelled = false;
    setIsBrandBackgroundResolving(true);
    sampleImageBackgroundColor(lawFirmLogoUrl).then((sampledColor) => {
      if (cancelled) return;
      if (!sampledColor) {
        setIsBrandBackgroundResolving(false);
        return;
      }
      setIntake((current) => {
        if (!current) return current;
        const currentLogoUrl = normalizeText(current.law_firm_logo_url);
        const currentBrandingEnabled =
          current.enable_firm_branding === true ||
          (current.enable_firm_branding == null && currentLogoUrl.length > 0);
        const currentBg = normalizeText(current.intake_background_color);
        if (
          currentLogoUrl !== lawFirmLogoUrl ||
          !currentBrandingEnabled ||
          (currentBg && currentBg.toLowerCase() !== BRAND_BLUE.toLowerCase())
        ) {
          return current;
        }
        return {
          ...current,
          intake_background_color: sampledColor
        };
      });
      setIsBrandBackgroundResolving(false);
    });

    return () => {
      cancelled = true;
    };
  }, [
    intake,
    intake?.enable_firm_branding,
    intake?.intake_background_color,
    intake?.law_firm_logo_url
  ]);

  const markIntakeFileInserted = useCallback((fileId: string) => {
    if (!fileId) return;
    setIntakeFileInsertedAtById((currentTimestamps) => ({
      ...currentTimestamps,
      [fileId]: Date.now()
    }));

    setRecentlyInsertedIntakeFileIds((currentIds) => {
      const nextIds = new Set(currentIds);
      nextIds.add(fileId);
      return nextIds;
    });

    if (insertedIntakeFileTimeoutRefs.current[fileId]) {
      window.clearTimeout(insertedIntakeFileTimeoutRefs.current[fileId]);
    }

    insertedIntakeFileTimeoutRefs.current[fileId] = window.setTimeout(() => {
      setRecentlyInsertedIntakeFileIds((currentIds) => {
        const nextIds = new Set(currentIds);
        nextIds.delete(fileId);
        return nextIds;
      });
      delete insertedIntakeFileTimeoutRefs.current[fileId];
    }, 900);
  }, []);

  const uploadFile = useCallback(
    async (
      questionId: string,
      file: File,
      ctx?: { groupId?: string; instanceId?: string }
    ) => {
      if (isPersistenceDisabled) return;
      if (!safeToken) return;
      const uploaded = await PublicOutgoingIntakesAPI.upload(safeToken, file);
      const groupId = ctx?.groupId ?? null;
      const instanceId = ctx?.instanceId ?? null;

      if (groupId && instanceId) {
        setGroups((prev) => {
          const next = { ...prev };
          const items = [...(next[groupId] ?? [])];
          const idx = items.findIndex((x) => x.instanceId === instanceId);
          if (idx < 0) return prev;
          const inst = items[idx];
          const existing = inst.answers[questionId] ?? {
            question_id: questionId,
            repeatable_group_id: groupId,
            repeatable_group_instance_id: instanceId,
            value: null,
            files: []
          };
          const files = [...(existing.files ?? []), uploaded];
          items[idx] = {
            ...inst,
            answers: { ...inst.answers, [questionId]: { ...existing, files } }
          };
          next[groupId] = items;
          return next;
        });
        return;
      }

      setAnswersById((prev) => {
        const existing = prev[questionId] ?? {
          question_id: questionId,
          value: null,
          files: []
        };
        const files = [...(existing.files ?? []), uploaded];
        return { ...prev, [questionId]: { ...existing, files } };
      });
    },
    [isPersistenceDisabled, safeToken]
  );

  const startUploads = useCallback((key: string, count: number) => {
    const n = Math.max(0, Number(count) || 0);
    if (!key || n <= 0) return;
    setUploadingCountByKey((prev) => ({
      ...prev,
      [key]: (prev[key] ?? 0) + n
    }));
  }, []);

  const finishUploads = useCallback((key: string, count: number) => {
    const n = Math.max(0, Number(count) || 0);
    if (!key || n <= 0) return;
    setUploadingCountByKey((prev) => {
      const current = prev[key] ?? 0;
      const updated = Math.max(0, current - n);
      const next = { ...prev };
      if (updated <= 0) {
        delete next[key];
      } else {
        next[key] = updated;
      }
      return next;
    });
  }, []);

  const addPendingUploads = useCallback(
    (key: string, items: { id: string; filename: string }[]) => {
      if (!key || items.length === 0) return;
      setPendingUploadsByKey((prev) => ({
        ...prev,
        [key]: [...(prev[key] ?? []), ...items]
      }));
      items.forEach((item) => markIntakeFileInserted(item.id));
    },
    [markIntakeFileInserted]
  );

  const resolvePendingUpload = useCallback((key: string, id: string) => {
    if (!key || !id) return;
    setPendingUploadsByKey((prev) => {
      const existing = prev[key] ?? [];
      const nextItems = existing.filter((x) => x.id !== id);
      if (nextItems.length === existing.length) return prev;
      const next = { ...prev };
      if (nextItems.length === 0) delete next[key];
      else next[key] = nextItems;
      return next;
    });
  }, []);

  const renderPendingUploadCard = useCallback(
    (item: { id: string; filename: string }) => {
      return (
        <div
          key={item.id}
          className={[
            "flex w-full min-w-0 items-center gap-3 rounded-xl border border-solid border-white/70 bg-[linear-gradient(135deg,rgba(255,255,255,0.88)_0%,rgba(232,236,242,0.70)_42%,rgba(184,194,208,0.54)_100%)] px-3 py-2.5 text-left shadow-[0_18px_50px_rgba(0,0,0,0.24),inset_0_1px_0_rgba(255,255,255,0.92),inset_0_-1px_0_rgba(148,163,184,0.24)] backdrop-blur-2xl",
            "flex w-full min-w-0 items-center gap-3 rounded-xl border border-solid border-white/90 bg-[linear-gradient(135deg,rgba(255,255,255,0.99)_0%,rgba(255,255,255,0.96)_58%,rgba(248,250,252,0.94)_100%)] px-3 py-2.5 text-left shadow-[0_18px_50px_rgba(0,0,0,0.20),inset_0_1px_0_rgba(255,255,255,1),inset_0_-1px_0_rgba(203,213,225,0.18)] backdrop-blur-xl",
            recentlyInsertedIntakeFileIds.has(item.id)
              ? "uploaded-evidence-insert"
              : ""
          ].join(" ")}
        >
          <div className="flex h-7 w-7 flex-none items-center justify-center">
            <FileTypeIcon name={item.filename} size={14} />
          </div>
          <div className="flex min-w-0 flex-1 flex-col items-start gap-1 text-left">
            <span className="block w-full min-w-0 max-w-full truncate text-left text-caption-bold font-caption-bold text-fg">
              {item.filename}
            </span>
            <span className="text-left text-caption font-caption text-fg-2">
              {tr("uploadingDocument")}
            </span>
          </div>
          <div className="flex flex-none items-center">
            <Spinner className="size-3 text-accent-seed" />
          </div>
        </div>
      );
    },
    [recentlyInsertedIntakeFileIds]
  );

  const renderUploadedIntakeFileCard = useCallback(
    (file: OutgoingIntakeFile, index: number) => {
      const isSelected = previewFile?.file_id === file.file_id;
      const reviewCheckItems = getIntakeFileReviewCheckItems(
        file,
        suppressedDuplicateMatchIds
      );
      const hasWarning = reviewCheckItems.length > 0;
      const hasFailedCheck = reviewCheckItems.some(
        (item) => item.status === "failed"
      );
      const reviewPending = file.reviewStatus === "checking";
      const previewGenerating = isIntakeFilePreviewGenerating(file);
      const canPreview = canPreviewIntakeFile(file);
      const statusIcon = hasFailedCheck
        ? "FeatherAlertCircle"
        : hasWarning
        ? "FeatherAlertTriangle"
        : "FeatherCheckCircle";
      const statusIconClass = hasFailedCheck
        ? "text-error-600"
        : hasWarning
        ? "text-warning-700"
        : "text-success-600";
      const stagedCardSubtitle = previewGenerating
        ? "Generating file preview"
        : reviewPending
        ? "Checking file quality"
        : reviewCheckItems[0]?.message;
      const statusLabel = reviewPending
        ? "Reviewing"
        : hasFailedCheck
        ? "Needs attention"
        : hasWarning
        ? "Warning"
        : "Ready";
      const isDeleting = deletingIntakeFileIds.has(file.file_id);
      const isRecentlyInserted = recentlyInsertedIntakeFileIds.has(
        file.file_id
      );
      return (
        <button
          ref={(node) => {
            uploadedDocumentCardRefs.current[file.file_id] = node;
          }}
          key={file.file_id}
          type="button"
          className={[
            "flex w-full min-w-0 items-center gap-3 rounded-xl border border-solid px-3 py-2.5 text-left shadow-[0_18px_50px_rgba(0,0,0,0.20),inset_0_1px_0_rgba(255,255,255,1),inset_0_-1px_0_rgba(203,213,225,0.18)] backdrop-blur-xl transition hover:-translate-y-0.5 hover:shadow-[0_26px_70px_rgba(0,0,0,0.24),inset_0_1px_0_rgba(255,255,255,1),inset_0_-1px_0_rgba(203,213,225,0.24)]",
            isSelected
              ? "border-white bg-[linear-gradient(135deg,rgba(255,255,255,1)_0%,rgba(255,255,255,0.98)_58%,rgba(248,250,252,0.96)_100%)] ring-1 ring-white/90"
              : "border-white/90 bg-[linear-gradient(135deg,rgba(255,255,255,0.98)_0%,rgba(255,255,255,0.95)_58%,rgba(248,250,252,0.92)_100%)] hover:border-white hover:bg-[linear-gradient(135deg,rgba(255,255,255,1)_0%,rgba(255,255,255,0.98)_58%,rgba(248,250,252,0.96)_100%)]",
            !canPreview ? "cursor-not-allowed" : "",
            isRecentlyInserted ? "uploaded-evidence-insert" : "",
            isDeleting ? "uploaded-evidence-fade-away" : ""
          ].join(" ")}
          aria-disabled={!canPreview}
          title={
            !canPreview
              ? getIntakeFilePreviewUnavailableMessage(file)
              : undefined
          }
          onClick={() => {
            if (!canPreview) return;
            openQuickPreviewOverlay(file);
          }}
        >
          <div className="flex h-7 w-7 flex-none items-center justify-center rounded-full border border-solid border-white/90 bg-[linear-gradient(145deg,rgba(255,255,255,1),rgba(248,250,252,0.82))] text-xs leading-none text-fg shadow-[inset_0_1px_0_rgba(255,255,255,1),0_1px_3px_rgba(15,23,42,0.12)]">
            {index + 1}
          </div>
          <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
            <div className="flex w-full min-w-0 items-center gap-1.5">
              <FileTypeIcon name={file.filename} size={14} />
              <span className="min-w-0 truncate text-caption-bold font-caption-bold text-fg">
                {file.filename}
              </span>
            </div>
            {stagedCardSubtitle ? (
              <span className="line-clamp-1 text-caption font-caption text-fg-2">
                {stagedCardSubtitle}
              </span>
            ) : null}
          </div>
          <div className="flex flex-none items-center">
            {previewGenerating || reviewPending ? (
              <Spinner className="size-3 text-accent-seed" />
            ) : (
              <IntakeIcon
                className={`text-caption font-caption ${statusIconClass}`}
                name={statusIcon}
              />
            )}
            <span className="sr-only">{statusLabel}</span>
          </div>
        </button>
      );
    },
    [
      deletingIntakeFileIds,
      openQuickPreviewOverlay,
      previewFile?.file_id,
      recentlyInsertedIntakeFileIds,
      suppressedDuplicateMatchIds
    ]
  );

  const questionNumberByKey = useMemo(() => {
    const schema = intake?.schema ?? [];
    const out = new Map<string, number>();

    let i = 0;
    const walk = (
      n: OutgoingIntakeFormNode,
      ctx?: { groupId?: string | null; instanceId?: string | null }
    ) => {
      if (!n) return;
      if (n.node_type === "question") {
        const k = decisionKey({
          question_id: n.node_id,
          repeatable_group_id: ctx?.groupId ?? null,
          repeatable_group_instance_id: ctx?.instanceId ?? null
        });
        i += 1;
        out.set(k, i);
        return;
      }
      if (n.node_type === "repeatable_group") {
        const groupId = n.node_id;
        const instances = groups[groupId] ?? [];
        for (const inst of instances) {
          for (const child of n.children ?? []) {
            walk(child, { groupId, instanceId: inst.instanceId });
          }
        }
        return;
      }
      for (const child of n.children ?? []) {
        walk(child, ctx);
      }
    };

    for (const n of schema) {
      walk(n, { groupId: null, instanceId: null });
    }
    return out;
  }, [groups, intake?.schema]);

  // Flagged answers the client has answered again. Requested-change answers are
  // blanked on load, so any value here means the client has addressed the
  // request. Re-entering the same value counts: the attorney can be wrong about
  // a requested change, and the client needs a way to stand by their original
  // answer (explaining why in a comment) rather than being forced to alter it.
  const resolvedRevisionKeys = useMemo(() => {
    const out = new Set<string>();
    if (!isRevisionRequested) return out;
    for (const key of rejectedByKey.keys()) {
      const [questionId, groupIdRaw, instanceIdRaw] = key.split("|");
      const groupId = (groupIdRaw ?? "").trim();
      const instanceId = (instanceIdRaw ?? "").trim();
      const current =
        groupId && instanceId
          ? (groups[groupId] ?? []).find((x) => x.instanceId === instanceId)
              ?.answers?.[questionId] ?? null
          : answersById[questionId] ?? null;
      if (isAnswerProvided(current)) out.add(key);
    }
    return out;
  }, [answersById, groups, isRevisionRequested, rejectedByKey]);

  // FN revision: mirror the attorney review tint per answer — requested changes
  // red, everything else (accepted) green — and drop the tint entirely once a
  // flagged answer has been answered again, so only outstanding items stay red.
  // Shared by the guided and single-page layouts.
  const revisionStatusClass = useCallback(
    (key: string): string => {
      if (!isRevisionRequested) return "";
      if (!rejectedByKey.has(key)) return "intake-review-accepted";
      return resolvedRevisionKeys.has(key) ? "" : "intake-review-rejected";
    },
    [isRevisionRequested, rejectedByKey, resolvedRevisionKeys]
  );

  // Uploads the quality check marked "Needs attention", confirmed before submit
  // so a blank or unreadable page isn't sent without her knowing.
  const [attentionUploads, setAttentionUploads] = useState<
    { id: string; name: string; reason: string }[] | null
  >(null);

  const submit = useCallback(
    async (opts?: { confirmedUploads?: boolean }) => {
      if (isPersistenceDisabled) {
        showToast({
          variant: "neutral",
          icon: "FeatherInfo",
          title: "Read-only",
          description: "Submitting is disabled in read-only mode."
        });
        return;
      }
      if (!safeToken || !intake) return;
      if (isSubmitting) return;
      if (!opts?.confirmedUploads) {
        const all = [
          ...Object.values(answersById),
          ...Object.values(groups).flatMap((instances) =>
            instances.flatMap((inst) => Object.values(inst.answers))
          )
        ];
        const flagged = all.flatMap((a) =>
          (a?.files ?? []).flatMap((file) => {
            const failed = getIntakeFileReviewCheckItems(file).find(
              (item) => item.status === "failed" && item.type !== "duplicate"
            );
            return failed
              ? [
                  {
                    id: file.file_id,
                    name: file.filename,
                    reason: failed.message
                  }
                ]
              : [];
          })
        );
        if (flagged.length) {
          setAttentionUploads(flagged);
          return;
        }
      }
      setIsSubmitting(true);
      try {
        const answers: OutgoingIntakeAnswer[] = [];
        for (const a of Object.values(answersById)) {
          if (a) {
            const hasFiles = (a.files ?? []).length > 0;
            const hasValue = hasMeaningfulValue(a.value);
            const hasComment = Boolean(normalizeText(a.comment));
            if (hasFiles || hasValue || hasComment) answers.push(a);
          }
        }
        for (const instances of Object.values(groups)) {
          for (const inst of instances) {
            for (const a of Object.values(inst.answers)) {
              const hasFiles = (a.files ?? []).length > 0;
              const hasValue = hasMeaningfulValue(a.value);
              const hasComment = Boolean(normalizeText(a.comment));
              if (hasFiles || hasValue || hasComment) answers.push(a);
            }
          }
        }

        // Answers to steps her later answers hid (spouse details, then "Single") aren't sent.
        const values: Record<string, unknown> = {};
        for (const [id, a] of Object.entries(answersById))
          values[id] = a?.value;
        const hiddenQuestions = new Set<string>();
        const hiddenGroups = new Set<string>();
        const hiddenSteps = flattenIntakeStepNodes(intake.schema ?? []).filter(
          (step) => !isNodeVisible(step, values)
        );
        for (const step of hiddenSteps) {
          if (step.node_type === "repeatable_group")
            hiddenGroups.add(String(step.node_id));
          else
            flatQuestions([step]).forEach((q) =>
              hiddenQuestions.add(String(q.node_id))
            );
        }
        const shown = answers.filter((a) =>
          a.repeatable_group_id
            ? !hiddenGroups.has(String(a.repeatable_group_id))
            : !hiddenQuestions.has(String(a.question_id))
        );

        const mergedAnswers = (() => {
          if (!isRevisionRequested) return shown;
          const rejectedKeys = new Set(rejectedByKey.keys());
          const byKey = new Map<string, OutgoingIntakeAnswer>();
          for (const a of baselineAnswerByKeyRef.current.values()) {
            const k = decisionKey({
              question_id: a.question_id,
              repeatable_group_id: a.repeatable_group_id ?? null,
              repeatable_group_instance_id:
                a.repeatable_group_instance_id ?? null
            });
            if (!rejectedKeys.has(k)) {
              byKey.set(k, a);
            }
          }
          for (const a of shown) {
            const k = decisionKey({
              question_id: a.question_id,
              repeatable_group_id: a.repeatable_group_id ?? null,
              repeatable_group_instance_id:
                a.repeatable_group_instance_id ?? null
            });
            byKey.set(k, a);
          }
          return Array.from(byKey.values());
        })();

        await PublicOutgoingIntakesAPI.submit(safeToken, {
          answers: mergedAnswers,
          previous_submission_id: intake.latest_submission_id ?? null
        });
        showToast({
          variant: "success",
          icon: "FeatherCheckCircle",
          title: tr("submitted"),
          description: tr("submittedBody"),
          durationMs: 2500
        });
        await load();
      } catch (err) {
        showToast({
          variant: "error",
          title: tr("couldntSubmit"),
          description: err instanceof Error ? err.message : tr("pleaseTryAgain")
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      answersById,
      groups,
      intake,
      isRevisionRequested,
      isPersistenceDisabled,
      isSubmitting,
      load,
      rejectedByKey,
      safeToken,
      showToast
    ]
  );

  // Per-intake page background + a legible foreground derived from it. The
  // custom background only applies when firm branding is enabled; otherwise we
  // keep the default navy. When branding sets a custom (often light) background,
  // the default white-on-navy theme would be unreadable, so text/borders/
  // surfaces switch to dark tokens.
  const hasIntakeLawFirmLogo =
    normalizeText(intake?.law_firm_logo_url).length > 0;
  const effectiveFirmBrandingEnabled =
    intake?.enable_firm_branding === true ||
    (intake?.enable_firm_branding == null && hasIntakeLawFirmLogo);
  const intakePageBg =
    (effectiveFirmBrandingEnabled &&
      normalizeText(intake?.intake_background_color)) ||
    BRAND_BLUE;
  const isDarkIntakeBg = isDarkColor(intakePageBg);
  const intakeFgRgb = isDarkIntakeBg ? "255, 255, 255" : "15, 23, 42";
  const intakeFgSolid = isDarkIntakeBg ? "#FFFFFF" : "#0f172a";
  // RGB triple of the page background, used to build a top scrim gradient that
  // fades scrolling content into the page color before it reaches the pinned
  // logo (fading to the same color avoids the fade-to-black artifact).
  const intakePageBgRgb = (() => {
    const rgb = hexToRgb(intakePageBg);
    return rgb ? `${rgb.r}, ${rgb.g}, ${rgb.b}` : "0, 0, 139";
  })();

  const renderQuestion = useCallback(
    (
      q: OutgoingIntakeFormNode,
      ctx?: {
        groupId?: string;
        instanceId?: string;
        padded?: boolean;
        hideLabel?: boolean;
        hideFileList?: boolean;
      }
    ) => {
      const questionId = q.node_id;
      const groupId = ctx?.groupId ?? null;
      const instanceId = ctx?.instanceId ?? null;
      const padded = ctx?.padded ?? true;
      const hideLabel = ctx?.hideLabel ?? false;
      const hideFileList = ctx?.hideFileList ?? false;
      const key = decisionKey({
        question_id: questionId,
        repeatable_group_id: groupId,
        repeatable_group_instance_id: instanceId
      });
      const rejection = rejectedByKey.get(key);
      const isRejected = Boolean(rejection);
      const showInputError = Boolean(rejection) && !isRevisionRequested;
      const isLockedNonRejected = isRevisionRequested && !isRejected;
      const isReadOnly =
        isAwaitingReview ||
        isAccepted ||
        isLockedNonRejected ||
        isAttorneyReadOnly ||
        reviewMode;
      const isDisabled =
        isAwaitingReview ||
        isAccepted ||
        isLockedNonRejected ||
        isAttorneyReadOnly ||
        reviewMode;

      const getAnswer = (): OutgoingIntakeAnswer => {
        if (groupId && instanceId) {
          const inst = (groups[groupId] ?? []).find(
            (x) => x.instanceId === instanceId
          );
          return (
            inst?.answers[questionId] ?? {
              question_id: questionId,
              repeatable_group_id: groupId,
              repeatable_group_instance_id: instanceId,
              value: null,
              files: []
            }
          );
        }
        return (
          answersById[questionId] ?? {
            question_id: questionId,
            value: null,
            files: []
          }
        );
      };

      const setAnswer = (next: OutgoingIntakeAnswer) => {
        if (groupId && instanceId) {
          setGroups((prev) => {
            const out = { ...prev };
            const items = [...(out[groupId] ?? [])];
            const idx = items.findIndex((x) => x.instanceId === instanceId);
            if (idx < 0) return prev;
            const inst = items[idx];
            items[idx] = {
              ...inst,
              answers: { ...inst.answers, [questionId]: next }
            };
            out[groupId] = items;
            return out;
          });
          return;
        }
        setAnswersById((prev) => ({ ...prev, [questionId]: next }));
      };

      const a = getAnswer();
      const files = a.files ?? [];
      const isCompleted = files.length > 0 || hasMeaningfulValue(a.value);
      const reviewAnswerWasProvided =
        files.length > 0 || hasMeaningfulValue(a.value);
      const forcedSingleSelectOptions = getForcedSingleSelectOptions(q);
      const effectiveQuestionType = forcedSingleSelectOptions
        ? "single_select"
        : q.question_type;
      const effectiveOptions = forcedSingleSelectOptions ?? q.options ?? [];
      const prefillOffer =
        !groupId &&
        !reviewMode &&
        !isReadOnly &&
        !isCompleted &&
        !prefillDismissed.has(questionId)
          ? intake?.prefill?.[questionId]
          : undefined;
      const prefillText = prefillOffer
        ? prefillDisplay(
            { question_type: effectiveQuestionType, options: effectiveOptions },
            prefillOffer.value,
            intakeLanguage.language,
            { yes: tr("yes"), no: tr("no") }
          )
        : "";

      const baseLabel = displayQuestionLabel(q, questionId);
      const label = `${baseLabel}${q.required ? " *" : ""}`;
      const questionNumber = questionNumberByKey.get(key);
      const numberedLabel = questionNumber
        ? `${questionNumber}. ${label}`
        : label;
      const helpText = (q.help_text ?? "").trim();
      const questionLabel = (
        <div className="flex items-center gap-2">
          <div className={QUESTION_LABEL_TEXT_CLASSNAME}>{numberedLabel}</div>
          {helpText ? (
            <div className="text-caption font-caption text-subtext-color">
              {helpText}
            </div>
          ) : null}
          {isCompleted ? (
            <IntakeIcon
              className="shrink-0 text-body font-body text-success-600"
              name="FeatherCheckCircle"
              aria-hidden
            />
          ) : null}
          {reviewMode && a.source === "assistant" ? (
            <AssistantAnswerBadge metadata={a.source_metadata} />
          ) : null}
        </div>
      );
      const isBlock =
        effectiveQuestionType === "address" ||
        effectiveQuestionType === "long_text" ||
        effectiveQuestionType === "file" ||
        effectiveQuestionType === "multi_select";
      return (
        <div
          key={key}
          className={[
            padded ? "w-full px-6 mobile:px-4" : "w-full",
            // Used by section-level grid layout (two fields per row).
            isBlock ? "col-span-2" : "col-span-1"
          ].join(" ")}
        >
          <div className="flex w-full flex-col items-start gap-2">
            {reviewMode && !reviewAnswerWasProvided ? (
              <div className="flex w-full flex-col items-start gap-2">
                {hideLabel ? null : questionLabel}
                <div className="typeform-review-value">
                  <span className="typeform-review-empty">
                    {tr("notProvided")}
                  </span>
                </div>
              </div>
            ) : prefillOffer && prefillText ? (
              <div className="flex w-full flex-col items-start gap-3">
                {hideLabel ? null : questionLabel}
                <PrefillConfirm
                  display={prefillText}
                  source={
                    prefillOffer.source?.kind === "document"
                      ? [tr("prefill.fromDocs"), prefillOffer.source.label]
                          .filter(Boolean)
                          .join(" · ")
                      : tr("prefill.onFile")
                  }
                  labels={{
                    ask: tr("prefill.ask"),
                    yes: tr("prefill.yes"),
                    change: tr("prefill.change")
                  }}
                  onConfirm={() => {
                    advanceAfterPrefillRef.current = true;
                    setAnswer({ ...a, value: prefillOffer.value });
                  }}
                  onChange={() => {
                    setPrefillDismissed((prev) =>
                      new Set(prev).add(questionId)
                    );
                    setAnswer({ ...a, value: prefillOffer.value });
                  }}
                  fgRgb={intakeFgRgb}
                />
              </div>
            ) : effectiveQuestionType === "address" ? (
              <div className="flex w-full flex-col items-start gap-4">
                {hideLabel ? null : questionLabel}
                <div className="flex w-full flex-col items-start gap-4">
                  {(() => {
                    const current = asAddressValue(a.value);
                    const setPart = (
                      part: keyof AddressValue,
                      nextValue: string
                    ) => {
                      if (isReadOnly) return;
                      const next: AddressValue = {
                        ...current,
                        [part]: nextValue
                      };
                      setAnswer({ ...a, value: next });
                    };
                    return (
                      <>
                        <IntakeTextField
                          className="h-auto w-full"
                          label="Street Address"
                          error={showInputError}
                          readOnly={isReadOnly}
                          placeholder={tr("street")}
                          value={current.street ?? ""}
                          onChange={(e) => setPart("street", e.target.value)}
                        />
                        <div className="flex w-full items-start gap-4">
                          <IntakeTextField
                            className="h-auto min-w-0 grow shrink basis-0"
                            label={tr("city")}
                            error={showInputError}
                            readOnly={isReadOnly}
                            placeholder={tr("city")}
                            value={current.city ?? ""}
                            onChange={(e) => setPart("city", e.target.value)}
                          />
                          <IntakeTextField
                            className="h-auto w-40 flex-none"
                            label={tr("state")}
                            error={showInputError}
                            readOnly={isReadOnly}
                            placeholder={tr("statePlaceholder")}
                            value={current.state ?? ""}
                            onChange={(e) => setPart("state", e.target.value)}
                          />
                        </div>
                        <div className="flex w-full items-start gap-4">
                          <IntakeTextField
                            className="h-auto min-w-0 grow shrink basis-0"
                            label={tr("postal")}
                            error={showInputError}
                            readOnly={isReadOnly}
                            placeholder={tr("postalPlaceholder")}
                            value={current.zip_code ?? ""}
                            onChange={(e) =>
                              setPart("zip_code", e.target.value)
                            }
                          />
                          <IntakeTextField
                            className="h-auto min-w-0 grow shrink basis-0"
                            label={tr("country")}
                            error={showInputError}
                            readOnly={isReadOnly}
                            placeholder={tr("country")}
                            value={current.country ?? ""}
                            onChange={(e) => setPart("country", e.target.value)}
                          />
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            ) : effectiveQuestionType === "long_text" ? (
              <div className="flex w-full flex-col items-start gap-2">
                {hideLabel ? null : questionLabel}
                <Textarea
                  className="field-sizing-fixed w-full"
                  aria-invalid={showInputError || undefined}
                  readOnly={isReadOnly}
                  value={typeof a.value === "string" ? a.value : ""}
                  placeholder={getTypeformInputPlaceholder(q)}
                  onChange={(e) => {
                    if (isReadOnly) return;
                    setAnswer({ ...a, value: e.target.value });
                  }}
                />
              </div>
            ) : effectiveQuestionType === "boolean" ? (
              <div className="flex w-full flex-col items-start gap-2">
                {hideLabel ? null : questionLabel}
                <div className="flex w-full max-w-90 flex-col gap-2">
                  {[
                    { value: true, label: tr("yes") },
                    { value: false, label: tr("no") }
                  ].map((opt, index) => (
                    <TypeformOptionCard
                      key={opt.label}
                      optionKey={String.fromCharCode(65 + index)}
                      label={opt.label}
                      isSelected={a.value === opt.value}
                      disabled={isDisabled}
                      selectionMode="single"
                      onSelect={() => setAnswer({ ...a, value: opt.value })}
                    />
                  ))}
                </div>
              </div>
            ) : effectiveQuestionType === "single_select" &&
              effectiveOptions.length > 0 ? (
              <div className="flex w-full flex-col items-start gap-2">
                {hideLabel ? null : questionLabel}
                {effectiveOptions.length <= 4 ? (
                  <div className="flex w-full max-w-90 flex-col gap-2">
                    {effectiveOptions.map((opt, index) => (
                      <TypeformOptionCard
                        key={opt.option_id}
                        optionKey={String.fromCharCode(65 + index)}
                        label={displayOptionLabel(q, opt.label)}
                        isSelected={a.value === opt.option_id}
                        disabled={isDisabled}
                        selectionMode="single"
                        onSelect={() =>
                          setAnswer({ ...a, value: opt.option_id })
                        }
                      />
                    ))}
                  </div>
                ) : (
                  <TypeformSearchableSelect
                    disabled={isDisabled}
                    placeholder={tr("typeOrSelect")}
                    pageBg={intakePageBg}
                    fgRgb={intakeFgRgb}
                    options={effectiveOptions.map((opt) => ({
                      value: opt.option_id,
                      label: displayOptionLabel(q, opt.label)
                    }))}
                    value={typeof a.value === "string" ? a.value : undefined}
                    onChange={(value) => {
                      if (isDisabled) return;
                      setAnswer({ ...a, value });
                    }}
                  />
                )}
              </div>
            ) : effectiveQuestionType === "multi_select" &&
              effectiveOptions.length > 0 ? (
              <div className="flex w-full flex-col items-start gap-2">
                {hideLabel ? null : questionLabel}
                {/* Left full width rather than capped like the single-select
                    column, because criteria-style options run long enough that a
                    narrow card would wrap every one of them onto three lines. */}
                <div className="flex w-full flex-col gap-2">
                  {effectiveOptions.map((opt, index) => {
                    const current = Array.isArray(a.value) ? a.value : [];
                    const checked = current.includes(opt.option_id);
                    return (
                      <TypeformOptionCard
                        key={opt.option_id}
                        optionKey={String.fromCharCode(65 + index)}
                        label={displayOptionLabel(q, opt.label)}
                        isSelected={checked}
                        disabled={isDisabled}
                        selectionMode="multiple"
                        onSelect={() => {
                          const next = new Set(current);
                          if (checked) next.delete(opt.option_id);
                          else next.add(opt.option_id);
                          setAnswer({ ...a, value: Array.from(next) });
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            ) : effectiveQuestionType === "file" ? (
              <div className="flex w-full flex-col items-start gap-2">
                {hideLabel ? null : questionLabel}
                {(() => {
                  const key = `file|${questionId}|${groupId ?? ""}|${
                    instanceId ?? ""
                  }`;
                  const uploadSelectedFiles = (selected: File[]) => {
                    if (isDisabled) return;
                    const filesToUpload = (selected ?? []).filter(Boolean);
                    if (!filesToUpload.length) return;
                    const placeholders = filesToUpload.map((f) => ({
                      id:
                        typeof crypto !== "undefined" &&
                        typeof crypto.randomUUID === "function"
                          ? crypto.randomUUID()
                          : `p_${Date.now()}_${Math.random()
                              .toString(16)
                              .slice(2)}`,
                      filename: String(f?.name ?? "Uploading")
                    }));
                    addPendingUploads(key, placeholders);
                    startUploads(key, filesToUpload.length);

                    const tasks = filesToUpload.map((file, idx) => {
                      const placeholderId = placeholders[idx]?.id ?? "";
                      return uploadFile(questionId, file, {
                        groupId: groupId ?? undefined,
                        instanceId: instanceId ?? undefined
                      }).finally(() => {
                        finishUploads(key, 1);
                        resolvePendingUpload(key, placeholderId);
                      });
                    });

                    Promise.allSettled(tasks)
                      .then((results) => {
                        const okCount = results.filter(
                          (r) => r.status === "fulfilled"
                        ).length;
                        const failCount = results.length - okCount;
                        if (okCount > 0) {
                          showToast({
                            variant: "success",
                            icon: "FeatherUpload",
                            title: tr("uploaded"),
                            description:
                              okCount === 1
                                ? tr("uploadedBody", { count: 1 })
                                : tr("uploadedBody", { count: okCount }),
                            durationMs: 2000
                          });
                        }
                        if (failCount > 0) {
                          showToast({
                            variant: "error",
                            title: tr("someFailed"),
                            description: tr("pleaseTryAgain")
                          });
                        }
                      })
                      .catch((err) =>
                        showToast({
                          variant: "error",
                          title: tr("uploadFailed"),
                          description:
                            err instanceof Error
                              ? err.message
                              : tr("pleaseTryAgain")
                        })
                      )
                      .finally(() => {
                        // Autosave draft when this upload event completes.
                        window.setTimeout(() => autosaveDraftLocal(), 0);
                      });
                  };

                  const isUploading = (uploadingCountByKey[key] ?? 0) > 0;

                  return (
                    <IntakeScopedDiv
                      className="public-intake-uploader w-full"
                      styles={{
                        "> div > div": {
                          backgroundColor:
                            "rgba(255, 255, 255, 0.08) !important",
                          borderColor: "rgba(255, 255, 255, 0.28) !important"
                        },
                        "> div > div:hover": {
                          backgroundColor:
                            "rgba(255, 255, 255, 0.12) !important"
                        },
                        "button.intake-upload-button": {
                          background: "#FFFFFF !important",
                          color: "#111827 !important",
                          borderColor: "rgba(255, 255, 255, 0.85) !important",
                          boxShadow:
                            "0 12px 28px rgba(0, 0, 0, 0.18) !important"
                        },
                        "button.intake-upload-button, button.intake-upload-button *":
                          {
                            color: "#111827 !important"
                          },
                        "button.intake-upload-button:hover": {
                          background: "rgba(255, 255, 255, 0.9) !important"
                        },
                        "button.intake-upload-button:disabled": {
                          background: "rgba(255, 255, 255, 0.45) !important",
                          color: "rgba(17, 24, 39, 0.62) !important"
                        },
                        "button.intake-upload-button:disabled *": {
                          color: "rgba(17, 24, 39, 0.62) !important"
                        },
                        "button:not(.intake-upload-button), button:not(.intake-upload-button) *":
                          {
                            color: "#111827 !important"
                          },
                        "button:not(.intake-upload-button) span": {
                          display: "inline !important",
                          color: "#111827 !important"
                        },
                        "> div > div p": {
                          color: "rgba(255, 255, 255, 0.62) !important"
                        },
                        "> div > div p:first-of-type": {
                          color: "rgba(255, 255, 255, 0.62) !important"
                        }
                      }}
                    >
                      <Uploader
                        layout={UploaderLayout.Vertical}
                        variant="canonical"
                        uploadSurfaceVariant="largeCard"
                        showUploadOptionsAsTiles
                        showUploadProgressTiles={false}
                        hideUrlUploadOption
                        onUpload={(selectedFiles) => {
                          const selected = Array.from(selectedFiles ?? []);
                          if (!selected.length) return;
                          uploadSelectedFiles(selected);
                        }}
                        isDisabled={isDisabled || isUploading}
                        storagePath={null}
                        collectionPath={null}
                        dragAndDropCta={
                          isUploading ? tr("uploadingShort") : tr("dragDrop")
                        }
                        buttonCta={
                          files.length ? tr("uploadMore") : tr("upload")
                        }
                      />
                    </IntakeScopedDiv>
                  );
                })()}
                {!hideFileList && (pendingUploadsByKey[key] ?? []).length ? (
                  <div className="flex w-full flex-col items-stretch gap-2">
                    {(pendingUploadsByKey[key] ?? []).map((p) =>
                      renderPendingUploadCard(p)
                    )}
                  </div>
                ) : null}
                {!hideFileList && files.length ? (
                  <div className="flex w-full flex-col items-stretch gap-2">
                    {files.map((f, index) =>
                      renderUploadedIntakeFileCard(f, index)
                    )}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="flex w-full flex-col items-start gap-2">
                {hideLabel ? null : questionLabel}
                {reviewMode ? (
                  <div className="typeform-review-value">
                    {typeof a.value === "string" && a.value.trim() ? (
                      q.question_type === "date" ? (
                        dateInWords(a.value, intakeLanguage.language) ?? a.value
                      ) : (
                        a.value
                      )
                    ) : (
                      <span className="typeform-review-empty">
                        {tr("notProvided")}
                      </span>
                    )}
                  </div>
                ) : q.question_type === "date" ? (
                  <IntakeDateInput
                    value={a.value}
                    onChange={(iso) => setAnswer({ ...a, value: iso })}
                    language={intakeLanguage.language}
                    labels={{
                      day: tr("date.day"),
                      month: tr("date.month"),
                      year: tr("date.year"),
                      monthPlaceholder: tr("date.pickMonth"),
                      echo: (words) => tr("date.echo", { date: words }),
                      invalid: tr("date.invalid")
                    }}
                    readOnly={isReadOnly}
                    error={showInputError}
                    fgRgb={intakeFgRgb}
                    ariaLabel={
                      normalizeText(q.title) ||
                      normalizeText(q.prompt) ||
                      tr("yourAnswer")
                    }
                  />
                ) : (
                  <IntakeTextField
                    className="h-auto w-full"
                    error={showInputError}
                    type="text"
                    aria-label={
                      normalizeText(q.title) ||
                      normalizeText(q.prompt) ||
                      tr("yourAnswer")
                    }
                    readOnly={isReadOnly}
                    value={typeof a.value === "string" ? a.value : ""}
                    placeholder={getTypeformInputPlaceholder(q)}
                    onChange={(e) => {
                      if (isReadOnly) return;
                      setAnswer({ ...a, value: e.target.value });
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      );
    },
    [
      answersById,
      dragOverKey,
      groups,
      intakePageBg,
      intakeFgRgb,
      isAccepted,
      isAwaitingReview,
      isRevisionRequested,
      rejectedByKey,
      renderPendingUploadCard,
      renderUploadedIntakeFileCard,
      reviewMode,
      showToast,
      uploadFile,
      intake?.prefill,
      intakeLanguage.language,
      prefillDismissed
    ]
  );

  const renderNode = useCallback(
    (n: OutgoingIntakeFormNode): React.ReactNode => {
      if (n.node_type === "question") return renderQuestion(n);
      if (n.node_type === "repeatable_group") {
        const groupId = n.node_id;
        const instances = groups[groupId] ?? [];

        const renderedInstances = instances
          .map((inst, idx) => {
            const children = (n.children ?? [])
              .map((child) =>
                child.node_type === "question"
                  ? renderQuestion(child, {
                      groupId,
                      instanceId: inst.instanceId,
                      padded: false
                    })
                  : null
              )
              .filter(Boolean);

            if (children.length === 0) return null;
            return (
              <div
                key={inst.instanceId}
                className="rounded-md border border-solid border-neutral-border bg-default-background p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="text-body-bold font-body-bold text-default-font">
                    {n.title ?? "Item"} #{idx + 1}
                  </div>
                  {isRevisionRequested ? null : (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setGroups((prev) => ({
                          ...prev,
                          [groupId]: (prev[groupId] ?? []).filter(
                            (x) => x.instanceId !== inst.instanceId
                          )
                        }));
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </div>
                <div className="mt-3 flex flex-col gap-3">{children}</div>
              </div>
            );
          })
          .filter(Boolean);

        if (isRevisionRequested && renderedInstances.length === 0) return null;

        return (
          <div
            key={groupId}
            id={`node-${groupId}`}
            className="col-span-2 flex w-full flex-col gap-0"
          >
            <div className="flex w-full items-center justify-between border-y border-solid border-neutral-border bg-fill-1 px-6 py-3">
              <span className={SECTION_HEADER_TEXT_CLASSNAME}>
                {(n.title ?? "Items").toString()}
              </span>
              {isRevisionRequested ? null : (
                <Button
                  variant="secondary"
                  onClick={() => {
                    const instanceId = `i_${Date.now()}_${Math.random()
                      .toString(16)
                      .slice(2)}`;
                    setGroups((prev) => ({
                      ...prev,
                      [groupId]: [
                        ...(prev[groupId] ?? []),
                        { instanceId, answers: {} }
                      ]
                    }));
                  }}
                >
                  {addLabelForGroup(groupId, n.title ?? null)}
                </Button>
              )}
            </div>
            <div className="flex w-full flex-col gap-3 px-6 py-4 mobile:px-4">
              {instances.length === 0 ? (
                <div className="text-caption font-caption text-subtext-color">
                  {tr("noEntries")}
                </div>
              ) : (
                renderedInstances
              )}
            </div>
          </div>
        );
      }
      // section
      const directChildren = (n.children ?? []).filter(Boolean);

      const directFileQuestions = directChildren.filter(
        (c) => c.node_type === "question" && c.question_type === "file"
      ) as OutgoingIntakeFormNode[];
      const otherChildren = directChildren.filter(
        (c) => !(c.node_type === "question" && c.question_type === "file")
      );

      if (isDocumentsSectionNode(n) && directFileQuestions.length > 0) {
        const dividerStyle: React.CSSProperties = {
          borderLeft: "1px solid rgb(226, 232, 240)"
        };

        return (
          <div
            key={n.node_id}
            id={`node-${n.node_id}`}
            className="col-span-2 flex w-full flex-col items-start gap-0"
          >
            {n.title ? (
              <div className="flex w-full items-center gap-2 border-y border-solid border-neutral-border bg-fill-1 px-6 py-3">
                {sectionHeaderIcon(n.title) ? (
                  <div className="flex items-center">
                    {sectionHeaderIcon(n.title)}
                  </div>
                ) : null}
                <span className={SECTION_HEADER_TEXT_CLASSNAME}>{n.title}</span>
              </div>
            ) : null}

            <div className="flex w-full flex-col items-start gap-6 px-0 py-0">
              <div className="flex w-full min-h-30 flex-col items-start overflow-auto bg-default-background">
                <Table>
                  <TableHeader className="[&_tr]:border-b-0">
                    <TableRow className="hover:bg-transparent">
                      <TableHead
                        className={cn(INTAKE_TABLE_HEAD_CLASSNAME, "min-w-60")}
                      >
                        Requested Document
                      </TableHead>
                      <TableHead
                        className={cn(INTAKE_TABLE_HEAD_CLASSNAME, "min-w-80")}
                        style={dividerStyle}
                      >
                        {tr("upload")}
                      </TableHead>
                      <TableHead
                        className={cn(INTAKE_TABLE_HEAD_CLASSNAME, "min-w-55")}
                        style={dividerStyle}
                      >
                        {tr("uploadedFiles")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  {(() => {
                    const rows = directFileQuestions
                      .map((q) => {
                        const questionId = q.node_id;
                        const key = decisionKey({
                          question_id: questionId,
                          repeatable_group_id: null,
                          repeatable_group_instance_id: null
                        });
                        const rejection = rejectedByKey.get(key);
                        const isRejected = Boolean(rejection);
                        const isLockedNonRejected =
                          isRevisionRequested && !isRejected;
                        const isDisabled =
                          isAwaitingReview ||
                          isAccepted ||
                          isLockedNonRejected ||
                          isAttorneyReadOnly;

                        const a = answersById[questionId] ?? {
                          question_id: questionId,
                          value: null,
                          files: []
                        };
                        const files = a.files ?? [];
                        const inputId = `file-${questionId}`;
                        const dragKey = `doc-file|${questionId}`;
                        const isDragOver = dragOverKey === dragKey;

                        const uploadSelectedFiles = (selected: File[]) => {
                          if (isPersistenceDisabled) return;
                          if (isDisabled) return;
                          const filesToUpload = (selected ?? []).filter(
                            Boolean
                          );
                          if (!filesToUpload.length) return;
                          const placeholders = filesToUpload.map((f) => ({
                            id:
                              typeof crypto !== "undefined" &&
                              typeof crypto.randomUUID === "function"
                                ? crypto.randomUUID()
                                : `p_${Date.now()}_${Math.random()
                                    .toString(16)
                                    .slice(2)}`,
                            filename: String(f?.name ?? "Uploading")
                          }));
                          addPendingUploads(key, placeholders);
                          startUploads(key, filesToUpload.length);

                          const tasks = filesToUpload.map((file, idx) => {
                            const placeholderId = placeholders[idx]?.id ?? "";
                            return uploadFile(questionId, file).finally(() => {
                              finishUploads(key, 1);
                              resolvePendingUpload(key, placeholderId);
                            });
                          });

                          Promise.allSettled(tasks)
                            .then((results) => {
                              const okCount = results.filter(
                                (r) => r.status === "fulfilled"
                              ).length;
                              const failCount = results.length - okCount;
                              if (okCount > 0) {
                                showToast({
                                  variant: "success",
                                  icon: "FeatherUpload",
                                  title: tr("uploaded"),
                                  description:
                                    okCount === 1
                                      ? tr("uploadedBody", { count: 1 })
                                      : tr("uploadedBody", { count: okCount }),
                                  durationMs: 2000
                                });
                              }
                              if (failCount > 0) {
                                showToast({
                                  variant: "error",
                                  title: tr("someFailed"),
                                  description: tr("pleaseTryAgain")
                                });
                              }
                            })
                            .catch((err) =>
                              showToast({
                                variant: "error",
                                title: tr("uploadFailed"),
                                description:
                                  err instanceof Error
                                    ? err.message
                                    : tr("pleaseTryAgain")
                              })
                            )
                            .finally(() => {
                              // Autosave draft when this upload event completes.
                              window.setTimeout(() => autosaveDraftLocal(), 0);
                            });
                        };

                        const isUploading = (uploadingCountByKey[key] ?? 0) > 0;
                        const isUploadBlocked = isDisabled || isUploading;
                        const hasUploadError =
                          Boolean(rejection) && !isRevisionRequested;

                        return (
                          <TableRow
                            key={questionId}
                            className={INTAKE_TABLE_ROW_CLASSNAME}
                          >
                            <TableCell className={INTAKE_TABLE_CELL_CLASSNAME}>
                              <div className="flex w-full items-start justify-center gap-1 px-3 py-4">
                                <div className="flex w-full flex-col items-center gap-1 text-center">
                                  <div className="flex items-center justify-center gap-2">
                                    <span
                                      className={
                                        QUESTION_VALUE_LABEL_TEXT_CLASSNAME
                                      }
                                    >
                                      {(() => {
                                        const num =
                                          questionNumberByKey.get(key);
                                        const base = displayQuestionLabel(
                                          q,
                                          questionId
                                        );
                                        return num ? `${num}. ${base}` : base;
                                      })()}
                                      {q.required ? " *" : ""}
                                    </span>
                                    {files.length > 0 ? (
                                      <IntakeIcon
                                        className="shrink-0 text-body font-body text-success-600"
                                        name="FeatherCheckCircle"
                                        aria-hidden
                                      />
                                    ) : null}
                                  </div>
                                  {q.help_text ? (
                                    <span className="text-caption font-caption text-subtext-color">
                                      {String(q.help_text)}
                                    </span>
                                  ) : null}
                                  {rejection?.note ? (
                                    <span className="text-caption font-caption text-error-700 whitespace-pre-wrap wrap-break-word">
                                      {String(rejection.note)}
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                            </TableCell>

                            <TableCell
                              className={INTAKE_TABLE_CELL_CLASSNAME}
                              style={dividerStyle}
                            >
                              <div className="flex w-full items-start gap-1 px-3 py-4">
                                <div className="flex w-full flex-col items-start gap-2">
                                  <input
                                    type="file"
                                    className="hidden"
                                    id={inputId}
                                    multiple
                                    disabled={isDisabled || isUploading}
                                    onChange={(e) => {
                                      const selected = Array.from(
                                        e.target.files ?? []
                                      );
                                      e.target.value = "";
                                      if (!selected.length) return;
                                      uploadSelectedFiles(selected);
                                    }}
                                  />
                                  <div
                                    className={cn(
                                      "group/file-upload flex w-full flex-col items-start gap-1",
                                      isDragOver &&
                                        "outline-solid outline-2 outline-brand-400 outline-offset-2 rounded-md"
                                    )}
                                    onDragEnter={(
                                      e: React.DragEvent<HTMLDivElement>
                                    ) => {
                                      if (isDisabled || isUploading) return;
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setDragOverKey(dragKey);
                                    }}
                                    onDragOver={(
                                      e: React.DragEvent<HTMLDivElement>
                                    ) => {
                                      if (isDisabled || isUploading) return;
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setDragOverKey(dragKey);
                                    }}
                                    onDragLeave={(
                                      e: React.DragEvent<HTMLDivElement>
                                    ) => {
                                      if (isDisabled || isUploading) return;
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setDragOverKey((prev) =>
                                        prev === dragKey ? null : prev
                                      );
                                    }}
                                    onDrop={(
                                      e: React.DragEvent<HTMLDivElement>
                                    ) => {
                                      if (isDisabled || isUploading) return;
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setDragOverKey(null);
                                      const dropped = Array.from(
                                        e.dataTransfer.files ?? []
                                      );
                                      if (!dropped.length) return;
                                      uploadSelectedFiles(dropped);
                                    }}
                                  >
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      disabled={isUploadBlocked}
                                      className={cn(
                                        "h-auto w-full flex-col gap-2 whitespace-normal border border-dashed border-line-3 bg-default-background px-4 py-4 hover:border-brand-400 hover:bg-brand-50 disabled:opacity-100",
                                        isUploadBlocked &&
                                          "border-line-2 bg-fill-1",
                                        hasUploadError &&
                                          "border-danger-line hover:border-danger-line hover:bg-danger-soft"
                                      )}
                                      onClick={() => {
                                        if (isUploadBlocked) return;
                                        const el = document.getElementById(
                                          inputId
                                        ) as HTMLInputElement | null;
                                        el?.click();
                                      }}
                                    >
                                      <span className="flex flex-col items-center gap-1">
                                        <IntakeIcon
                                          name="FeatherUpload"
                                          className={cn(
                                            "text-heading-2 font-heading-2 text-subtext-color",
                                            isUploadBlocked && "text-fg-4",
                                            hasUploadError && "text-danger"
                                          )}
                                        />
                                        <span
                                          className={cn(
                                            "text-body font-body text-default-font",
                                            isUploadBlocked && "text-fg-4",
                                            hasUploadError && "text-danger"
                                          )}
                                        >
                                          {isUploading
                                            ? "Uploading…"
                                            : "Choose file(s) or drag them here"}
                                        </span>
                                        <span
                                          className={cn(
                                            "text-caption font-caption text-subtext-color",
                                            isUploadBlocked && "text-fg-4",
                                            hasUploadError && "text-danger"
                                          )}
                                        >
                                          {isUploading
                                            ? "Uploading your file(s). Please keep this page open."
                                            : "PDF, DOC, DOCX, JPG, or PNG up to 20MB"}
                                        </span>
                                      </span>
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </TableCell>

                            <TableCell
                              className={INTAKE_TABLE_CELL_CLASSNAME}
                              style={dividerStyle}
                            >
                              <div className="flex w-full items-start gap-1 px-3 py-4">
                                {files.length ? (
                                  <div className="flex w-full flex-col items-stretch gap-2">
                                    {(pendingUploadsByKey[key] ?? []).length ? (
                                      <div className="flex w-full flex-col items-stretch gap-2 pb-1">
                                        {(pendingUploadsByKey[key] ?? []).map(
                                          (p) => renderPendingUploadCard(p)
                                        )}
                                      </div>
                                    ) : null}
                                    {files.map((f, index) =>
                                      renderUploadedIntakeFileCard(f, index)
                                    )}
                                  </div>
                                ) : (pendingUploadsByKey[key] ?? []).length ? (
                                  <div className="flex w-full flex-col items-stretch gap-2">
                                    {(pendingUploadsByKey[key] ?? []).map((p) =>
                                      renderPendingUploadCard(p)
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-caption font-caption text-subtext-color">
                                    —
                                  </span>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                      .filter(Boolean) as React.ReactNode[];

                    if (rows.length === 0) {
                      return (
                        <TableBody className={INTAKE_TABLE_BODY_CLASSNAME}>
                          <TableRow
                            className={cn(
                              INTAKE_TABLE_ROW_CLASSNAME,
                              "border-t-0"
                            )}
                          >
                            <TableCell
                              colSpan={3}
                              className={INTAKE_TABLE_CELL_CLASSNAME}
                            >
                              <div className="flex w-full items-center justify-center gap-1 px-3 py-10">
                                <span className="text-caption font-caption text-subtext-color">
                                  {isRevisionRequested
                                    ? tr("noDocChanges")
                                    : tr("noDocs")}
                                </span>
                              </div>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      );
                    }
                    return (
                      <TableBody
                        className={cn(
                          INTAKE_TABLE_BODY_CLASSNAME,
                          "[&_tr:last-child]:border-t"
                        )}
                      >
                        {rows}
                      </TableBody>
                    );
                  })()}
                </Table>
              </div>

              {otherChildren.length > 0 ? (
                <div className="flex w-full flex-col items-start gap-6 px-0 py-0">
                  {
                    otherChildren
                      .map((child) => renderNode(child))
                      .filter(Boolean) as React.ReactNode[]
                  }
                </div>
              ) : null}
            </div>
          </div>
        );
      }

      const renderedChildren = directChildren
        .map((child) => renderNode(child))
        .filter(Boolean);
      if (renderedChildren.length === 0) return null;
      const allRepeatableGroups =
        directChildren.length > 0 &&
        directChildren.every((child) => child.node_type === "repeatable_group");
      if (allRepeatableGroups) {
        // Avoid duplicate headers (e.g. "Employment History" section wrapping
        // an "Employment History" repeatable group that already renders its own header).
        return (
          <div key={n.node_id} id={`node-${n.node_id}`} className="w-full">
            {renderedChildren as React.ReactNode[]}
          </div>
        );
      }
      return (
        <div
          key={n.node_id}
          id={`node-${n.node_id}`}
          className="col-span-2 flex w-full flex-col items-start gap-0"
        >
          {n.title ? (
            <div className="flex w-full items-center gap-2 border-y border-solid border-neutral-border bg-fill-1 px-6 py-3">
              {sectionHeaderIcon(n.title) ? (
                <div className="flex items-center">
                  {sectionHeaderIcon(n.title)}
                </div>
              ) : null}
              <span className={SECTION_HEADER_TEXT_CLASSNAME}>{n.title}</span>
            </div>
          ) : null}
          <div className="grid w-full grid-cols-2 items-start gap-y-6 px-0 py-6 mobile:grid-cols-1">
            {renderedChildren as React.ReactNode[]}
          </div>
        </div>
      );
    },
    [
      answersById,
      dragOverKey,
      finishUploads,
      groups,
      isAccepted,
      isAwaitingReview,
      isPreview,
      isRevisionRequested,
      rejectedByKey,
      renderPendingUploadCard,
      renderQuestion,
      renderUploadedIntakeFileCard,
      showToast,
      startUploads,
      uploadingCountByKey,
      uploadFile
    ]
  );

  // The answers conditional steps read (`show_if` points at top-level questions).
  const visibilityValues = useMemo(() => {
    const out: Record<string, unknown> = {};
    for (const [id, answer] of Object.entries(answersById))
      out[id] = answer?.value;
    return out;
  }, [answersById]);

  const isFormComplete = useMemo(() => {
    if (!intake) return false;
    const schema = intake.schema ?? [];

    const isAnswered = (ans: OutgoingIntakeAnswer | null): boolean => {
      if (!ans) return false;
      const files = ans.files ?? [];
      if (files.length > 0) return true;
      return hasMeaningfulValue(ans.value);
    };

    if (isRevisionRequested) {
      // Every flagged answer has to come back with a value, but it does not have
      // to be a different one.
      return Array.from(rejectedByKey.keys()).every((k) =>
        resolvedRevisionKeys.has(k)
      );
    }

    const getAnswerFor = (params: {
      questionId: string;
      groupId?: string | null;
      instanceId?: string | null;
    }): OutgoingIntakeAnswer | null => {
      const { questionId } = params;
      const groupId = params.groupId ?? null;
      const instanceId = params.instanceId ?? null;
      if (groupId && instanceId) {
        const inst = (groups[groupId] ?? []).find(
          (x) => x.instanceId === instanceId
        );
        return inst?.answers?.[questionId] ?? null;
      }
      return answersById[questionId] ?? null;
    };

    const walk = (
      n: OutgoingIntakeFormNode,
      ctx?: { groupId?: string | null; instanceId?: string | null }
    ): boolean => {
      // A step the client's answers hid can't be required of her.
      if (!ctx?.groupId && !isNodeVisible(n, visibilityValues)) return true;
      if (n.node_type === "question") {
        if (!n.required) return true;
        return isAnswered(
          getAnswerFor({
            questionId: n.node_id,
            groupId: ctx?.groupId ?? null,
            instanceId: ctx?.instanceId ?? null
          })
        );
      }
      if (n.node_type === "repeatable_group") {
        const groupId = n.node_id;
        const minItems = Math.max(0, n.min_items ?? 0);
        const instances = groups[groupId] ?? [];
        // Not enough entries for a required (min_items > 0) group.
        if (instances.length < minItems) return false;
        // Optional group the client declined (no entries) is complete.
        if (instances.length === 0) return true;
        const requiredQs = flatQuestions(n.children ?? []).filter(
          (x) => x.node_type === "question" && Boolean(x.required)
        );
        if (requiredQs.length === 0) return true;
        for (const inst of instances) {
          for (const child of n.children ?? []) {
            if (!walk(child, { groupId, instanceId: inst.instanceId }))
              return false;
          }
        }
        return true;
      }
      for (const child of n.children ?? []) {
        if (!walk(child, ctx)) return false;
      }
      return true;
    };

    for (const n of schema) {
      if (!walk(n)) return false;
    }
    return true;
  }, [
    answersById,
    groups,
    intake,
    isRevisionRequested,
    rejectedByKey,
    resolvedRevisionKeys,
    visibilityValues
  ]);

  const typeformStepNodes = useMemo(() => {
    // The revision-requested flow now mirrors the first attempt: every step is
    // shown (accepted answers read-only/green, requested-change ones
    // editable/red), so there is no longer a rejected-only filter here.
    return flattenIntakeStepNodes(intake?.schema ?? []);
  }, [intake?.schema]);

  // Expand the top-level step nodes into the concrete list of full screens,
  // turning each repeatable group into a lead-in + per-entry sub-question +
  // "add another?" sequence based on the live `groups`/`groupDecisions` state.
  const typeformScreens = useMemo<TypeformScreen[]>(() => {
    const screens: TypeformScreen[] = [];

    typeformStepNodes.forEach((step, stepIndex) => {
      // Conditional steps appear once an earlier answer matches their rule.
      if (!isNodeVisible(step, visibilityValues)) return;
      if (step.node_type !== "repeatable_group") {
        screens.push({
          kind: "question",
          key: `q|${step.node_id}`,
          stepIndex,
          node: step
        });
        return;
      }

      const groupId = String(step.node_id ?? "");
      const childQuestions = groupChildQuestions(step);
      if (childQuestions.length === 0) return;
      const instances = groups[groupId] ?? [];

      // Revision mode surfaces every submitted entry's sub-questions (accepted
      // read-only/green, requested editable/red) but skips the lead-in /
      // add-another / review decision screens since entry management stays
      // disabled while revising.
      if (isRevisionRequested) {
        instances.forEach((instance, entryIndex) => {
          childQuestions.forEach((qNode) => {
            screens.push({
              kind: "group-question",
              key: `grp-q|${groupId}|${instance.instanceId}|${qNode.node_id}`,
              stepIndex,
              group: step,
              instanceId: instance.instanceId,
              entryIndex,
              node: qNode
            });
          });
        });
        return;
      }

      const minItems = Math.max(0, step.min_items ?? 0);
      const maxItems =
        typeof step.max_items === "number" && step.max_items > 0
          ? step.max_items
          : null;
      const decision = groupDecisions[groupId];
      const requiresLeadIn = minItems === 0;

      if (requiresLeadIn) {
        screens.push({
          kind: "group-lead-in",
          key: `grp-lead|${groupId}`,
          stepIndex,
          group: step
        });
      }

      const started =
        minItems > 0 || decision === "yes" || instances.length > 0;
      // Declined, or lead-in not answered yet: don't expand entries.
      if (!started || (requiresLeadIn && decision === "no")) return;

      instances.forEach((instance, entryIndex) => {
        childQuestions.forEach((qNode) => {
          screens.push({
            kind: "group-question",
            key: `grp-q|${groupId}|${instance.instanceId}|${qNode.node_id}`,
            stepIndex,
            group: step,
            instanceId: instance.instanceId,
            entryIndex,
            node: qNode
          });
        });
      });

      const atMax = maxItems != null && instances.length >= maxItems;
      if (instances.length > 0 && !atMax) {
        screens.push({
          kind: "group-add-another",
          key: `grp-add|${groupId}`,
          stepIndex,
          group: step,
          entryIndex: instances.length
        });
      }
      // Final "review your entries" screen once at least one entry exists.
      if (instances.length > 0) {
        screens.push({
          kind: "group-review",
          key: `grp-review|${groupId}`,
          stepIndex,
          group: step
        });
      }
    });

    // Attorney review presents one screen per top-level step: standalone
    // questions as a single question screen, and each repeatable group as one
    // "group-stack" screen whose body lists every entry as its own card. This
    // keeps the review TOC 1:1 with the steps while still surfacing individual
    // entries inside the center panel.
    if (reviewMode) {
      const reviewScreens: TypeformScreen[] = [];
      typeformStepNodes.forEach((step, stepIndex) => {
        if (!isNodeVisible(step, visibilityValues)) return;
        if (step.node_type === "repeatable_group") {
          reviewScreens.push({
            kind: "group-stack",
            key: `grp-stack|${step.node_id}`,
            stepIndex,
            group: step
          });
        } else {
          reviewScreens.push({
            kind: "question",
            key: `q|${step.node_id}`,
            stepIndex,
            node: step
          });
        }
      });
      return reviewScreens;
    }

    // Revision: only the answers the attorney flagged, in order; everything
    // else stays as she sent it (and is merged back in on submit).
    if (isRevisionRequested && !reviewMode && rejectedByKey.size > 0) {
      const flagged = screens.filter((screen) => {
        if (screen.kind === "question")
          return flatQuestions([screen.node]).some((q) =>
            rejectedByKey.has(
              decisionKey({
                question_id: q.node_id,
                repeatable_group_id: null,
                repeatable_group_instance_id: null
              })
            )
          );
        if (screen.kind === "group-question")
          return rejectedByKey.has(
            decisionKey({
              question_id: screen.node.node_id,
              repeatable_group_id: screen.group.node_id,
              repeatable_group_instance_id: screen.instanceId
            })
          );
        return false;
      });
      if (flagged.length) screens.splice(0, screens.length, ...flagged);
    }

    // Final "review your answers" screen, always the last step (revision
    // included, so "Send to …" hands off from a full review).
    if (screens.length > 0) {
      screens.push({
        kind: "final-review",
        key: "final-review",
        stepIndex: typeformStepNodes.length
      });
    }

    return screens;
  }, [
    typeformStepNodes,
    groups,
    groupDecisions,
    isRevisionRequested,
    rejectedByKey,
    reviewMode,
    visibilityValues
  ]);

  // Seed the minimum required entries for groups with min_items > 0 so their
  // first entry's questions appear immediately (no lead-in for required groups).
  useEffect(() => {
    const schemaGroups = flattenIntakeStepNodes(intake?.schema ?? []).filter(
      (n) => n.node_type === "repeatable_group"
    );
    const groupsNeedingSeed = schemaGroups.filter((g) => {
      const minItems = Math.max(0, g.min_items ?? 0);
      if (minItems <= 0) return false;
      const existing = groups[String(g.node_id ?? "")] ?? [];
      return existing.length < minItems;
    });
    if (groupsNeedingSeed.length === 0) return;

    setGroups((prev) => {
      const next = { ...prev };
      groupsNeedingSeed.forEach((g) => {
        const groupId = String(g.node_id ?? "");
        const minItems = Math.max(0, g.min_items ?? 0);
        const existing = next[groupId] ?? [];
        const toAdd = minItems - existing.length;
        if (toAdd <= 0) return;
        const seeded = Array.from({ length: toAdd }, () => ({
          instanceId: createGroupInstanceId(),
          answers: {}
        }));
        next[groupId] = [...existing, ...seeded];
      });
      return next;
    });
  }, [intake?.schema, groups]);

  const hasWelcomeStep =
    Boolean(intake) && !isAccepted && !focusSingleNode && !forceSinglePage;
  const totalTypeformScreens =
    typeformScreens.length + (hasWelcomeStep ? 1 : 0);

  /**
   * Ask Casey is for the client filling the form in. It stands down for the
   * builder preview and the attorney's read-only view, and once the intake is
   * submitted or accepted there is nothing left for it to help with.
   */
  const isCaseyAvailable =
    Boolean(intake) &&
    Boolean(safeToken) &&
    !isPreview &&
    !isAttorneyReadOnly &&
    !isAwaitingReview &&
    !isAccepted;

  /**
   * The question on screen, so "what does this mean?" needs no antecedent. Only
   * question screens have one; on a lead-in or review screen there is nothing
   * specific to point at.
   */
  const caseyFocusQuestionId = useMemo(() => {
    const formStepIndex = hasWelcomeStep
      ? activeStepIndex - 1
      : activeStepIndex;
    if (formStepIndex < 0) return null;
    const screen = typeformScreens[formStepIndex];
    if (!screen) return null;
    if (screen.kind === "question" || screen.kind === "group-question") {
      return screen.node.node_id;
    }
    return null;
  }, [typeformScreens, activeStepIndex, hasWelcomeStep]);

  useEffect(() => {
    setActiveStepIndex((currentIndex) => {
      const lastIndex = Math.max(0, totalTypeformScreens - 1);
      return Math.min(currentIndex, lastIndex);
    });
  }, [totalTypeformScreens]);

  // When an entry is added/removed the screen list shifts; snap the active step
  // to the intended screen (identified by its stable key) once it re-renders.
  useEffect(() => {
    if (!pendingScreenKey) return;
    const idx = typeformScreens.findIndex((s) => s.key === pendingScreenKey);
    if (idx >= 0) {
      setActiveStepIndex(idx + (hasWelcomeStep ? 1 : 0));
    }
    setPendingScreenKey(null);
  }, [pendingScreenKey, typeformScreens, hasWelcomeStep]);

  // When a returning user chooses "Continue where you left off", resume on the
  // screen just past the furthest one they've answered. Using the furthest
  // answered screen (rather than the first gap) avoids trapping them on an
  // intentionally skipped optional question earlier in the flow.
  const resumeTargetStepIndex = useMemo(() => {
    const answerHasValue = (ans: OutgoingIntakeAnswer | null): boolean => {
      if (!ans) return false;
      if ((ans.files ?? []).length > 0) return true;
      return hasMeaningfulValue(ans.value);
    };

    let lastAnswered = -1;
    typeformScreens.forEach((screen, index) => {
      let answered = false;
      if (screen.kind === "question") {
        answered = answerHasValue(
          answersById[String(screen.node.node_id ?? "")] ?? null
        );
      } else if (screen.kind === "group-question") {
        const groupId = String(screen.group.node_id ?? "");
        const instance = (groups[groupId] ?? []).find(
          (x) => x.instanceId === screen.instanceId
        );
        answered = answerHasValue(
          instance?.answers?.[String(screen.node.node_id ?? "")] ?? null
        );
      } else if (screen.kind === "group-lead-in") {
        answered = Boolean(groupDecisions[String(screen.group.node_id ?? "")]);
      } else if (screen.kind === "group-review") {
        const groupId = String(screen.group.node_id ?? "");
        answered = (groups[groupId] ?? []).some((inst) =>
          Object.values(inst.answers ?? {}).some((a) => answerHasValue(a))
        );
      }
      if (answered) lastAnswered = index;
    });

    const lastIndex = Math.max(0, typeformScreens.length - 1);
    return Math.min(lastAnswered + 1, lastIndex);
  }, [typeformScreens, answersById, groups, groupDecisions]);

  // Preview-only: drive the active step from an externally selected node id so
  // the builder's left rail can focus the preview on a specific question. The
  // target may be a top-level step or a repeatable group; map it to the first
  // screen belonging to that step.
  useEffect(() => {
    if (!focusSingleNode) return;
    const target = String(activeNodeId ?? "").trim();
    if (!target) return;
    const stepIndex = findStepIndexForNode(intake?.schema ?? [], target);
    if (stepIndex < 0) return;
    const screenIndex = typeformScreens.findIndex(
      (screen) => screen.stepIndex === stepIndex
    );
    if (screenIndex < 0) return;
    const welcomeOffset = hasWelcomeStep ? 1 : 0;
    const nextIndex = screenIndex + welcomeOffset;
    if (nextIndex === activeStepIndexRef.current) return;
    // Animate toward the direction of travel. Without this an externally driven
    // move (TOC click, review auto-advance) keeps whatever direction was set
    // last, so advancing forward can play the backward transition.
    setStepTransitionDirection(
      nextIndex > activeStepIndexRef.current ? "forward" : "backward"
    );
    setActiveStepIndex(nextIndex);
  }, [
    activeNodeId,
    focusSingleNode,
    hasWelcomeStep,
    intake?.schema,
    typeformScreens
  ]);

  // Review-only: when a node is selected in the TOC, scroll it into view. In the
  // guided flow this only matters for repeatable-group entries (plain questions
  // become their own screen); in single-page every question/entry lives on one
  // page, so top-level questions are scrolled to as well.
  useEffect(() => {
    if (!focusSingleNode || !reviewMode) return undefined;
    const groupId = String(activeNodeId ?? "").trim();
    if (!groupId) return undefined;
    const instanceId = String(activeInstanceId ?? "").trim();
    const questionId = String(activeQuestionId ?? "").trim();
    const isSinglePage = layoutMode === "single_page";
    // A plain top-level node only needs scrolling in single-page mode.
    if (!instanceId && !isSinglePage) return undefined;
    const raf = window.requestAnimationFrame(() => {
      let targetId: string;
      let fallbackId: string | null = null;
      if (instanceId) {
        // Prefer the specific sub-question when targeted, else the entry card.
        targetId = questionId
          ? `review-entry-${groupId}-${instanceId}-q-${questionId}`
          : `review-entry-${groupId}-${instanceId}`;
        fallbackId = `review-entry-${groupId}-${instanceId}`;
      } else {
        targetId = `review-q-${groupId}`;
      }
      const el =
        document.getElementById(targetId) ||
        (fallbackId ? document.getElementById(fallbackId) : null);
      if (!el) return;

      let scrollParent = el.parentElement;
      while (
        scrollParent &&
        scrollParent !== document.body &&
        scrollParent !== document.documentElement
      ) {
        const style = window.getComputedStyle(scrollParent);
        const canScrollY =
          /(auto|scroll|overlay)/.test(style.overflowY) &&
          scrollParent.scrollHeight > scrollParent.clientHeight;
        if (canScrollY) break;
        scrollParent = scrollParent.parentElement;
      }

      if (
        scrollParent &&
        scrollParent !== document.body &&
        scrollParent !== document.documentElement
      ) {
        const parentRect = scrollParent.getBoundingClientRect();
        const targetRect = el.getBoundingClientRect();
        const nextTop =
          scrollParent.scrollTop + targetRect.top - parentRect.top - 24;
        scrollParent.scrollTo({
          top: Math.max(0, nextTop),
          behavior: "smooth"
        });
        return;
      }

      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
    return () => window.cancelAnimationFrame(raf);
  }, [
    activeInstanceId,
    activeNodeId,
    activeQuestionId,
    focusSingleNode,
    reviewMode,
    activeStepIndex,
    layoutMode
  ]);

  // Review mode: emit the schema node id of the active screen so an external
  // table of contents (the review screen's left rail) can follow caret nav.
  // Emits only when the screen changes: re-emitting on a new callback identity
  // would echo the previous node back while an external selection is still
  // being applied, snapping the parent back to it.
  const onActiveNodeChangeRef = useRef(onActiveNodeChange);
  onActiveNodeChangeRef.current = onActiveNodeChange;
  const lastEmittedNodeIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const emitActiveNode = onActiveNodeChangeRef.current;
    if (!reviewMode || !emitActiveNode) return;
    const idx = hasWelcomeStep ? activeStepIndex - 1 : activeStepIndex;
    const screen = idx >= 0 ? typeformScreens[idx] : null;
    let nodeId: string | null = null;
    if (screen) {
      if (screen.kind === "question") {
        nodeId = String(screen.node.node_id ?? "").trim() || null;
      } else if (
        screen.kind === "group-question" ||
        screen.kind === "group-lead-in" ||
        screen.kind === "group-add-another" ||
        screen.kind === "group-review" ||
        screen.kind === "group-stack"
      ) {
        nodeId = String(screen.group.node_id ?? "").trim() || null;
      }
    }
    if (lastEmittedNodeIdRef.current === nodeId) return;
    lastEmittedNodeIdRef.current = nodeId;
    emitActiveNode(nodeId);
  }, [reviewMode, activeStepIndex, hasWelcomeStep, typeformScreens]);

  const isActiveStepComplete = useMemo(() => {
    if (hasWelcomeStep && activeStepIndex === 0) return true;

    const activeFormStepIndex = hasWelcomeStep
      ? activeStepIndex - 1
      : activeStepIndex;
    const activeScreen = typeformScreens[activeFormStepIndex];
    if (!activeScreen) return false;

    const isAnswered = (ans: OutgoingIntakeAnswer | null): boolean => {
      if (!ans) return false;
      const files = ans.files ?? [];
      if (files.length > 0) return true;
      return hasMeaningfulValue(ans.value);
    };

    const getAnswerFor = (params: {
      questionId: string;
      groupId?: string | null;
      instanceId?: string | null;
    }): OutgoingIntakeAnswer | null => {
      const { questionId } = params;
      const groupId = params.groupId ?? null;
      const instanceId = params.instanceId ?? null;
      if (groupId && instanceId) {
        const inst = (groups[groupId] ?? []).find(
          (x) => x.instanceId === instanceId
        );
        return inst?.answers?.[questionId] ?? null;
      }
      return answersById[questionId] ?? null;
    };

    // "Add another?" is a branch choice with a safe default (continue), and the
    // lead-in only needs an explicit yes/no before advancing.
    if (activeScreen.kind === "group-add-another") return true;
    if (activeScreen.kind === "group-review") return true;
    if (activeScreen.kind === "final-review") return true;
    if (activeScreen.kind === "group-stack") return true;
    if (activeScreen.kind === "group-lead-in") {
      return Boolean(groupDecisions[String(activeScreen.group.node_id ?? "")]);
    }

    if (isRevisionRequested) {
      const ownedKeys = Array.from(rejectedByKey.keys()).filter((key) => {
        const [questionId, groupIdRaw, instanceIdRaw] = key.split("|");
        if (activeScreen.kind === "group-question") {
          return (
            questionId === String(activeScreen.node.node_id ?? "") &&
            (String(groupIdRaw ?? "").trim() || null) ===
              String(activeScreen.group.node_id ?? "") &&
            (String(instanceIdRaw ?? "").trim() || null) ===
              activeScreen.instanceId
          );
        }
        return flatQuestions([activeScreen.node]).some(
          (question) => String(question.node_id ?? "") === questionId
        );
      });

      if (ownedKeys.length === 0) return true;

      // A flagged answer only has to come back with a value; standing by the
      // original answer is allowed.
      return ownedKeys.every((key) => resolvedRevisionKeys.has(key));
    }

    if (activeScreen.kind === "group-question") {
      const { node } = activeScreen;
      if (!node.required) return true;
      return isAnswered(
        getAnswerFor({
          questionId: node.node_id,
          groupId: String(activeScreen.group.node_id ?? ""),
          instanceId: activeScreen.instanceId
        })
      );
    }

    const walk = (
      n: OutgoingIntakeFormNode,
      ctx?: { groupId?: string | null; instanceId?: string | null }
    ): boolean => {
      if (n.node_type === "question") {
        if (!n.required) return true;
        return isAnswered(
          getAnswerFor({
            questionId: n.node_id,
            groupId: ctx?.groupId ?? null,
            instanceId: ctx?.instanceId ?? null
          })
        );
      }

      if (n.node_type === "repeatable_group") {
        const groupId = n.node_id;
        const requiredQuestions = flatQuestions(n.children ?? []).filter(
          (question) => question.required
        );
        if (requiredQuestions.length === 0) return true;
        const instances = groups[groupId] ?? [];
        if (instances.length === 0) return false;
        return instances.every((instance) =>
          (n.children ?? []).every((child) =>
            walk(child, { groupId, instanceId: instance.instanceId })
          )
        );
      }

      return (n.children ?? []).every((child) => walk(child, ctx));
    };

    return walk(activeScreen.node);
  }, [
    activeStepIndex,
    answersById,
    groups,
    groupDecisions,
    hasWelcomeStep,
    isRevisionRequested,
    rejectedByKey,
    resolvedRevisionKeys,
    typeformScreens
  ]);

  const isActiveStepFullyAnswered = useMemo(() => {
    if (hasWelcomeStep && activeStepIndex === 0) return true;

    const activeFormStepIndex = hasWelcomeStep
      ? activeStepIndex - 1
      : activeStepIndex;
    const activeScreen = typeformScreens[activeFormStepIndex];
    if (!activeScreen) return true;

    const isAnswered = (ans: OutgoingIntakeAnswer | null): boolean => {
      if (!ans) return false;
      const files = ans.files ?? [];
      if (files.length > 0) return true;
      return hasMeaningfulValue(ans.value);
    };

    const getAnswerFor = (params: {
      questionId: string;
      groupId?: string | null;
      instanceId?: string | null;
    }): OutgoingIntakeAnswer | null => {
      const { questionId } = params;
      const groupId = params.groupId ?? null;
      const instanceId = params.instanceId ?? null;
      if (groupId && instanceId) {
        const inst = (groups[groupId] ?? []).find(
          (x) => x.instanceId === instanceId
        );
        return inst?.answers?.[questionId] ?? null;
      }
      return answersById[questionId] ?? null;
    };

    // Decision screens are never "skippable": treat them as fully answered so
    // the primary action reads "OK" rather than "Skip".
    if (activeScreen.kind === "group-add-another") return true;
    if (activeScreen.kind === "group-review") return true;
    if (activeScreen.kind === "final-review") return true;
    if (activeScreen.kind === "group-stack") return true;
    if (activeScreen.kind === "group-lead-in") {
      return Boolean(groupDecisions[String(activeScreen.group.node_id ?? "")]);
    }

    if (activeScreen.kind === "group-question") {
      return isAnswered(
        getAnswerFor({
          questionId: activeScreen.node.node_id,
          groupId: String(activeScreen.group.node_id ?? ""),
          instanceId: activeScreen.instanceId
        })
      );
    }

    const walk = (
      n: OutgoingIntakeFormNode,
      ctx?: { groupId?: string | null; instanceId?: string | null }
    ): boolean => {
      if (n.node_type === "question") {
        return isAnswered(
          getAnswerFor({
            questionId: n.node_id,
            groupId: ctx?.groupId ?? null,
            instanceId: ctx?.instanceId ?? null
          })
        );
      }

      if (n.node_type === "repeatable_group") {
        const groupId = n.node_id;
        const questions = flatQuestions(n.children ?? []);
        if (questions.length === 0) return true;
        const instances = groups[groupId] ?? [];
        if (instances.length === 0) return false;
        return instances.every((instance) =>
          (n.children ?? []).every((child) =>
            walk(child, { groupId, instanceId: instance.instanceId })
          )
        );
      }

      return (n.children ?? []).every((child) => walk(child, ctx));
    };

    return walk(activeScreen.node);
  }, [
    activeStepIndex,
    answersById,
    groups,
    groupDecisions,
    hasWelcomeStep,
    typeformScreens
  ]);

  const isLastTypeformStep =
    activeStepIndex >= Math.max(0, totalTypeformScreens - 1);

  const goToPreviousTypeformStep = useCallback(() => {
    autosaveProgress();
    setStepTransitionDirection("backward");
    setActiveStepIndex((currentIndex) => Math.max(0, currentIndex - 1));
  }, [autosaveProgress]);

  const goToNextTypeformStep = useCallback(() => {
    const activeFormStepIndex = hasWelcomeStep
      ? activeStepIndex - 1
      : activeStepIndex;
    const activeScreen =
      activeFormStepIndex >= 0 ? typeformScreens[activeFormStepIndex] : null;

    const welcomeOffset = hasWelcomeStep ? 1 : 0;

    // When a plain forward move would enter a repeatable group that was already
    // started (in a prior session or earlier navigation), jump straight to that
    // group's review screen instead of walking each filled entry screen-by-screen.
    const computeForwardActiveStepIndex = () => {
      const fromFormIdx = activeStepIndex - welcomeOffset; // -1 when on welcome
      const fromScreen = fromFormIdx >= 0 ? typeformScreens[fromFormIdx] : null;
      const candidate = typeformScreens[fromFormIdx + 1];

      if (
        candidate &&
        (candidate.kind === "group-lead-in" ||
          candidate.kind === "group-question")
      ) {
        // Only when crossing INTO the group from outside (not while building it).
        const enteringNewStep =
          !fromScreen || fromScreen.stepIndex !== candidate.stepIndex;
        if (enteringNewStep) {
          const groupId = String(candidate.group.node_id ?? "");
          const started = (groups[groupId] ?? []).some((inst) =>
            Object.values(inst.answers ?? {}).some((a) => {
              const files = a?.files ?? [];
              return files.length > 0 || hasMeaningfulValue(a?.value);
            })
          );
          if (started) {
            const reviewIdx = typeformScreens.findIndex(
              (s) =>
                s.kind === "group-review" &&
                String(s.group.node_id ?? "") === groupId
            );
            if (reviewIdx >= 0)
              return Math.min(
                totalTypeformScreens - 1,
                reviewIdx + welcomeOffset
              );
          }
        }
      }
      return Math.min(totalTypeformScreens - 1, activeStepIndex + 1);
    };

    const advanceForward = () => {
      autosaveProgress();
      setStepTransitionDirection("forward");
      if (pendingReviewReturnKey) {
        const returnIdx = typeformScreens.findIndex(
          (s) => s.key === pendingReviewReturnKey
        );
        setPendingReviewReturnKey(null);
        if (returnIdx >= 0) {
          setActiveStepIndex(returnIdx + welcomeOffset);
          return;
        }
      }
      setActiveStepIndex(computeForwardActiveStepIndex());
    };

    const proceedOrSubmit = () => {
      if (isLastTypeformStep) {
        if (isPreview || isPersistenceDisabled) return;
        if (isFormComplete && !isSubmitting) {
          submit().catch(() => undefined);
        }
        return;
      }
      advanceForward();
    };

    // Repeatable-group lead-in ("Do you have any … to add?").
    if (activeScreen?.kind === "group-lead-in") {
      const groupId = String(activeScreen.group.node_id ?? "");
      const decision =
        groupDecisions[groupId] ?? (isPreview ? "yes" : undefined);
      if (!decision) return;
      if (decision === "no") {
        proceedOrSubmit();
        return;
      }
      // "Yes": ensure a first entry exists, then advance into its questions.
      // The new entry's screens are inserted right after this lead-in, so
      // currentIndex + 1 lands on the entry's first sub-question.
      setGroups((prev) => {
        const existing = prev[groupId] ?? [];
        if (existing.length > 0) return prev;
        return {
          ...prev,
          [groupId]: [{ instanceId: createGroupInstanceId(), answers: {} }]
        };
      });
      if (groupDecisions[groupId] !== "yes") {
        setGroupDecisions((prev) => ({ ...prev, [groupId]: "yes" }));
      }
      autosaveProgress();
      setStepTransitionDirection("forward");
      setActiveStepIndex((currentIndex) => currentIndex + 1);
      return;
    }

    // Repeatable-group "Add another?" decision.
    if (activeScreen?.kind === "group-add-another") {
      const groupId = String(activeScreen.group.node_id ?? "");
      const choice = addAnotherChoice[groupId] ?? "no";
      setAddAnotherChoice((prev) => {
        const next = { ...prev };
        delete next[groupId];
        return next;
      });
      if (choice === "no") {
        proceedOrSubmit();
        return;
      }
      // "Yes": append a new entry. Its questions are inserted at this screen's
      // current index (pushing the add-another screen later), so we keep the
      // same index to land on the new entry's first sub-question.
      setGroups((prev) => ({
        ...prev,
        [groupId]: [
          ...(prev[groupId] ?? []),
          { instanceId: createGroupInstanceId(), answers: {} }
        ]
      }));
      autosaveProgress();
      setStepTransitionDirection("forward");
      setActiveStepIndex((currentIndex) => currentIndex);
      return;
    }

    // Repeatable-group review screen: "Continue" advances to the next step.
    if (activeScreen?.kind === "group-review") {
      proceedOrSubmit();
      return;
    }

    // Normal question / section screen.
    if (isLastTypeformStep) {
      if (isPreview || isPersistenceDisabled) return;
      if (isFormComplete && !isSubmitting) {
        submit().catch(() => undefined);
      }
      return;
    }

    if (!isPreview && !isActiveStepComplete) return;
    advanceForward();
  }, [
    activeStepIndex,
    addAnotherChoice,
    groupDecisions,
    groups,
    hasWelcomeStep,
    isActiveStepComplete,
    autosaveProgress,
    isFormComplete,
    isLastTypeformStep,
    isPersistenceDisabled,
    isPreview,
    isSubmitting,
    pendingReviewReturnKey,
    submit,
    totalTypeformScreens,
    typeformScreens
  ]);

  useEffect(() => {
    if (!advanceAfterPrefillRef.current || !isActiveStepComplete) return;
    advanceAfterPrefillRef.current = false;
    goToNextTypeformStep();
  }, [goToNextTypeformStep, isActiveStepComplete]);

  const handleTypeformKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      // Single-page mode has no step-by-step navigation, so Enter must not
      // advance a hidden step or trigger submit.
      if (layoutMode === "single_page") return;
      if (event.key !== "Enter" || event.shiftKey) return;
      if (event.target instanceof HTMLTextAreaElement) return;

      event.preventDefault();
      goToNextTypeformStep();
    },
    [goToNextTypeformStep, layoutMode]
  );

  if (!safeToken) {
    return (
      <div className="p-6">
        <div className="text-body font-body text-default-font">
          Missing token.
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className={[
          isPreview ? "h-full" : "h-screen",
          "w-full flex items-center justify-center bg-default-background"
        ].join(" ")}
      >
        <LoadingState size="xl" />
      </div>
    );
  }

  if (linkGone) return <LinkGoneState link={linkGone} />;

  if (errorMessage) {
    return (
      <ErrorState
        minH={isPreview ? "100%" : "100vh"}
        title={tr("oops")}
        message={tr("persists")}
        buttonCta={tr("tryAgain")}
        onRetry={() => {
          load().catch(() => undefined);
        }}
      />
    );
  }

  if (!intake) return null;

  return (() => {
    const lawFirmName = normalizeText(intake.law_firm_name) || tr("yourFirm");
    const lawFirmLogoUrl = normalizeText(intake.law_firm_logo_url);
    // The per-intake branding toggle is the source of truth when it's been set
    // (true/false). When it has not been set yet, default branding on if the
    // law firm has an uploaded logo.
    const showWhitelabelLogo =
      effectiveFirmBrandingEnabled && lawFirmLogoUrl.length > 0;
    const showResolvedFirmLogo =
      showWhitelabelLogo && !isBrandBackgroundResolving;
    const firstName = normalizeText(intake.foreign_national_first_name);

    const requestedKeys = isRevisionRequested
      ? Array.from(rejectedByKey.keys())
      : [];

    const isAnswered = (ans: OutgoingIntakeAnswer | null): boolean => {
      if (!ans) return false;
      const hasFiles = (ans.files ?? []).length > 0;
      const hasValue = hasMeaningfulValue(ans.value);
      return hasFiles || hasValue;
    };

    const totalSteps = totalTypeformScreens || 1;
    const isWelcomeStep = hasWelcomeStep && activeStepIndex === 0;
    const welcomeVideoUrl = normalizeText(intake.welcome_video_url) || "";
    const hasWelcomeVideo = isWelcomeStep && Boolean(welcomeVideoUrl);
    // Single-page form mode: render the whole intake on one scrollable page
    // instead of the screen-by-screen flow. Kept off the welcome screen and
    // terminal states; revision is supported (green/red tint + per-question
    // comment threads mirror the guided flow).
    // The awaiting-review and accepted exclusions exist so a client isn't shown
    // an editable-looking form after they've handed it over; a forced read-only
    // record has no such risk and needs the whole form on one page.
    const showSinglePageForm =
      layoutMode === "single_page" &&
      !isWelcomeStep &&
      (forceSinglePage || (!isAwaitingReview && !isAccepted));
    const activeFormStepIndex = hasWelcomeStep
      ? activeStepIndex - 1
      : activeStepIndex;
    const activeScreen =
      activeFormStepIndex >= 0 ? typeformScreens[activeFormStepIndex] : null;
    // Narrowed views of the active screen so property access is type-safe.
    const leadInScreen =
      activeScreen?.kind === "group-lead-in" ? activeScreen : null;
    const addAnotherScreen =
      activeScreen?.kind === "group-add-another" ? activeScreen : null;
    const groupQuestionScreen =
      activeScreen?.kind === "group-question" ? activeScreen : null;
    const reviewScreen =
      activeScreen?.kind === "group-review" ? activeScreen : null;
    const groupStackScreen =
      activeScreen?.kind === "group-stack" ? activeScreen : null;
    const finalReviewScreen =
      activeScreen?.kind === "final-review" ? activeScreen : null;
    // On the final review screen, a selected file opens an in-app preview in a
    // right-side panel that shifts the review content left to make room.
    const isReviewFilePreviewOpen =
      Boolean(finalReviewScreen) && Boolean(previewFile);
    // The builder preview's own top bar already carries the logo, so the
    // overlay and the inline logo both stand down when it is showing.
    const isPreviewTopBarShown =
      !hideTopBar && mode === "preview" && !isWelcomeStep;
    // Single-page mode is one long scrolling column, so the logo sits at the top
    // of that column and scrolls away with the content instead of staying
    // pinned over it.
    const isLogoInlineWithContent =
      showWhitelabelLogo && showSinglePageForm && !isPreviewTopBarShown;
    // The firm logo renders as a top-left overlay (not a top bar) for the live
    // client view. At narrower widths the centered content shifts left beneath
    // the logo, so any top-aligned content (steps, single-page, review) needs
    // top clearance below it until the viewport is wide enough to clear it.
    const isLogoOverlayShown =
      showWhitelabelLogo &&
      (hideTopBar || mode !== "preview") &&
      !isLogoInlineWithContent;

    // Shared card used by both the desktop right-side preview panel and the
    // small-screen inline preview (rendered directly under the clicked tile).
    const renderReviewFilePreviewCard = (options?: {
      showHeader?: boolean;
    }) => {
      if (!previewFile) return null;
      const showHeader = options?.showHeader ?? true;
      return (
        <div className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-xl border border-solid border-neutral-border bg-fill-1 shadow-[0_18px_50px_rgba(0,0,0,0.28)]">
          {showHeader ? (
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-solid border-neutral-border bg-default-background px-3 py-2">
              <span className="min-w-0 truncate text-caption-bold font-caption-bold text-default-font">
                {previewFile.filename}
              </span>
              <button
                type="button"
                aria-label="Close preview"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-fg-2 transition hover:bg-fill-1 active:bg-fill-1"
                onClick={closeQuickPreviewOverlay}
              >
                <IntakeIcon name="FeatherX" />
              </button>
            </div>
          ) : null}
          <div className="relative min-h-0 flex-1 overflow-hidden">
            {quickPreviewBlobUrl ? (
              <PdfPreview
                file={quickPreviewBlobUrl}
                ariaLabel={`${previewFile.filename} preview`}
              />
            ) : (
              <div className="flex h-full min-h-0 flex-col items-center justify-center gap-2 px-5 py-6 text-center">
                {isQuickPreviewLoading ||
                (!quickPreviewBlobUrl && !quickPreviewError) ? (
                  <LoadingState text="Loading preview..." />
                ) : (
                  <span className="text-body font-body text-subtext-color">
                    {quickPreviewError ||
                      getIntakeFilePreviewUnavailableMessage(previewFile)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      );
    };
    // Underlying question node for question / group-question screens (used for
    // file-upload and formatting logic). Decision screens have no node.
    const activeStepNode =
      activeScreen &&
      (activeScreen.kind === "question" ||
        activeScreen.kind === "group-question")
        ? activeScreen.node
        : null;
    const isDecisionScreen = Boolean(leadInScreen || addAnotherScreen);
    // Attorney review context for the active answerable screen. The decision
    // key matches the review screen's per-question decisions/comments.
    const activeReviewKey =
      reviewMode && activeStepNode
        ? decisionKey({
            question_id: String(activeStepNode.node_id ?? ""),
            repeatable_group_id: groupQuestionScreen
              ? String(groupQuestionScreen.group.node_id ?? "")
              : null,
            repeatable_group_instance_id: groupQuestionScreen
              ? groupQuestionScreen.instanceId
              : null
          })
        : null;
    const activeReviewStatus = activeReviewKey
      ? reviewDecisionByKey?.[activeReviewKey] ?? "pending"
      : "pending";
    const activeReviewThread = activeReviewKey
      ? reviewCommentsByKey?.[activeReviewKey] ?? []
      : [];
    const activeReviewNote = activeReviewKey
      ? reviewNoteByKey?.[activeReviewKey] ?? ""
      : "";
    // Reviewing attorney identity for the Request Changes compose row.
    const reviewAttorneyName =
      normalizeText(intake?.attorney_name) || "Reviewer";
    const reviewAttorneyAvatarUrl = intake?.attorney_avatar_url ?? undefined;
    const reviewAttorneyInitials =
      reviewAttorneyName
        .split(/\s+/)
        .filter(Boolean)
        .map((p) => p[0]?.toUpperCase())
        .join("")
        .slice(0, 2) || "AT";
    // Foreign national's optional per-question comment on the active answer.
    const activeCommentGroupId = groupQuestionScreen
      ? String(groupQuestionScreen.group.node_id ?? "")
      : null;
    const activeCommentInstanceId = groupQuestionScreen
      ? groupQuestionScreen.instanceId
      : null;
    const activeCommentQuestionId = activeStepNode
      ? String(activeStepNode.node_id ?? "")
      : null;
    // Keyed comment helpers so the thread + composer can render for any
    // question (the guided active question and every single-page question),
    // not just the active one.
    const readAnswerCommentForKey = (
      questionId: string,
      groupId: string | null,
      instanceId: string | null
    ): string => {
      if (!questionId) return "";
      if (groupId && instanceId) {
        const inst = (groups[groupId] ?? []).find(
          (x) => x.instanceId === instanceId
        );
        return normalizeText(inst?.answers[questionId]?.comment);
      }
      return normalizeText(answersById[questionId]?.comment);
    };
    const setAnswerCommentForKey = (
      questionId: string,
      groupId: string | null,
      instanceId: string | null,
      next: string
    ) => {
      if (!questionId) return;
      const qid = questionId;
      if (groupId && instanceId) {
        setGroups((prev) => {
          const out = { ...prev };
          const items = [...(out[groupId] ?? [])];
          const idx = items.findIndex((x) => x.instanceId === instanceId);
          if (idx < 0) return prev;
          const inst = items[idx];
          const existing = inst.answers[qid] ?? {
            question_id: qid,
            repeatable_group_id: groupId,
            repeatable_group_instance_id: instanceId,
            value: null,
            files: []
          };
          items[idx] = {
            ...inst,
            answers: { ...inst.answers, [qid]: { ...existing, comment: next } }
          };
          out[groupId] = items;
          return out;
        });
        return;
      }
      setAnswersById((prev) => {
        const existing = prev[qid] ?? {
          question_id: qid,
          value: null,
          files: []
        };
        return { ...prev, [qid]: { ...existing, comment: next } };
      });
    };
    const openClientComposerFor = (key: string, current: string) => {
      setClientCommentDrafts((prev) => ({ ...prev, [key]: current }));
      setCommentComposerOpenKey(key);
    };
    const closeClientComposerFor = (key: string) => {
      setClientCommentDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setCommentComposerOpenKey((prev) => (prev === key ? null : prev));
    };
    const postClientCommentFor = (
      key: string,
      questionId: string,
      groupId: string | null,
      instanceId: string | null,
      value: string
    ) => {
      setAnswerCommentForKey(questionId, groupId, instanceId, value.trim());
      closeClientComposerFor(key);
    };
    const showClientCommentField =
      !reviewMode &&
      !isPreview &&
      !isAccepted &&
      !isAwaitingReview &&
      Boolean(activeStepNode) &&
      (activeScreen?.kind === "question" ||
        activeScreen?.kind === "group-question");
    // Prior comments (client + attorney) for the active question, shown as a
    // thread above the composer whenever any exist.
    const activeCommentKey = activeCommentQuestionId
      ? decisionKey({
          question_id: activeCommentQuestionId,
          repeatable_group_id: activeCommentGroupId,
          repeatable_group_instance_id: activeCommentInstanceId
        })
      : null;
    // Client identity for the compose card (mirrors the attorney compose card).
    const clientCommentName =
      [
        normalizeText(intake?.foreign_national_first_name),
        normalizeText(intake?.foreign_national_last_name)
      ]
        .filter(Boolean)
        .join(" ") || tr("you");
    const clientCommentAvatarUrl =
      intake?.foreign_national_avatar_url ?? undefined;
    const clientCommentInitials =
      clientCommentName
        .split(/\s+/)
        .filter(Boolean)
        .map((p) => p[0]?.toUpperCase())
        .join("")
        .slice(0, 2) || "CL";
    // Per-question comment thread + composer, keyed so it works for the guided
    // active question and every single-page question. On a flagged (rejected)
    // revision answer the newest attorney note also carries a struck-through
    // "You previously submitted:" row so the FN sees exactly what to change.
    const commentFirmName = normalizeText(intake?.law_firm_name);
    const renderClientCommentBlock = ({
      key,
      questionNode,
      groupId,
      instanceId,
      wrapperClassName
    }: {
      key: string;
      questionNode: OutgoingIntakeFormNode;
      groupId: string | null;
      instanceId: string | null;
      wrapperClassName?: string;
    }): React.ReactNode => {
      const questionId = String(questionNode.node_id ?? "");
      const thread = clientThreadByKey[key] ?? [];
      const committedComment = readAnswerCommentForKey(
        questionId,
        groupId,
        instanceId
      );
      const composerOpen =
        !isAttorneyReadOnly && commentComposerOpenKey === key;
      const draftValue = clientCommentDrafts[key] ?? committedComment;
      // The previously submitted value belongs on the newest attorney note for
      // a flagged answer.
      let lastAttorneyIdx = -1;
      thread.forEach((it, i) => {
        if (it.author === "attorney") lastAttorneyIdx = i;
      });
      // What she sent before, so a flagged answer never looks wiped: "You
      // answered: …", or for an upload "You sent ~~file.pdf~~ · Blank page".
      const baseline =
        isRevisionRequested && rejectedByKey.has(key)
          ? baselineAnswerByKeyRef.current.get(key) ?? null
          : null;
      const priorFiles = baseline?.files ?? [];
      const showSubmittedValue =
        Boolean(baseline) &&
        (priorFiles.length > 0 || hasMeaningfulValue(baseline?.value));
      const priorReason = attentionReason(baseline);
      const submittedLabel = !showSubmittedValue
        ? ""
        : priorFiles.length
        ? priorFiles.map((f) => f.filename).join(", ")
        : formatAnswerValueLabel(questionNode, baseline);
      const priorAnswerRow = (bordered: boolean) => (
        <div
          className={[
            "flex w-full flex-wrap items-baseline gap-2",
            bordered
              ? "mt-1.5 border-t border-solid border-slate-900/10 pt-1.5"
              : ""
          ].join(" ")}
        >
          <span className="intake-comment-meta text-caption font-caption">
            {priorFiles.length ? tr("youSent") : tr("youAnswered")}
          </span>
          <span className="intake-comment-submitted-value text-caption-bold font-caption-bold line-through">
            {submittedLabel}
          </span>
          {priorReason ? (
            <span className="intake-comment-meta text-caption font-caption">
              · {priorReason}
            </span>
          ) : null}
        </div>
      );
      const standalonePrior = showSubmittedValue && lastAttorneyIdx < 0;
      const wrapper = wrapperClassName ?? "mt-4 flex w-full flex-col gap-3";
      // Nothing to show and nothing to add: skip the wrapper so read-only
      // records don't get blank gaps under every unanswered question.
      if (isAttorneyReadOnly && thread.length === 0 && !committedComment) {
        return null;
      }
      return (
        <div className={wrapper}>
          {thread.length > 0 ||
          committedComment ||
          composerOpen ||
          standalonePrior ? (
            <div className="public-intake-review-comments flex w-full flex-col gap-2">
              {standalonePrior ? (
                <div className="intake-comment-card flex w-full items-start rounded-2xl px-4 py-3">
                  {priorAnswerRow(false)}
                </div>
              ) : null}
              {thread.map((item, idx) => {
                const isAttorney = item.author === "attorney";
                const initials =
                  item.name
                    .split(/\s+/)
                    .filter(Boolean)
                    .map((p) => p[0]?.toUpperCase())
                    .join("")
                    .slice(0, 2) || (isAttorney ? "AT" : "CL");
                return (
                  <div
                    key={`${item.author}-${idx}`}
                    className="intake-comment-card flex w-full items-start gap-2.5 rounded-2xl px-4 py-3.5"
                  >
                    <IntakeAvatar
                      size="xs"
                      image={item.avatarUrl ?? undefined}
                      variant={isAttorney ? "error" : "neutral"}
                    >
                      {initials}
                    </IntakeAvatar>
                    <div className="flex min-w-0 grow shrink-0 basis-0 flex-col items-start gap-1">
                      <div className="flex w-full min-w-0 items-baseline justify-between gap-3">
                        <div className="flex min-w-0 flex-wrap items-baseline gap-2">
                          <span className="intake-comment-name text-caption-bold font-caption-bold truncate">
                            {item.name}
                          </span>
                          {isAttorney && commentFirmName ? (
                            <span className="intake-comment-meta text-caption font-caption truncate">
                              {commentFirmName}
                            </span>
                          ) : null}
                        </div>
                        {item.timeLabel ? (
                          <span className="intake-comment-meta text-caption font-caption shrink-0 text-right">
                            {item.timeLabel}
                          </span>
                        ) : null}
                      </div>
                      <span
                        className={[
                          "text-caption font-caption whitespace-pre-wrap wrap-break-word",
                          isAttorney
                            ? "intake-comment-body-attorney"
                            : "intake-comment-body-client"
                        ].join(" ")}
                      >
                        {item.text}
                      </span>
                      {isAttorney &&
                      idx === lastAttorneyIdx &&
                      showSubmittedValue
                        ? priorAnswerRow(true)
                        : null}
                    </div>
                  </div>
                );
              })}
              {committedComment && !composerOpen ? (
                <div className="intake-comment-card flex w-full items-start gap-2.5 rounded-2xl px-4 py-3.5">
                  <IntakeAvatar
                    size="xs"
                    image={clientCommentAvatarUrl}
                    variant="neutral"
                  >
                    {clientCommentInitials}
                  </IntakeAvatar>
                  <div className="flex min-w-0 grow shrink-0 basis-0 flex-col items-start gap-1.5">
                    <span className="intake-comment-name text-caption-bold font-caption-bold truncate">
                      {clientCommentName}
                    </span>
                    <span className="intake-comment-body-client text-caption font-caption whitespace-pre-wrap wrap-break-word">
                      {committedComment}
                    </span>
                  </div>
                  {isAttorneyReadOnly ? null : (
                    <button
                      type="button"
                      onClick={() =>
                        openClientComposerFor(key, committedComment)
                      }
                      className="intake-comment-edit-btn inline-flex shrink-0 items-center gap-1 rounded-md border border-solid px-2.5 py-1.5 text-sm font-semibold leading-none transition"
                    >
                      <IntakeIcon name="FeatherPencil" className="text-base" />
                      Edit
                    </button>
                  )}
                </div>
              ) : null}
              {composerOpen ? (
                <div className="intake-comment-card flex w-full items-start gap-2.5 rounded-2xl px-4 py-3.5">
                  <IntakeAvatar
                    size="xs"
                    image={clientCommentAvatarUrl}
                    variant="neutral"
                  >
                    {clientCommentInitials}
                  </IntakeAvatar>
                  <div className="flex min-w-0 grow shrink-0 basis-0 flex-col items-start gap-1.5">
                    <span className="intake-comment-name text-caption-bold font-caption-bold truncate">
                      {clientCommentName}
                    </span>
                    <textarea
                      className="intake-comment-textarea w-full resize-y"
                      placeholder={tr("leaveComment")}
                      value={draftValue}
                      onChange={(e) => {
                        const val = e.target.value;
                        setClientCommentDrafts((prev) => ({
                          ...prev,
                          [key]: val
                        }));
                      }}
                    />
                    <div className="mt-1 flex w-full items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => closeClientComposerFor(key)}
                        className="intake-comment-cancel-btn inline-flex items-center rounded-md px-4 py-2.5 text-md font-medium leading-none transition hover:bg-fill-2"
                      >
                        {tr("cancel")}
                      </button>
                      <button
                        type="button"
                        disabled={!draftValue.trim()}
                        onClick={() =>
                          postClientCommentFor(
                            key,
                            questionId,
                            groupId,
                            instanceId,
                            draftValue
                          )
                        }
                        className="inline-flex items-center gap-1.5 rounded-md bg-brand-primary px-4 py-2.5 text-md font-semibold leading-none text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-[#b6bcc6] disabled:hover:bg-[#b6bcc6]"
                      >
                        Post
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {!composerOpen && !committedComment && !isAttorneyReadOnly ? (
            <button
              type="button"
              onClick={() => openClientComposerFor(key, committedComment)}
              className="inline-flex items-center gap-2 self-start rounded-lg px-3 py-2 text-base font-medium leading-none transition hover:opacity-80"
              style={{
                border: `1px solid rgba(${intakeFgRgb}, 0.25)`,
                background: `rgba(${intakeFgRgb}, 0.06)`,
                color: `rgba(${intakeFgRgb}, 0.82)`
              }}
            >
              <IntakeIcon name="FeatherMessageSquare" className="text-md" />
              {thread.length > 0 ? tr("addReply") : tr("addComment")}
            </button>
          ) : null}
        </div>
      );
    };
    // File uploads are only special-cased for top-level question steps.
    const isActiveDocumentRequestStep =
      !isWelcomeStep &&
      activeScreen?.kind === "question" &&
      activeStepNode?.node_type === "question" &&
      activeStepNode.question_type === "file";
    const activeStepBaseTitle = isWelcomeStep
      ? normalizeText(intake.title) || tr("defaultTitle")
      : finalReviewScreen
      ? tr("review")
      : leadInScreen
      ? groupLeadInPrompt(leadInScreen.group)
      : addAnotherScreen
      ? groupAddAnotherPrompt(addAnotherScreen.group)
      : reviewScreen
      ? groupReviewPrompt(reviewScreen.group)
      : groupStackScreen
      ? groupQuestionTitle(groupStackScreen.group)
      : normalizeText(activeStepNode?.title) ||
        normalizeText(activeStepNode?.prompt) ||
        `Step ${activeStepIndex + 1}`;
    // Contextualize a group sub-question using the entry's existing answers,
    // e.g. "When did you start living at 123 Main St?".
    const groupQuestionEntryAnswers = groupQuestionScreen
      ? (groups[String(groupQuestionScreen.group.node_id ?? "")] ?? []).find(
          (x) => x.instanceId === groupQuestionScreen.instanceId
        )?.answers ?? {}
      : {};
    const contextualTitle = groupQuestionScreen
      ? contextualGroupQuestionTitle(
          groupQuestionScreen.group,
          groupQuestionScreen.node,
          groupQuestionEntryAnswers
        )
      : null;
    const activeStepTitle = isWelcomeStep
      ? activeStepBaseTitle
      : isDecisionScreen
      ? activeStepBaseTitle
      : isCustomQuestionNode(activeStepNode)
      ? activeStepBaseTitle
      : contextualTitle ||
        (isActiveDocumentRequestStep
          ? formatDocumentRequestTitle(activeStepBaseTitle)
          : activeStepNode?.node_type === "question"
          ? formatInformationRequestTitle(activeStepBaseTitle)
          : activeStepBaseTitle);
    // Eyebrow above the title identifies which repeatable-group entry this is.
    // Prefer the entry's own context (e.g. its street address) once we have it,
    // falling back to a numbered label like "Address 1".
    const entryEyebrow = groupQuestionScreen
      ? groupEntryContextLabel(
          groupQuestionScreen.group,
          groupQuestionEntryAnswers
        ) ||
        `${groupEntryLabel(groupQuestionScreen.group)} ${
          groupQuestionScreen.entryIndex + 1
        }`
      : "";
    const fixingTotal =
      isRevisionRequested && !reviewMode && rejectedByKey.size > 0
        ? typeformScreens.filter((s) => s.kind !== "final-review").length
        : 0;
    const fixingIndex = activeStepIndex - (hasWelcomeStep ? 1 : 0);
    const activeStepEyebrow =
      fixingTotal && fixingIndex >= 0 && fixingIndex < fixingTotal
        ? [`Fixing ${fixingIndex + 1} of ${fixingTotal}`, entryEyebrow]
            .filter(Boolean)
            .join(" · ")
        : entryEyebrow;
    const activeStepDescription = finalReviewScreen
      ? tr("reviewBody")
      : reviewScreen
      ? tr("reviewGroupBody")
      : !isWelcomeStep &&
        !isDecisionScreen &&
        activeStepNode?.node_type === "question"
      ? informationRequestDescription(
          activeStepNode,
          Boolean(groupQuestionScreen)
        )
      : "";
    const activeDocumentUploadKey =
      isActiveDocumentRequestStep && activeStepNode
        ? `file|${activeStepNode.node_id}||`
        : null;
    const activeDocumentFiles =
      activeDocumentUploadKey && activeStepNode?.node_type === "question"
        ? answersById[activeStepNode.node_id]?.files ?? []
        : [];
    const sortedActiveDocumentFiles = [...activeDocumentFiles].sort((a, b) => {
      if (uploadedDocumentSort.sortBy === "filename") {
        const cmp = String(a.filename || "").localeCompare(
          String(b.filename || ""),
          undefined,
          { sensitivity: "base" }
        );
        return uploadedDocumentSort.sortDir === "asc" ? cmp : -cmp;
      }
      const aTimestamp = Math.max(
        getOutgoingIntakeFileUploadTimestamp(a),
        intakeFileInsertedAtById[a.file_id] ?? 0
      );
      const bTimestamp = Math.max(
        getOutgoingIntakeFileUploadTimestamp(b),
        intakeFileInsertedAtById[b.file_id] ?? 0
      );
      const cmp = aTimestamp - bTimestamp;
      return uploadedDocumentSort.sortDir === "asc" ? cmp : -cmp;
    });
    const activeDocumentPendingUploads = activeDocumentUploadKey
      ? pendingUploadsByKey[activeDocumentUploadKey] ?? []
      : [];
    const shouldShowDocumentRail =
      Boolean(activeDocumentUploadKey) &&
      (activeDocumentFiles.length > 0 ||
        activeDocumentPendingUploads.length > 0);
    const uploadedDocumentSectionTitle =
      activeDocumentUploadKey && normalizeText(activeStepBaseTitle)
        ? toTitleCase(activeStepBaseTitle)
        : "Uploaded Documents";
    // "About you · 12 of 102": the same numbering as the badge and the
    // attorney's count. Group entries share their group's number.
    const stepCount = typeformStepNodes.length;
    const stepNumber =
      activeScreen && activeScreen.kind !== "final-review"
        ? activeScreen.stepIndex + 1
        : null;
    const progressSection = stepNumber
      ? sectionByStep(intake.schema ?? []).get(
          String(typeformStepNodes[stepNumber - 1]?.node_id ?? "")
        ) ?? ""
      : "";
    const progressLine = stepNumber
      ? [
          progressSection,
          tr("progressOf", { current: stepNumber, total: stepCount })
        ]
          .filter(Boolean)
          .join(" · ")
      : "";
    const typeformProgressValue = finalReviewScreen
      ? 100
      : stepNumber && stepCount
      ? Math.round(((stepNumber - 1) / stepCount) * 100)
      : Math.max(
          0,
          Math.min(100, Math.round(((activeStepIndex + 1) / totalSteps) * 100))
        );
    const renderGroupDecisionScreen = (
      screen: Extract<
        TypeformScreen,
        { kind: "group-lead-in" | "group-add-another" }
      >
    ) => {
      const groupId = String(screen.group.node_id ?? "");
      const isLeadIn = screen.kind === "group-lead-in";
      const selected = isLeadIn
        ? groupDecisions[groupId]
        : addAnotherChoice[groupId];
      const options: Array<{ value: "yes" | "no"; label: string }> = [
        { value: "yes", label: tr("yes") },
        { value: "no", label: tr("no") }
      ];
      const choose = (value: "yes" | "no") => {
        if (isLeadIn) {
          setGroupDecisions((prev) => ({ ...prev, [groupId]: value }));
          if (value === "no") {
            setGroups((prev) => {
              if (!(prev[groupId] ?? []).length) return prev;
              const next = { ...prev };
              delete next[groupId];
              return next;
            });
          }
        } else {
          setAddAnotherChoice((prev) => ({ ...prev, [groupId]: value }));
        }
      };
      return (
        <div className="flex w-full max-w-90 flex-col gap-2">
          {options.map((opt, index) => {
            const optionKey = String.fromCharCode(65 + index);
            const isSelected = selected === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                aria-pressed={isSelected}
                className={[
                  "flex min-h-12 w-full items-center gap-3 rounded-lg border border-solid px-3 py-2.5 text-left transition",
                  "bg-white/10 text-white backdrop-blur-xs hover:bg-white/16 active:bg-white/20",
                  isSelected
                    ? "border-white/70 bg-white/22 shadow-[0_0_0_1px_rgba(255,255,255,0.32)]"
                    : "border-white/10"
                ].join(" ")}
                onClick={() => choose(opt.value)}
              >
                <span className="flex h-6 min-w-6 items-center justify-center rounded border border-solid border-white/25 bg-white/5 px-1.5 text-xs font-semibold leading-none text-white">
                  {optionKey}
                </span>
                <span className="min-w-0 flex-1 text-body font-body text-white">
                  {opt.label}
                </span>
                {isSelected ? (
                  <IntakeIcon
                    className="text-body font-body text-white"
                    name="FeatherCheck"
                  />
                ) : null}
              </button>
            );
          })}
        </div>
      );
    };

    const removeCurrentEntry = (
      screen: Extract<TypeformScreen, { kind: "group-question" }>
    ) => {
      const groupId = String(screen.group.node_id ?? "");
      const minItems = Math.max(0, screen.group.min_items ?? 0);
      const remaining = (groups[groupId] ?? []).filter(
        (x) => x.instanceId !== screen.instanceId
      );
      setGroups((prev) => ({
        ...prev,
        [groupId]: (prev[groupId] ?? []).filter(
          (x) => x.instanceId !== screen.instanceId
        )
      }));
      if (remaining.length === 0 && minItems === 0) {
        setGroupDecisions((prev) => {
          const next = { ...prev };
          delete next[groupId];
          return next;
        });
      }
      setStepTransitionDirection("backward");
      setActiveStepIndex((currentIndex) => Math.max(0, currentIndex - 1));
    };

    // Review-screen actions. These mutate the screen list, so they navigate via
    // `pendingScreenKey` (see the syncing effect) instead of relative indexes.
    const welcomeOffset = hasWelcomeStep ? 1 : 0;

    // Jump from the final review to a specific question screen, then return to
    // the review once the user continues (see `advanceForward`).
    const editReviewField = (targetKey: string) => {
      const targetIndex = typeformScreens.findIndex((s) => s.key === targetKey);
      if (targetIndex < 0) return;
      setPendingReviewReturnKey("final-review");
      setStepTransitionDirection("backward");
      setActiveStepIndex(targetIndex + welcomeOffset);
    };

    const editReviewEntry = (
      group: OutgoingIntakeFormNode,
      instanceId: string
    ) => {
      const groupId = String(group.node_id ?? "");
      const firstChildId = groupChildQuestions(group)[0]?.node_id;
      if (!firstChildId) return;
      const targetIndex = typeformScreens.findIndex(
        (s) => s.key === `grp-q|${groupId}|${instanceId}|${firstChildId}`
      );
      if (targetIndex < 0) return;
      setStepTransitionDirection("backward");
      setActiveStepIndex(targetIndex + welcomeOffset);
    };

    const removeReviewEntry = (
      group: OutgoingIntakeFormNode,
      instanceId: string
    ) => {
      const groupId = String(group.node_id ?? "");
      const minItems = Math.max(0, group.min_items ?? 0);
      const remaining = (groups[groupId] ?? []).filter(
        (x) => x.instanceId !== instanceId
      );
      setGroups((prev) => ({
        ...prev,
        [groupId]: (prev[groupId] ?? []).filter(
          (x) => x.instanceId !== instanceId
        )
      }));
      setStepTransitionDirection("backward");
      if (remaining.length === 0 && minItems === 0) {
        // Emptied an optional group: drop back to its lead-in question.
        setGroupDecisions((prev) => {
          const next = { ...prev };
          delete next[groupId];
          return next;
        });
        setPendingScreenKey(`grp-lead|${groupId}`);
      } else {
        setPendingScreenKey(`grp-review|${groupId}`);
      }
    };

    const addReviewEntry = (group: OutgoingIntakeFormNode) => {
      const groupId = String(group.node_id ?? "");
      const firstChildId = groupChildQuestions(group)[0]?.node_id;
      const newInstanceId = createGroupInstanceId();
      setGroups((prev) => ({
        ...prev,
        [groupId]: [
          ...(prev[groupId] ?? []),
          { instanceId: newInstanceId, answers: {} }
        ]
      }));
      setStepTransitionDirection("forward");
      if (firstChildId) {
        setPendingScreenKey(
          `grp-q|${groupId}|${newInstanceId}|${firstChildId}`
        );
      }
    };

    const renderGroupReviewScreen = (
      screen: Extract<TypeformScreen, { kind: "group-review" }>
    ) => {
      const { group } = screen;
      const groupId = String(group.node_id ?? "");
      const instances = groups[groupId] ?? [];
      const sortedInstances = instances
        .map((instance, originalIndex) => ({ instance, originalIndex }))
        .sort((a, b) => {
          const keyA = groupEntrySortKey(group, a.instance.answers);
          const keyB = groupEntrySortKey(group, b.instance.answers);
          // Undated entries always sink to the bottom, regardless of order.
          if (keyA !== keyB) {
            if (!Number.isFinite(keyA)) return 1;
            if (!Number.isFinite(keyB)) return -1;
            // Reverse chronological: most recent start date first.
            return keyB - keyA;
          }
          return a.originalIndex - b.originalIndex;
        })
        .map((entry) => entry.instance);
      const minItems = Math.max(0, group.min_items ?? 0);
      const maxItems =
        typeof group.max_items === "number" && group.max_items > 0
          ? group.max_items
          : null;
      const childQuestions = groupChildQuestions(group);
      const entryLabel = groupEntryLabel(group);
      const canRemove = !isRevisionRequested && instances.length > minItems;
      const canAdd =
        !isRevisionRequested &&
        (maxItems == null || instances.length < maxItems);
      const toggleExpanded = (instanceId: string) =>
        setReviewExpanded((prev) => ({
          ...prev,
          [instanceId]: !prev[instanceId]
        }));
      return (
        <div className="flex w-full max-w-140 flex-col gap-2.5 text-left">
          <div className="flex max-h-[calc(100dvh-360px)] w-full flex-col gap-2.5 overflow-y-auto overflow-x-hidden pr-1 mobile:max-h-[calc(100dvh-300px)]">
            {sortedInstances.map((instance, index) => {
              const contextLabel =
                groupEntryContextLabel(group, instance.answers) ||
                `${entryLabel} ${index + 1}`;
              const summary = groupEntrySummary(group, instance.answers);
              const isCurrent = groupEntryIsCurrent(group, instance.answers);
              const rows = childQuestions
                .map((q) => ({
                  q,
                  value: formatGroupAnswerValue(q, instance.answers[q.node_id])
                }))
                .filter((r) => r.value);
              const isExpanded = reviewExpanded[instance.instanceId] ?? false;
              const panelId = `review-entry-${instance.instanceId}`;
              return (
                <div
                  key={instance.instanceId}
                  className="flex w-full flex-col rounded-2xl border border-solid border-white/12 bg-white/5 text-left backdrop-blur-xs transition hover:border-white/20"
                >
                  <div className="flex items-center gap-2 px-4 py-3.5">
                    <button
                      type="button"
                      className="flex min-w-0 flex-1 items-center gap-3 text-left"
                      aria-expanded={isExpanded}
                      aria-controls={panelId}
                      onClick={() => toggleExpanded(instance.instanceId)}
                    >
                      <span className="flex h-6 min-w-6 items-center justify-center rounded-full border border-solid border-white/25 bg-white/10 px-1.5 text-sm font-semibold leading-none text-white">
                        {index + 1}
                      </span>
                      <span className="flex min-w-0 flex-col">
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="truncate text-lg font-semibold leading-tight text-white">
                            {contextLabel}
                          </span>
                          {isCurrent ? (
                            <span className="shrink-0 rounded-full border border-solid border-success-line bg-success-soft px-2 py-0.5 text-xs font-semibold uppercase leading-none tracking-wide text-success">
                              Current
                            </span>
                          ) : null}
                        </span>
                        {summary ? (
                          <span className="truncate text-base leading-tight text-white/55">
                            {summary}
                          </span>
                        ) : null}
                      </span>
                    </button>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        aria-label={`Edit ${contextLabel}`}
                        className="flex items-center gap-1.5 rounded-md px-2 py-1 text-base font-medium text-white/70 transition hover:bg-white/10 hover:text-white"
                        onClick={() =>
                          editReviewEntry(group, instance.instanceId)
                        }
                      >
                        <IntakeIcon name="FeatherPencil" />
                        <span className="mobile:hidden">{tr("edit")}</span>
                      </button>
                      {canRemove ? (
                        <button
                          type="button"
                          aria-label={`Remove ${contextLabel}`}
                          className="flex items-center gap-1.5 rounded-md px-2 py-1 text-base font-medium text-white/55 transition hover:bg-white/10 hover:text-white"
                          onClick={() =>
                            removeReviewEntry(group, instance.instanceId)
                          }
                        >
                          <IntakeIcon name="FeatherTrash2" />
                          <span className="mobile:hidden">{tr("remove")}</span>
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <ReviewEntryDetails id={panelId} isExpanded={isExpanded}>
                    <div className="flex w-full flex-col border-t border-solid border-white/10 px-4 py-1">
                      {rows.length ? (
                        <div className="flex w-full flex-col divide-y divide-white/10">
                          {rows.map((r) => (
                            <div
                              key={r.q.node_id}
                              className="flex flex-col gap-1 py-3 text-left"
                            >
                              <span className="text-base leading-tight text-white/50">
                                {normalizeText(r.q.prompt) ||
                                  normalizeText(r.q.title)}
                              </span>
                              <span className="text-lg leading-snug text-white/95">
                                {r.value}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="py-3 text-base text-white/45">
                          {tr("noDetails")}
                        </span>
                      )}
                    </div>
                  </ReviewEntryDetails>
                </div>
              );
            })}
          </div>
          {canAdd ? (
            <button
              type="button"
              className="flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-white/25 bg-white/3 px-4 py-3 text-md font-medium text-white/80 transition hover:border-white/40 hover:bg-white/8 hover:text-white"
              onClick={() => addReviewEntry(group)}
            >
              <IntakeIcon name="FeatherPlus" />
              Add another {entryLabel.toLowerCase()}
            </button>
          ) : null}
        </div>
      );
    };

    const renderFinalReviewRow = (
      label: string,
      value: string,
      targetKey: string,
      files?: OutgoingIntakeFile[]
    ) => (
      <div className="flex items-start justify-between gap-3 py-3.5 text-left">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-base leading-tight text-white/50">{label}</span>
          {files !== undefined ? (
            files.length > 0 ? (
              <div className="mt-1 flex w-full flex-col gap-1.5">
                {files.map((file) => {
                  const isFilePreviewSelected =
                    previewFile?.file_id === file.file_id;
                  return (
                    <div
                      key={file.file_id}
                      className="flex w-full min-w-0 flex-col gap-1.5"
                    >
                      <button
                        type="button"
                        aria-pressed={isFilePreviewSelected}
                        className={[
                          "relative flex w-full min-w-0 items-center gap-2 overflow-hidden rounded-lg border border-solid px-2.5 py-2 text-left transition",
                          isFilePreviewSelected
                            ? "border-white/80 bg-white/18 shadow-[0_8px_24px_rgba(0,0,0,0.28)] ring-1 ring-inset ring-white/50"
                            : "border-white/15 bg-white/6 hover:border-white/30 hover:bg-white/12"
                        ].join(" ")}
                        onClick={() => openQuickPreviewOverlay(file)}
                      >
                        <FileTypeIcon name={file.filename} size={16} />
                        <span
                          className={[
                            "min-w-0 flex-1 truncate text-md leading-snug",
                            isFilePreviewSelected
                              ? "font-semibold text-white"
                              : "text-white/95"
                          ].join(" ")}
                        >
                          {file.filename}
                        </span>
                        {isFilePreviewSelected ? (
                          <span className="flex shrink-0 items-center gap-1.5">
                            <span className="hidden text-xs font-semibold uppercase tracking-wide text-white/70 sm:inline">
                              Previewing
                            </span>
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/20 text-white">
                              <IntakeIcon className="text-sm" name="FeatherX" />
                            </span>
                          </span>
                        ) : (
                          <IntakeIcon
                            className="shrink-0 text-md text-white/50"
                            name="FeatherEye"
                          />
                        )}
                      </button>
                      {isFilePreviewSelected && !isReviewPreviewDesktop ? (
                        <div className="h-[60vh] min-h-80 w-full overflow-hidden">
                          {renderReviewFilePreviewCard({ showHeader: false })}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <span className="text-md italic leading-snug text-white/40">
                {tr("notAnswered")}
              </span>
            )
          ) : value ? (
            <span className="text-lg leading-snug text-white/95">{value}</span>
          ) : (
            <span className="text-md italic leading-snug text-white/40">
              {tr("notAnswered")}
            </span>
          )}
        </div>
        <button
          type="button"
          aria-label={`Edit ${label}`}
          className="flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-base font-medium text-white/70 transition hover:bg-white/10 hover:text-white"
          onClick={() => editReviewField(targetKey)}
        >
          <IntakeIcon name="FeatherPencil" />
          <span className="mobile:hidden">{tr("edit")}</span>
        </button>
      </div>
    );

    // Phrase review labels exactly as the client saw them in the flow:
    // conversational question titles, and contextual titles for group entries
    // (e.g. "When did you start living at 123 Main St?").
    const finalReviewLabel = (
      q: OutgoingIntakeFormNode,
      group?: OutgoingIntakeFormNode,
      entryAnswers?: Record<string, OutgoingIntakeAnswer>
    ) => {
      const base = normalizeText(q.title) || normalizeText(q.prompt);
      if (isCustomQuestionNode(q)) return base || "Field";
      if (group && entryAnswers) {
        const contextual = contextualGroupQuestionTitle(group, q, entryAnswers);
        if (contextual) return contextual;
      }
      if (q.node_type === "question" && q.question_type === "file") {
        return formatDocumentRequestTitle(base) || base;
      }
      return formatInformationRequestTitle(base) || base;
    };

    const renderFinalReviewScreen = () => (
      <div className="flex w-full max-w-140 flex-col gap-2.5 text-left">
        <div className="flex max-h-[calc(100dvh-360px)] w-full flex-col gap-4 overflow-y-auto overflow-x-hidden pr-1 mobile:max-h-[calc(100dvh-300px)]">
          {typeformStepNodes.map((step) => {
            const stepId = String(step.node_id ?? "");
            const sectionTitle =
              normalizeText(step.title) ||
              formatInformationRequestTitle(
                normalizeText(step.prompt) || "Section"
              );

            if (step.node_type === "repeatable_group") {
              const childQuestions = groupChildQuestions(step);
              const instances = groups[stepId] ?? [];
              return (
                <div
                  key={stepId}
                  className="flex w-full flex-col gap-2 text-left"
                >
                  <span className="px-1 text-sm font-semibold uppercase leading-none tracking-wide text-white/45">
                    {sectionTitle}
                  </span>
                  {instances.length === 0 ? (
                    <div className="rounded-2xl border border-solid border-white/12 bg-white/5 px-4 py-3 text-md text-white/45">
                      {tr("noEntriesAdded")}
                    </div>
                  ) : (
                    instances.map((inst, i) => {
                      const entryLabel =
                        groupEntryContextLabel(step, inst.answers) ||
                        `${groupEntryLabel(step)} ${i + 1}`;
                      return (
                        <div
                          key={inst.instanceId}
                          className="flex w-full flex-col rounded-2xl border border-solid border-white/12 bg-white/5 text-left"
                        >
                          <div className="px-4 pb-1 pt-3.5">
                            <span className="text-md font-semibold text-white">
                              {entryLabel}
                            </span>
                          </div>
                          <div className="flex w-full flex-col divide-y divide-white/10 px-4 pb-1">
                            {childQuestions.map((q) =>
                              React.cloneElement(
                                renderFinalReviewRow(
                                  finalReviewLabel(q, step, inst.answers),
                                  formatGroupAnswerValue(
                                    q,
                                    inst.answers[q.node_id]
                                  ),
                                  `grp-q|${stepId}|${inst.instanceId}|${q.node_id}`,
                                  q.question_type === "file"
                                    ? inst.answers[q.node_id]?.files ?? []
                                    : undefined
                                ),
                                { key: q.node_id }
                              )
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              );
            }

            const leaves = flatQuestions([step]);
            if (leaves.length === 0) return null;
            return (
              <div
                key={stepId}
                className="flex w-full flex-col divide-y divide-white/10 rounded-2xl border border-solid border-white/12 bg-white/5 px-4 text-left"
              >
                {leaves.map((q) =>
                  React.cloneElement(
                    renderFinalReviewRow(
                      finalReviewLabel(q),
                      formatGroupAnswerValue(q, answersById[q.node_id]),
                      `q|${stepId}`,
                      q.question_type === "file"
                        ? answersById[q.node_id]?.files ?? []
                        : undefined
                    ),
                    { key: q.node_id }
                  )
                )}
              </div>
            );
          })}
        </div>
      </div>
    );

    // Uploaded documents for a file question, rendered with the same card +
    // inline preview UX as the small-width guided flow. Shared by the
    // single-page form and the attorney review group stack.
    const renderSinglePageFileAttachments = (
      q: OutgoingIntakeFormNode,
      groupId?: string,
      instanceId?: string
    ) => {
      const key = `file|${q.node_id}|${groupId ?? ""}|${instanceId ?? ""}`;
      const answer =
        groupId && instanceId
          ? (groups[groupId] ?? []).find((x) => x.instanceId === instanceId)
              ?.answers[q.node_id]
          : answersById[q.node_id];
      const files = answer?.files ?? [];
      const pending = pendingUploadsByKey[key] ?? [];
      if (!files.length && !pending.length) return null;
      return (
        <div className="public-intake-doc-section mt-3 flex w-full min-w-0 flex-col gap-2">
          {pending.map((p) => renderPendingUploadCard(p))}
          {files.map((f, index) => (
            <div key={f.file_id} className="flex w-full min-w-0 flex-col gap-2">
              {renderUploadedIntakeFileCard(f, index)}
              {previewFile?.file_id === f.file_id ? (
                <div className="h-[60vh] min-h-80 w-full overflow-hidden">
                  {renderReviewFilePreviewCard({ showHeader: true })}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      );
    };

    // A single question row: numbered/letter badge + external Typeform-style
    // title (matching the guided flow's phrasing) with the input underneath.
    // Shared by the single-page form and the attorney review group stack so
    // both render multi-entry questions identically. `statusClass` tints the
    // input (review accept/reject) and `afterInput` renders review controls.
    const renderQuestionBadge = (badge: React.ReactNode) => (
      <span
        className="mt-1 inline-flex h-5 min-w-5 shrink-0 items-center justify-center px-1.25 text-sm font-medium"
        style={{
          background: intakeFgSolid,
          color: intakePageBg,
          borderRadius: "5px"
        }}
      >
        {badge}
      </span>
    );

    const renderIntakeQuestionBlock = (
      q: OutgoingIntakeFormNode,
      opts: {
        badge: string;
        group?: OutgoingIntakeFormNode;
        instanceId?: string;
        entryAnswers?: Record<string, OutgoingIntakeAnswer>;
        statusClass?: string;
        afterInput?: React.ReactNode;
        domId?: string;
      }
    ) => {
      const {
        badge,
        group,
        instanceId,
        entryAnswers,
        statusClass,
        afterInput,
        domId
      } = opts;
      const groupId = group ? String(group.node_id ?? "") : undefined;
      const isFileQuestion = q.question_type === "file";
      const title = group
        ? finalReviewLabel(q, group, entryAnswers)
        : finalReviewLabel(q);
      const help = informationRequestDescription(q, Boolean(group));
      return (
        <div
          key={q.node_id}
          id={domId}
          className="flex w-full items-start gap-3"
          style={domId ? { scrollMarginTop: "24px" } : undefined}
        >
          {renderQuestionBadge(badge)}
          <div className="min-w-0 flex-1">
            <p
              className="font-medium"
              style={{
                fontSize: isMdUp ? "20px" : "18px",
                lineHeight: 1.3,
                color: intakeFgSolid
              }}
            >
              {title}
              {q.required ? (
                <span style={{ color: `rgba(${intakeFgRgb}, 0.5)` }}> *</span>
              ) : null}
            </p>
            {help ? (
              <p
                className="mt-1.5"
                style={{
                  fontSize: "15px",
                  lineHeight: 1.4,
                  color: `rgba(${intakeFgRgb}, 0.62)`
                }}
              >
                {help}
              </p>
            ) : null}
            <div className={cn("mt-4", statusClass)}>
              {renderQuestion(q, {
                groupId,
                instanceId,
                hideLabel: true,
                padded: false,
                hideFileList: isFileQuestion
              })}
              {isFileQuestion
                ? renderSinglePageFileAttachments(q, groupId, instanceId)
                : null}
            </div>
            {afterInput ? <div className="mt-4">{afterInput}</div> : null}
          </div>
        </div>
      );
    };

    // One repeatable-group entry rendered as a card: entry-label header plus
    // letter-badged (A, B, C, ...) sub-questions. Shared so the single-page
    // form and the attorney review stack look identical.
    const renderGroupEntryCard = (
      group: OutgoingIntakeFormNode,
      instance: {
        instanceId: string;
        answers: Record<string, OutgoingIntakeAnswer>;
      },
      index: number,
      opts?: {
        domId?: string;
        showRemove?: boolean;
        onRemove?: () => void;
        showCurrent?: boolean;
        statusClassForKey?: (key: string) => string;
        renderAfterQuestion?: (
          key: string,
          q: OutgoingIntakeFormNode,
          groupId: string | null,
          instanceId: string | null
        ) => React.ReactNode;
        // Review-only: give each sub-question a dom id so the TOC's third level
        // can scroll a specific sub-answer into view.
        withSubQuestionIds?: boolean;
      }
    ) => {
      const childQuestions = groupChildQuestions(group);
      const groupId = String(group.node_id ?? "");
      const entryAnswers = instance.answers ?? {};
      const entryLabel =
        groupEntryContextLabel(group, entryAnswers) ||
        `${groupEntryLabel(group)} ${index + 1}`;
      const showCurrent =
        Boolean(opts?.showCurrent) && groupEntryIsCurrent(group, entryAnswers);
      return (
        <div
          key={instance.instanceId}
          id={opts?.domId}
          className="w-full rounded-2xl border border-solid p-5 text-left"
          style={{
            borderColor: `rgba(${intakeFgRgb}, 0.12)`,
            background: `rgba(${intakeFgRgb}, 0.05)`,
            scrollMarginTop: opts?.domId ? "24px" : undefined
          }}
        >
          <div className="flex items-center justify-between gap-3">
            <p
              className="font-semibold"
              style={{ fontSize: "15px", color: intakeFgSolid }}
            >
              {entryLabel}
            </p>
            {opts?.showRemove && opts.onRemove ? (
              <button
                type="button"
                onClick={opts.onRemove}
                className="shrink-0 text-base font-medium underline underline-offset-2 transition-opacity hover:opacity-80"
                style={{ color: `rgba(${intakeFgRgb}, 0.62)` }}
              >
                Remove
              </button>
            ) : showCurrent ? (
              <span className="shrink-0 rounded-full border border-solid border-success-line bg-success-soft px-2 py-0.5 text-xs font-semibold uppercase leading-none tracking-wide text-success">
                Current
              </span>
            ) : null}
          </div>
          <div
            className={cn(
              "mt-5 flex flex-col",
              opts?.renderAfterQuestion ? "gap-14" : "gap-7"
            )}
          >
            {childQuestions.map((q, qi) => {
              const key = decisionKey({
                question_id: String(q.node_id ?? ""),
                repeatable_group_id: groupId,
                repeatable_group_instance_id: instance.instanceId
              });
              return renderIntakeQuestionBlock(q, {
                badge: String.fromCharCode(65 + qi),
                group,
                instanceId: instance.instanceId,
                entryAnswers,
                statusClass: opts?.statusClassForKey?.(key),
                afterInput: opts?.renderAfterQuestion?.(
                  key,
                  q,
                  groupId,
                  instance.instanceId
                ),
                domId: opts?.withSubQuestionIds
                  ? `review-entry-${groupId}-${instance.instanceId}-q-${String(
                      q.node_id ?? ""
                    )}`
                  : undefined
              });
            })}
          </div>
        </div>
      );
    };

    // Traditional single-page form: the whole intake rendered on one page via
    // the legacy renderNode tree, on a light "paper" card so it stays legible
    // over any brand background. Reuses the same answer state, uploads, and
    // submit path as the guided flow.
    const renderSinglePageForm = () => {
      const steps = typeformStepNodes;
      // Running question counter so each field shows a sequential number badge,
      // mirroring the guided (Typeform) experience.
      let questionNumber = 0;

      // Attorney review: green/red tint class for an answer's decision.
      const reviewStatusClass = (key: string): string => {
        const status = reviewDecisionByKey?.[key] ?? "pending";
        if (status === "accepted") return "intake-review-accepted";
        if (status === "rejected") return "intake-review-rejected";
        return "";
      };

      // Whether the per-question comment block renders in single-page (same
      // gate as the guided flow's `showClientCommentField`). A read-only record
      // still shows the threads — it just can't add to them, which
      // renderClientCommentBlock enforces.
      const showSinglePageCommentField =
        !reviewMode &&
        (isAttorneyReadOnly ||
          (!isPreview && !isAccepted && !isAwaitingReview));

      // A single top-level question: allocates the next running number and
      // delegates to the shared block renderer. Group sub-questions are handled
      // by `renderGroupEntryCard` (letter badges), so this path is number-only.
      // In attorney review, each question also gets the accept / request-changes
      // controls + comment thread beneath it and a dom id for TOC scroll-to.
      const renderQuestionBlock = (q: OutgoingIntakeFormNode) => {
        questionNumber += 1;
        const badge = String(questionNumber);
        if (!reviewMode) {
          const key = decisionKey({ question_id: String(q.node_id ?? "") });
          return renderIntakeQuestionBlock(q, {
            badge,
            statusClass: revisionStatusClass(key),
            afterInput: showSinglePageCommentField
              ? renderClientCommentBlock({
                  key,
                  questionNode: q,
                  groupId: null,
                  instanceId: null,
                  wrapperClassName: "flex w-full flex-col gap-3"
                })
              : undefined
          });
        }
        const key = decisionKey({ question_id: String(q.node_id ?? "") });
        return renderIntakeQuestionBlock(q, {
          badge,
          statusClass: reviewStatusClass(key),
          afterInput: renderReviewDecisionBlock(key),
          domId: `review-q-${String(q.node_id ?? "")}`
        });
      };

      const renderStep = (step: OutgoingIntakeFormNode) => {
        const stepId = String(step.node_id ?? "");
        const sectionTitle =
          normalizeText(step.title) ||
          formatInformationRequestTitle(
            normalizeText(step.prompt) || "Section"
          );
        const eyebrow = (
          <p
            className="text-sm font-semibold uppercase tracking-[0.08em]"
            style={{ color: `rgba(${intakeFgRgb}, 0.45)` }}
          >
            {sectionTitle}
          </p>
        );

        if (step.node_type === "repeatable_group") {
          const instances = groups[stepId] ?? [];
          const maxItems = step.max_items ?? null;
          const minItems = Math.max(0, step.min_items ?? 0);
          const canManage = !isPreview && !isRevisionRequested;
          const canAdd =
            canManage && (maxItems == null || instances.length < maxItems);
          questionNumber += 1;
          const groupNumber = questionNumber;
          const groupHelp = normalizeText(step.help_text);
          return (
            <div key={stepId} className="flex w-full items-start gap-3">
              {renderQuestionBadge(groupNumber)}
              <div className="min-w-0 flex-1">
                <p
                  className="font-medium"
                  style={{
                    fontSize: isMdUp ? "20px" : "18px",
                    lineHeight: 1.3,
                    color: intakeFgSolid
                  }}
                >
                  {groupQuestionTitle(step)}
                  {minItems > 0 ? (
                    <span style={{ color: `rgba(${intakeFgRgb}, 0.5)` }}>
                      {" "}
                      *
                    </span>
                  ) : null}
                </p>
                {groupHelp ? (
                  <p
                    className="mt-1.5"
                    style={{
                      fontSize: "15px",
                      lineHeight: 1.4,
                      color: `rgba(${intakeFgRgb}, 0.62)`
                    }}
                  >
                    {groupHelp}
                  </p>
                ) : null}
                <div className="mt-4 flex flex-col gap-4">
                  {instances.length === 0 ? (
                    <div
                      className="w-full rounded-2xl border border-solid px-5 py-4"
                      style={{
                        borderColor: `rgba(${intakeFgRgb}, 0.12)`,
                        background: `rgba(${intakeFgRgb}, 0.04)`,
                        fontSize: "14px",
                        color: `rgba(${intakeFgRgb}, 0.55)`
                      }}
                    >
                      {tr("noEntriesYet")}
                    </div>
                  ) : (
                    instances.map((inst, i) =>
                      renderGroupEntryCard(
                        step,
                        inst,
                        i,
                        reviewMode
                          ? {
                              domId: `review-entry-${stepId}-${inst.instanceId}`,
                              showCurrent: true,
                              statusClassForKey: reviewStatusClass,
                              renderAfterQuestion: (key) =>
                                renderReviewDecisionBlock(key),
                              withSubQuestionIds: true
                            }
                          : {
                              showRemove:
                                canManage && instances.length > minItems,
                              onRemove: () =>
                                setGroups((prev) => ({
                                  ...prev,
                                  [stepId]: (prev[stepId] ?? []).filter(
                                    (x) => x.instanceId !== inst.instanceId
                                  )
                                })),
                              statusClassForKey: revisionStatusClass,
                              renderAfterQuestion: showSinglePageCommentField
                                ? (key, q, groupId, instanceId) =>
                                    renderClientCommentBlock({
                                      key,
                                      questionNode: q,
                                      groupId,
                                      instanceId,
                                      wrapperClassName:
                                        "flex w-full flex-col gap-3"
                                    })
                                : undefined
                            }
                      )
                    )
                  )}
                  {canAdd ? (
                    <button
                      type="button"
                      onClick={() =>
                        setGroups((prev) => ({
                          ...prev,
                          [stepId]: [
                            ...(prev[stepId] ?? []),
                            { instanceId: createGroupInstanceId(), answers: {} }
                          ]
                        }))
                      }
                      className="flex items-center gap-2 self-start rounded-lg border border-solid px-4 py-2.5 text-md font-semibold transition-colors"
                      style={{
                        borderColor: `rgba(${intakeFgRgb}, 0.3)`,
                        color: intakeFgSolid,
                        background: `rgba(${intakeFgRgb}, 0.04)`
                      }}
                    >
                      <IntakeIcon name="FeatherPlus" />
                      <span>
                        {addLabelForGroup(stepId, step.title ?? null)}
                      </span>
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          );
        }

        const leaves = flatQuestions([step]);
        if (leaves.length === 0) return null;
        const hasTitle = normalizeText(step.title).length > 0;
        return (
          <div key={stepId} className="w-full">
            {hasTitle ? eyebrow : null}
            <div className={cn("flex flex-col gap-8", hasTitle && "mt-5")}>
              {leaves.map((q) => renderQuestionBlock(q))}
            </div>
          </div>
        );
      };

      return (
        <div
          className="public-intake-typeform-content mx-auto w-full max-w-180 text-left"
          style={{ color: intakeFgSolid }}
        >
          {normalizeText(intake.attorney_note) ? (
            <p
              className="whitespace-pre-wrap"
              style={{
                fontSize: isMdUp ? "18px" : "16px",
                lineHeight: 1.4,
                color: `rgba(${intakeFgRgb}, 0.66)`
              }}
            >
              {intake.attorney_note}
            </p>
          ) : null}

          <div
            className={cn(
              "flex flex-col gap-14",
              normalizeText(intake.attorney_note) && "mt-12"
            )}
          >
            {steps.map((step) => renderStep(step))}
          </div>

          <div
            className="mt-12 border-t border-solid pt-8"
            style={{ borderColor: `rgba(${intakeFgRgb}, 0.14)` }}
          >
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="primary"
                disabled={!isFormComplete || isSubmitting}
                className="min-h-11 gap-2 rounded-md bg-white px-5 py-2.5 text-base font-medium leading-none text-[#111827]! shadow-[0_8px_18px_rgba(0,0,0,0.18)] hover:bg-white/90 active:bg-white disabled:bg-white/40 disabled:text-[#111827]/60! disabled:opacity-100 **:text-[#111827]! **:disabled:text-[#111827]/60!"
                onClick={() => {
                  // Hand off to the guided review screen (per-question edit +
                  // final submit), keeping one review/submit implementation.
                  const finalReviewIdx = typeformScreens.findIndex(
                    (s) => s.kind === "final-review"
                  );
                  if (finalReviewIdx < 0) {
                    submit();
                    return;
                  }
                  autosaveProgress();
                  setLayoutMode("typeform");
                  setStepTransitionDirection("forward");
                  setActiveStepIndex(finalReviewIdx + (hasWelcomeStep ? 1 : 0));
                }}
              >
                <IntakeIcon name="FeatherArrowRight" className="text-base" />
                Review &amp; Submit
              </Button>
              {!isFormComplete ? (
                <p
                  style={{
                    fontSize: "15px",
                    color: `rgba(${intakeFgRgb}, 0.6)`
                  }}
                >
                  Complete all required fields to review and submit.
                </p>
              ) : null}
            </div>
          </div>
        </div>
      );
    };

    const groupChildQuestionIds = groupQuestionScreen
      ? groupChildQuestions(groupQuestionScreen.group).map((q) => q.node_id)
      : [];
    const isFirstSubQuestionOfEntry =
      Boolean(groupQuestionScreen) &&
      groupChildQuestionIds[0] === groupQuestionScreen?.node.node_id;
    const canRemoveCurrentEntry =
      Boolean(groupQuestionScreen) &&
      !isPreview &&
      !isRevisionRequested &&
      isFirstSubQuestionOfEntry &&
      (groups[String(groupQuestionScreen?.group.node_id ?? "")] ?? []).length >
        Math.max(0, groupQuestionScreen?.group.min_items ?? 0);

    // Attorney review: the accept / request-changes controls plus the comment
    // thread for a single answer. Rendered once per screen for standalone
    // questions and once per sub-answer inside a stacked repeatable group.
    function renderReviewDecisionBlock(reviewKey: string): React.ReactNode {
      const status = reviewDecisionByKey?.[reviewKey] ?? "pending";
      const thread = reviewCommentsByKey?.[reviewKey] ?? [];
      const note = reviewNoteByKey?.[reviewKey] ?? "";
      const isAccepted = status === "accepted";
      const isRejected = status === "rejected";
      const isDecided = isAccepted || isRejected;
      // Compose state: the textarea binds to a local draft and only commits on
      // "Post". Once a note is committed it renders as a posted card with an
      // "Edit" affordance; the composer stays open until the required note has
      // been posted.
      const noteDraftPresent = reviewKey in reviewNoteDrafts;
      const noteComposerOpen = noteDraftPresent || note.trim() === "";
      const noteDraftValue = reviewNoteDrafts[reviewKey] ?? note;
      const openNoteComposer = () =>
        setReviewNoteDrafts((prev) => ({ ...prev, [reviewKey]: note }));
      const closeNoteComposer = () =>
        setReviewNoteDrafts((prev) => {
          const next = { ...prev };
          delete next[reviewKey];
          return next;
        });
      const postNote = () => {
        onReviewNoteChange?.(reviewKey, noteDraftValue.trim());
        closeNoteComposer();
      };
      const btnBase =
        "inline-flex min-h-10 items-center gap-1.5 overflow-hidden whitespace-nowrap rounded-md border border-dotted py-2 text-md font-semibold leading-none transition-all duration-300 ease-out";
      const collapsed = "max-w-0 border-0 px-0 opacity-0 pointer-events-none";
      const acceptCollapsed = isRejected;
      const rejectCollapsed = isAccepted;
      return (
        <div className="flex w-full flex-col gap-4">
          <div className="flex items-center">
            <button
              type="button"
              tabIndex={acceptCollapsed ? -1 : 0}
              aria-hidden={acceptCollapsed}
              aria-pressed={isAccepted}
              onClick={() =>
                onReviewDecision?.(
                  reviewKey,
                  isAccepted ? "pending" : "accepted"
                )
              }
              className={[
                btnBase,
                acceptCollapsed ? collapsed : "max-w-70 px-4",
                isAccepted
                  ? "border-[#16a34a] bg-[#16a34a] text-white"
                  : "border-white/45 bg-transparent text-white hover:bg-white/10"
              ].join(" ")}
            >
              <IntakeIcon name="FeatherCheck" />
              {isAccepted ? "Accepted" : "Accept"}
            </button>
            <button
              type="button"
              tabIndex={rejectCollapsed ? -1 : 0}
              aria-hidden={rejectCollapsed}
              aria-pressed={isRejected}
              onClick={() =>
                onReviewDecision?.(
                  reviewKey,
                  isRejected ? "pending" : "rejected"
                )
              }
              className={[
                btnBase,
                rejectCollapsed
                  ? collapsed
                  : ["max-w-70 px-4", isDecided ? "ml-0" : "ml-3"].join(" "),
                isRejected
                  ? "border-[#dc2626] bg-[#dc2626] text-white"
                  : "border-white/45 bg-transparent text-white hover:bg-white/10"
              ].join(" ")}
            >
              <IntakeIcon name="FeatherMessageSquare" />
              {isRejected ? "Requesting Changes" : "Request Changes"}
            </button>
            {isDecided ? (
              <button
                type="button"
                onClick={() => onReviewDecision?.(reviewKey, "pending")}
                className="ml-3 inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-base font-medium leading-none text-white/70 transition hover:bg-white/10 hover:text-white"
              >
                <IntakeIcon name="FeatherRotateCcw" />
                Undo
              </button>
            ) : null}
          </div>

          {thread.length > 0 || isRejected ? (
            <div className="public-intake-review-comments flex w-full flex-col gap-2">
              {thread.map((item, idx) => {
                const isAttorney = item.author === "attorney";
                const initials =
                  item.name
                    .split(/\s+/)
                    .filter(Boolean)
                    .map((p) => p[0]?.toUpperCase())
                    .join("")
                    .slice(0, 2) || (isAttorney ? "AT" : "CL");
                return (
                  <div
                    key={`${item.author}-${idx}`}
                    className="intake-comment-card flex w-full items-start gap-2.5 rounded-2xl px-4 py-3.5"
                  >
                    <IntakeAvatar
                      size="xs"
                      image={item.avatarUrl ?? undefined}
                      variant={isAttorney ? "error" : "neutral"}
                    >
                      {initials}
                    </IntakeAvatar>
                    <div className="flex min-w-0 grow shrink-0 basis-0 flex-col items-start gap-1">
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <span className="intake-comment-name text-caption-bold font-caption-bold truncate">
                          {item.name}
                        </span>
                        <span className="intake-comment-meta text-caption font-caption">
                          {isAttorney ? "Attorney" : "Client"}
                        </span>
                        {typeof item.revisionNumber === "number" ? (
                          <span className="intake-comment-meta text-caption font-caption">
                            {`Revision ${item.revisionNumber}`}
                          </span>
                        ) : null}
                        {item.timeLabel ? (
                          <span className="intake-comment-meta text-caption font-caption">
                            {item.timeLabel}
                          </span>
                        ) : null}
                      </div>
                      <span
                        className={[
                          "text-caption font-caption whitespace-pre-wrap wrap-break-word",
                          isAttorney
                            ? "intake-comment-body-attorney"
                            : "intake-comment-body-client"
                        ].join(" ")}
                      >
                        {item.text}
                      </span>
                    </div>
                  </div>
                );
              })}
              {isRejected ? (
                <div className="intake-comment-card flex w-full items-start gap-2.5 rounded-2xl px-4 py-3.5">
                  <IntakeAvatar
                    size="xs"
                    image={reviewAttorneyAvatarUrl}
                    variant="error"
                  >
                    {reviewAttorneyInitials}
                  </IntakeAvatar>
                  <div className="flex min-w-0 grow shrink-0 basis-0 flex-col items-start gap-1.5">
                    <span className="intake-comment-name text-caption-bold font-caption-bold truncate">
                      {reviewAttorneyName}
                    </span>
                    {noteComposerOpen ? (
                      <>
                        <textarea
                          className="intake-comment-textarea w-full resize-y"
                          placeholder={tr("leaveComment")}
                          value={noteDraftValue}
                          onChange={(e) =>
                            setReviewNoteDrafts((prev) => ({
                              ...prev,
                              [reviewKey]: e.target.value
                            }))
                          }
                        />
                        <div className="mt-1 flex w-full items-center justify-end gap-2">
                          {note.trim() ? (
                            <button
                              type="button"
                              onClick={closeNoteComposer}
                              className="intake-comment-cancel-btn inline-flex items-center rounded-md px-4 py-2.5 text-md font-medium leading-none transition hover:bg-fill-2"
                            >
                              {tr("cancel")}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            disabled={!noteDraftValue.trim()}
                            onClick={postNote}
                            className="inline-flex items-center gap-1.5 rounded-md bg-brand-primary px-4 py-2.5 text-md font-semibold leading-none text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-[#b6bcc6] disabled:hover:bg-[#b6bcc6]"
                          >
                            Post
                          </button>
                        </div>
                      </>
                    ) : (
                      <span className="intake-comment-body-attorney text-caption font-caption whitespace-pre-wrap wrap-break-word">
                        {note}
                      </span>
                    )}
                  </div>
                  {!noteComposerOpen ? (
                    <button
                      type="button"
                      onClick={openNoteComposer}
                      className="intake-comment-edit-btn inline-flex shrink-0 items-center gap-1 rounded-md border border-solid px-2.5 py-1.5 text-sm font-semibold leading-none transition"
                    >
                      <IntakeIcon name="FeatherPencil" className="text-base" />
                      Edit
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      );
    }

    // Attorney review: render an entire repeatable group as one screen with
    // every submitted entry stacked as its own card, each sub-answer keeping
    // its own accept / request-changes controls and comment thread.
    const renderReviewGroupStack = (
      group: OutgoingIntakeFormNode
    ): React.ReactNode => {
      const groupId = String(group.node_id ?? "");
      const instances = groups[groupId] ?? [];
      if (instances.length === 0) {
        return (
          <div className="rounded-2xl border border-solid border-white/12 bg-white/5 px-4 py-6 text-md text-white/60">
            {tr("noEntriesSubmitted")}
          </div>
        );
      }
      const statusClassForKey = (key: string): string => {
        const status = reviewDecisionByKey?.[key] ?? "pending";
        if (status === "accepted") return "intake-review-accepted";
        if (status === "rejected") return "intake-review-rejected";
        return "";
      };
      return (
        <div className="flex w-full flex-col gap-5">
          {instances.map((inst, idx) =>
            renderGroupEntryCard(group, inst, idx, {
              domId: `review-entry-${groupId}-${inst.instanceId}`,
              showCurrent: true,
              statusClassForKey,
              renderAfterQuestion: (key) => renderReviewDecisionBlock(key),
              withSubQuestionIds: true
            })
          )}
        </div>
      );
    };

    let content: React.ReactNode = null;
    if (!isWelcomeStep && activeScreen) {
      if (leadInScreen) {
        content = renderGroupDecisionScreen(leadInScreen);
      } else if (addAnotherScreen) {
        content = renderGroupDecisionScreen(addAnotherScreen);
      } else if (reviewScreen) {
        content = renderGroupReviewScreen(reviewScreen);
      } else if (groupStackScreen) {
        content = renderReviewGroupStack(groupStackScreen.group);
      } else if (finalReviewScreen) {
        content = renderFinalReviewScreen();
      } else if (groupQuestionScreen) {
        content = (
          <div className="flex w-full flex-col gap-4">
            {renderQuestion(groupQuestionScreen.node, {
              groupId: String(groupQuestionScreen.group.node_id ?? ""),
              instanceId: groupQuestionScreen.instanceId,
              hideLabel: true
            })}
            {canRemoveCurrentEntry ? (
              <button
                type="button"
                className="self-start text-caption font-caption text-white/60 underline decoration-white/30 underline-offset-2 transition hover:text-white/85"
                onClick={() => removeCurrentEntry(groupQuestionScreen)}
              >
                Remove this{" "}
                {groupEntryLabel(groupQuestionScreen.group).toLowerCase()}
              </button>
            ) : null}
          </div>
        );
      } else if (activeStepNode) {
        content =
          activeStepNode.node_type === "question"
            ? renderQuestion(activeStepNode, {
                hideLabel: true,
                hideFileList: activeStepNode.question_type === "file"
              })
            : renderNode(activeStepNode);
      }
    }
    const isTypeformPrimaryDisabled = isDecisionScreen
      ? !isPreview && !isActiveStepComplete
      : isLastTypeformStep
      ? !isPreview && (isPersistenceDisabled || !isFormComplete || isSubmitting)
      : !isPreview && !isActiveStepComplete && !isPersistenceDisabled;
    const primaryActionLabel = isDecisionScreen
      ? tr("ok")
      : isLastTypeformStep
      ? isRevisionRequested
        ? tr("sendTo", {
            name:
              normalizeText(intake.attorney_name).split(/\s+/)[0] ||
              tr("yourAttorneyLower")
          })
        : tr("submit")
      : reviewScreen
      ? tr("continue")
      : isActiveStepComplete && !isActiveStepFullyAnswered
      ? tr("skip")
      : tr("ok");

    const renderPublicPreviewDeleteAction = (file: OutgoingIntakeFile) => (
      <Button
        type="button"
        variant="danger"
        size="icon-sm"
        aria-label="Delete uploaded document"
        title="Delete uploaded document"
        onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
          handleRequestDeleteIntakeFile(file, event)
        }
      >
        <Trash2 />
      </Button>
    );

    const publicPreviewDeleteActions = (
      file: OutgoingIntakeFile
    ): SnippetAction[] => [
      {
        id: "delete-uploaded-document",
        label: "Delete uploaded document",
        icon: "trash",
        onSelect: () => handleRequestDeleteIntakeFile(file)
      }
    ];

    const renderUploadedDocumentSortMenu = () => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={`${SORT_PILL_TRIGGER_CLASS} text-fg-2 hover:text-fg`}
            aria-label="Sort uploaded documents"
          >
            <IntakeIcon
              className={`${SORT_PILL_ICON_CLASS} text-fg-3`}
              name="FeatherArrowUpDown"
            />
            <span className="min-w-0 max-w-37.5 truncate text-caption font-caption text-fg-2">
              {uploadedDocumentSortLabel(
                uploadedDocumentSort.sortBy,
                uploadedDocumentSort.sortDir
              )}
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="bottom"
          align="end"
          sideOffset={8}
          className="z-2147483647 w-auto min-w-60"
        >
          {UPLOADED_DOCUMENT_SORT_OPTIONS.map(({ value, label }) => {
            const isSelected =
              uploadedDocumentSort.sortBy === value.sortBy &&
              uploadedDocumentSort.sortDir === value.sortDir;
            return (
              <DropdownMenuItem
                key={`${value.sortBy}-${value.sortDir}`}
                onSelect={() => setUploadedDocumentSort(value)}
                className="h-10 gap-2 px-3"
              >
                <span className="min-w-0 flex-1 truncate">{label}</span>
                {isSelected ? <Check className="text-accent-seed" /> : null}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );

    const renderInlineDocumentPreview = (file: OutgoingIntakeFile) => {
      if (!previewFile || previewFile.file_id !== file.file_id) return null;

      const duplicateUrl = previewDuplicateKey
        ? duplicatePreviewUrls[previewDuplicateKey] ?? null
        : null;
      const isDuplicateLoading = previewDuplicateKey
        ? Boolean(duplicatePreviewLoadingIds[previewDuplicateKey])
        : false;
      const duplicateError = previewDuplicateKey
        ? duplicatePreviewErrors[previewDuplicateKey] ?? null
        : null;
      const showPreviewLoader =
        isQuickPreviewLoading || (!quickPreviewBlobUrl && !quickPreviewError);
      const showDuplicateCompare = Boolean(previewDuplicateMatch);
      const previewReviewCheckItems = getIntakeFileReviewCheckItems(
        previewFile,
        suppressedDuplicateMatchIds
      );
      const selectedReviewWarning = previewReviewCheckItems[0];
      const duplicateReviewWarning =
        previewReviewCheckItems.find((item) => item.type === "duplicate") ??
        selectedReviewWarning;
      const previewUnavailableMessage =
        quickPreviewError ||
        getIntakeFilePreviewUnavailableMessage(previewFile);
      const duplicateIntakeFile =
        previewDuplicateMatch &&
        ["outgoing_intake", "public_outgoing_intake", "public_intake"].includes(
          previewDuplicateMatch.source || ""
        )
          ? activeDocumentFiles.find(
              (candidate) => candidate.file_id === previewDuplicateMatch.id
            ) ?? null
          : null;

      return (
        <div className="overflow-hidden rounded-xl border border-solid border-neutral-border bg-fill-1 shadow-lg">
          {showDuplicateCompare ? (
            <div className="flex min-h-0 w-full flex-col">
              <div className="flex shrink-0 items-center justify-between gap-3 border-b border-solid border-neutral-border bg-default-background px-3 py-2">
                <span
                  className={[
                    "flex min-w-0 flex-1 items-start gap-1.5 text-caption font-caption",
                    duplicateReviewWarning?.status === "failed"
                      ? "text-error-600"
                      : "text-warning-700"
                  ].join(" ")}
                >
                  <IntakeIcon
                    className="mt-0.5 shrink-0 text-caption font-caption"
                    name={
                      duplicateReviewWarning?.status === "failed"
                        ? "FeatherAlertCircle"
                        : "FeatherAlertTriangle"
                    }
                  />
                  <span className="min-w-0 flex-1 whitespace-normal wrap-break-word">
                    {duplicateReviewWarning?.message ||
                      "Possible duplicate evidence."}
                  </span>
                </span>
                <button
                  type="button"
                  aria-label="Close preview"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-fg-2 transition hover:bg-fill-1 active:bg-fill-1"
                  onClick={closeQuickPreviewOverlay}
                >
                  <IntakeIcon name="FeatherX" />
                </button>
              </div>
              <div className="grid min-h-0 w-full grid-cols-1 md:grid-cols-2">
                <div className="min-w-0 border-b border-solid border-neutral-border md:border-b-0 md:border-r">
                  <div className="flex items-center justify-between gap-3 border-b border-solid border-neutral-border bg-default-background px-3 py-2">
                    <span className="truncate text-caption-bold font-caption-bold text-default-font">
                      <strong>This file:</strong> {previewFile.filename}
                    </span>
                    {renderPublicPreviewDeleteAction(previewFile)}
                  </div>
                  <div className="relative h-105 overflow-hidden">
                    {quickPreviewBlobUrl ? (
                      <PdfPreview
                        file={quickPreviewBlobUrl}
                        defaultZoomMode="page-width"
                        ariaLabel={`${previewFile.filename} preview`}
                        showControls={false}
                      />
                    ) : (
                      <div className="flex h-full flex-col items-center justify-center gap-2 px-5 py-6 text-center">
                        {showPreviewLoader ? (
                          <LoadingState text="Loading preview..." />
                        ) : (
                          <span className="text-body font-body text-subtext-color">
                            {previewUnavailableMessage}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="flex items-center justify-between gap-3 border-b border-solid border-neutral-border bg-default-background px-3 py-2">
                    <span className="truncate text-caption-bold font-caption-bold text-default-font">
                      <strong>Duplicate:</strong>{" "}
                      {previewDuplicateMatch?.docNames || "Matched file"}
                    </span>
                    {duplicateIntakeFile
                      ? renderPublicPreviewDeleteAction(duplicateIntakeFile)
                      : null}
                  </div>
                  <div className="relative h-105 overflow-hidden">
                    {duplicateUrl ? (
                      <PdfPreview
                        file={duplicateUrl}
                        defaultZoomMode="page-width"
                        ariaLabel={`${
                          previewDuplicateMatch?.docNames || "Duplicate file"
                        } preview`}
                        showControls={false}
                      />
                    ) : (
                      <div className="flex h-full flex-col items-center justify-center gap-2 px-5 py-6 text-center">
                        {isDuplicateLoading ? (
                          <LoadingState text="Loading duplicate preview..." />
                        ) : (
                          <span className="text-body font-body text-subtext-color">
                            {duplicateError ||
                              "Duplicate preview is not available."}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex min-h-0 w-full flex-col">
              <div className="flex shrink-0 items-center justify-between gap-3 border-b border-solid border-neutral-border bg-default-background px-3 py-2">
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="min-w-0 truncate text-caption-bold font-caption-bold text-default-font">
                    {previewFile.filename}
                  </span>
                  {selectedReviewWarning ? (
                    <span
                      className={[
                        "flex min-w-0 items-start gap-1.5 text-caption font-caption",
                        selectedReviewWarning.status === "failed"
                          ? "text-error-600"
                          : "text-warning-700"
                      ].join(" ")}
                    >
                      <IntakeIcon
                        className="mt-0.5 shrink-0 text-caption font-caption"
                        name={
                          selectedReviewWarning.status === "failed"
                            ? "FeatherAlertCircle"
                            : "FeatherAlertTriangle"
                        }
                      />
                      <span className="min-w-0 flex-1 whitespace-normal wrap-break-word">
                        {selectedReviewWarning.message}
                      </span>
                    </span>
                  ) : null}
                </div>
                <button
                  type="button"
                  aria-label="Close preview"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-fg-2 transition hover:bg-fill-1 active:bg-fill-1"
                  onClick={closeQuickPreviewOverlay}
                >
                  <IntakeIcon name="FeatherX" />
                </button>
              </div>
              <div className="relative h-105 overflow-hidden">
                {quickPreviewBlobUrl ? (
                  <PdfPreview
                    file={quickPreviewBlobUrl}
                    defaultZoomMode="page-width"
                    ariaLabel={`${previewFile.filename} preview`}
                    actions={publicPreviewDeleteActions(previewFile)}
                  />
                ) : (
                  <div className="flex h-full flex-col items-center justify-center gap-2 px-5 py-6 text-center">
                    {showPreviewLoader ? (
                      <LoadingState text="Loading preview..." />
                    ) : (
                      <span className="text-body font-body text-subtext-color">
                        {previewUnavailableMessage}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      );
    };

    const renderUploadedDocumentsInlineSection = () => {
      if (!shouldShowDocumentRail) return null;

      return (
        <div className="mt-5 flex w-full min-w-0 flex-col items-start gap-3 lg:hidden">
          <div className="flex w-full min-w-0 flex-none flex-col overflow-hidden rounded-xl border border-solid border-white/90 bg-[linear-gradient(135deg,rgba(255,255,255,0.99)_0%,rgba(255,255,255,0.96)_58%,rgba(248,250,252,0.94)_100%)] shadow-[0_18px_50px_rgba(0,0,0,0.20),inset_0_1px_0_rgba(255,255,255,1),inset_0_-1px_0_rgba(203,213,225,0.18)] backdrop-blur-xl">
            <div className="flex w-full items-start justify-between gap-3 border-b border-solid border-slate-200/70 bg-white/70 px-3 py-2">
              <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
                <span className="min-w-0 max-w-full truncate text-left text-caption-bold font-caption-bold text-fg">
                  {uploadedDocumentSectionTitle}
                </span>
              </div>
              <span className="flex-none text-caption-bold font-caption-bold font-semibold text-fg-2">
                {activeDocumentFiles.length}{" "}
                {activeDocumentFiles.length === 1 ? "file" : "files"}
              </span>
            </div>
            <div className="flex w-full items-center justify-between gap-3 px-3 py-3">
              <span className="text-caption-bold font-caption-bold uppercase text-fg-2">
                Sort
              </span>
              {renderUploadedDocumentSortMenu()}
            </div>
          </div>

          <div className="flex w-full min-w-0 flex-col gap-2">
            {activeDocumentPendingUploads.map((item) =>
              renderPendingUploadCard(item)
            )}
            {sortedActiveDocumentFiles.map((file, index) => (
              <div
                key={file.file_id}
                className="flex w-full min-w-0 flex-col gap-2"
              >
                {renderUploadedIntakeFileCard(file, index)}
                {renderInlineDocumentPreview(file)}
              </div>
            ))}
          </div>
        </div>
      );
    };

    // Minutes per unanswered question: finding and uploading a document takes
    // far longer than typing an answer.
    const minutesFor = (n: OutgoingIntakeFormNode | undefined) =>
      n?.question_type === "file" ? 1 : 0.25;
    const fieldProgress = (() => {
      if (isRevisionRequested) {
        const questionsById = new Map<string, OutgoingIntakeFormNode>();
        const index = (n: OutgoingIntakeFormNode) => {
          if (n.node_type === "question")
            questionsById.set(String(n.node_id), n);
          (n.children ?? []).forEach(index);
        };
        (intake.schema ?? []).forEach(index);
        const totalFields = requestedKeys.length;
        const open = requestedKeys.filter((k) => !resolvedRevisionKeys.has(k));
        const completedFields = totalFields - open.length;
        const remainingMinutes = open.reduce(
          (sum, k) => sum + minutesFor(questionsById.get(k.split("|")[0])),
          0
        );
        const percent = Math.max(
          0,
          Math.min(
            100,
            totalFields ? Math.round((completedFields / totalFields) * 100) : 0
          )
        );
        return { totalFields, completedFields, percent, remainingMinutes };
      }

      let totalFields = 0;
      let completedFields = 0;
      let remainingMinutes = 0;

      const getAnswer = (opts: {
        questionId: string;
        groupId?: string | null;
        instanceId?: string | null;
      }): OutgoingIntakeAnswer | null => {
        const { questionId } = opts;
        const groupId = opts.groupId ?? null;
        const instanceId = opts.instanceId ?? null;
        if (groupId && instanceId) {
          const inst = (groups[groupId] ?? []).find(
            (x) => x.instanceId === instanceId
          );
          return inst?.answers?.[questionId] ?? null;
        }
        return answersById[questionId] ?? null;
      };

      const walk = (
        n: OutgoingIntakeFormNode,
        ctx?: { groupId?: string | null; instanceId?: string | null }
      ) => {
        if (n.node_type === "question") {
          totalFields += 1;
          if (
            isAnswered(
              getAnswer({
                questionId: n.node_id,
                groupId: ctx?.groupId ?? null,
                instanceId: ctx?.instanceId ?? null
              })
            )
          ) {
            completedFields += 1;
          } else {
            remainingMinutes += minutesFor(n);
          }
          return;
        }

        if (n.node_type === "repeatable_group") {
          const groupId = n.node_id;
          const instances = groups[groupId] ?? [];
          for (const inst of instances) {
            for (const child of n.children ?? []) {
              walk(child, { groupId, instanceId: inst.instanceId });
            }
          }
          return;
        }

        for (const child of n.children ?? []) {
          walk(child, ctx);
        }
      };

      for (const n of intake.schema ?? []) {
        if (isNodeVisible(n, visibilityValues)) walk(n);
      }

      const percent = Math.max(
        0,
        Math.min(
          100,
          totalFields ? Math.round((completedFields / totalFields) * 100) : 0
        )
      );

      return { totalFields, completedFields, percent, remainingMinutes };
    })();
    // A returning user has already answered at least one field; show "Resume"
    // and base the time estimate on what's left rather than the whole intake.
    const isIntakeInProgress = fieldProgress.completedFields > 0;
    const isRevisionFlow = isRevisionRequested && requestedKeys.length > 0;
    // Same counting as the attorney's builder and the portal.
    const answeredIds = answeredIdsOf([
      ...Object.values(answersById),
      ...Object.values(groups).flatMap((items) =>
        items.flatMap((item) => Object.values(item.answers ?? {}))
      )
    ]);
    const prefilledIds = new Set(Object.keys(intake.prefill ?? {}));
    const welcomeOutline = isRevisionFlow
      ? []
      : sectionOutline(intake.schema ?? [], answeredIds, prefilledIds);
    const estimatedMinutes = Math.max(
      1,
      isRevisionFlow
        ? Math.ceil(fieldProgress.remainingMinutes)
        : intakeEstimate(intake.schema ?? [], answeredIds).minutes
    );
    const revisionItems = isRevisionFlow
      ? typeformScreens.flatMap((screen) => {
          if (screen.kind === "group-question") {
            const title =
              normalizeText(screen.node.title) ||
              normalizeText(screen.node.prompt);
            return [
              {
                key: screen.key,
                label: `${groupEntryLabel(screen.group)} ${
                  screen.entryIndex + 1
                }${title ? ` · ${title}` : ""}`,
                step: screen.stepIndex + 1
              }
            ];
          }
          if (screen.kind !== "question") return [];
          return [
            {
              key: screen.key,
              label:
                normalizeText(screen.node.title) ||
                normalizeText(screen.node.prompt) ||
                `Step ${screen.stepIndex + 1}`,
              step: screen.stepIndex + 1
            }
          ];
        })
      : [];
    const revisionFixCount = requestedKeys.length;
    const revisionSavedCount = isRevisionFlow
      ? Array.from(baselineAnswerByKeyRef.current.entries()).filter(
          ([key, ans]) =>
            !rejectedByKey.has(key) &&
            ((ans.files ?? []).length > 0 || hasMeaningfulValue(ans.value))
        ).length
      : 0;
    const attorneyFirstName =
      normalizeText(intake.attorney_name).split(/\s+/)[0] || "";
    const welcomeCtaLabel = isRevisionFlow
      ? tr("fix", { count: revisionFixCount })
      : isIntakeInProgress
      ? tr("resume")
      : tr("start");
    // On the welcome screen the note explains the ask. When the attorney has
    // requested changes, lead with the note they wrote when submitting the
    // review; otherwise fall back to the intake's attorney note / default.
    const welcomeNoteText = isRevisionRequested
      ? normalizeText(latestSubmission?.revision_request_note)
        ? latestSubmission?.revision_request_note
        : normalizeText(intake.attorney_note)
        ? intake.attorney_note
        : tr("defaultNote")
      : normalizeText(intake.attorney_note)
      ? intake.attorney_note
      : normalizeText(latestSubmission?.revision_request_note)
      ? latestSubmission?.revision_request_note
      : tr("defaultNote");
    const estimatedTimeLabel = isRevisionFlow
      ? `${tr("about", { count: estimatedMinutes })}${
          revisionSavedCount
            ? tr("othersSaved", { count: revisionSavedCount })
            : ""
        }`
      : isIntakeInProgress
      ? tr("left", { count: estimatedMinutes })
      : tr("takes", { count: estimatedMinutes });

    const revisionNoteText = normalizeText(
      latestSubmission?.revision_request_note
    );
    const revisionAttorneyName =
      normalizeText(intake.attorney_name) || tr("yourAttorney");
    const revisionReviewedAt = latestSubmission?.reviewed_at
      ? new Date(String(latestSubmission.reviewed_at))
      : null;
    const revisionWelcomeBody = isRevisionFlow ? (
      <div className="typeform-step-part typeform-step-part-2 mt-4 flex w-full max-w-140 flex-col gap-4 text-left">
        <p className="text-lg leading-snug text-white/80 md:text-xl">
          {tr("needsChanges", { count: revisionFixCount })}
        </p>
        {revisionNoteText ? (
          <div className="flex items-start gap-3 rounded-2xl border border-solid border-white/15 bg-white/10 px-4 py-3.5">
            <IntakeAvatar
              className="shrink-0"
              size="sm"
              image={intake.attorney_avatar_url ?? undefined}
              variant="neutral"
            >
              {revisionAttorneyName
                .split(/\s+/)
                .map((p) => p[0]?.toUpperCase())
                .join("")
                .slice(0, 2)}
            </IntakeAvatar>
            <div className="flex min-w-0 flex-col gap-1">
              <div className="flex flex-wrap items-baseline gap-x-2 text-base">
                <span className="font-semibold text-white">
                  {revisionAttorneyName}
                </span>
                <span className="text-white/60">{tr("attorney")}</span>
                {revisionReviewedAt &&
                !Number.isNaN(revisionReviewedAt.getTime()) ? (
                  <span className="text-white/50">
                    ·{" "}
                    {revisionReviewedAt.toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric"
                    })}
                  </span>
                ) : null}
              </div>
              <p className="whitespace-pre-wrap text-md leading-snug text-white/85">
                {revisionNoteText}
              </p>
            </div>
          </div>
        ) : null}
        {revisionItems.length ? (
          <ul className="flex flex-col divide-y divide-solid divide-white/10 rounded-2xl border border-solid border-white/15">
            {revisionItems.map((item) => (
              <li
                key={item.key}
                className="flex items-center gap-3 px-4 py-2.5 text-md"
              >
                <span
                  className="size-1.5 shrink-0 rounded-full bg-warn"
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate text-white">
                  {item.label}
                </span>
                <span className="shrink-0 text-base text-white/55">
                  Step {item.step}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    ) : null;

    const welcomeChips = (
      <div
        className="flex flex-wrap items-center gap-2 text-base text-white/75"
        data-testid="intake-welcome-chips"
      >
        <span className="inline-flex items-center gap-1.5 rounded-full border border-solid border-white/20 bg-white/10 px-3 py-1">
          <IntakeIcon className="text-caption" name="FeatherClock" />
          {estimatedTimeLabel}
        </span>
        {!isPersistenceDisabled ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-solid border-white/20 bg-white/10 px-3 py-1">
            <IntakeIcon className="text-caption" name="FeatherSave" />
            {tr("savesAuto")}
          </span>
        ) : null}
        {prefilledIds.size && !isRevisionFlow ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-solid border-white/20 bg-white/10 px-3 py-1">
            <IntakeIcon className="text-caption" name="FeatherCheck" />
            {tr("prefilledCount", { count: prefilledIds.size })}
          </span>
        ) : null}
      </div>
    );
    const welcomeOutlineList =
      welcomeOutline.length > 1 ? (
        <ol
          className="flex w-full flex-col overflow-hidden rounded-2xl border border-solid border-white/15 bg-white/[0.06] text-left"
          data-testid="intake-welcome-outline"
        >
          {welcomeOutline.map((row, i) => (
            <li
              key={row.id}
              className="flex items-center gap-3 border-0 border-t border-solid border-white/10 px-4 py-2.5 first:border-t-0"
            >
              <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-md bg-white/15 text-sm font-medium">
                {i + 1}
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-md font-medium text-white">
                  {row.title}
                </span>
                <span className="text-sm text-white/60">
                  {[
                    tr("outline.questions", { count: row.questions }),
                    row.prefilled
                      ? tr("outline.filled", { count: row.prefilled })
                      : null
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
              <span className="shrink-0 text-sm text-white/60">
                {row.minutes
                  ? tr("outline.minutes", { count: row.minutes })
                  : tr("outline.done")}
              </span>
            </li>
          ))}
        </ol>
      ) : null;

    // Returning users get an intermediate choice screen; new users advance
    // straight into the flow.
    const openResumeChoice = () => {
      if (layoutMode === "single_page" || isRevisionFlow) {
        // Single-page shows the whole form at once, with saved answers already
        // hydrated, so there is no per-step resume choice — just open the form.
        autosaveProgress();
        setStepTransitionDirection("forward");
        setActiveStepIndex(welcomeOffset);
        return;
      }
      if (isIntakeInProgress) {
        setResumeChoiceActive(true);
      } else {
        goToNextTypeformStep();
      }
    };
    const continueWhereLeftOff = () => {
      setResumeChoiceActive(false);
      autosaveProgress();
      setStepTransitionDirection("forward");
      setActiveStepIndex(resumeTargetStepIndex + welcomeOffset);
    };
    const startFromBeginning = () => {
      setResumeChoiceActive(false);
      setStepTransitionDirection("forward");
      setActiveStepIndex(welcomeOffset);
    };

    const isResponsivePreview = isPreview && previewViewport === "responsive";
    const isNaturalEmbeddedPreview =
      isPreview && embeddedPreview && !isResponsivePreview;
    // What the bottom-right control cluster is showing. Ask Casey parks to its
    // left, so the cluster's own markup and the launcher's offset both read this
    // rather than each restating when the cluster is there and how wide it is.
    const intakeControlsLayout: IntakeControlsLayout =
      isNaturalEmbeddedPreview || isWelcomeStep || forceSinglePage
        ? "hidden"
        : showSinglePageForm
        ? "settings-only"
        : "with-step-arrows";
    const rootClassName = isUrlPreview
      ? "relative flex h-screen w-full min-h-0 flex-col items-start overflow-hidden overflow-x-hidden fixed inset-0"
      : isNaturalEmbeddedPreview
      ? "relative flex w-full flex-col items-start"
      : isPreview
      ? "relative flex h-full w-full min-h-0 flex-col items-start overflow-hidden overflow-x-hidden"
      : "relative flex h-screen w-full min-h-0 flex-col items-start overflow-hidden overflow-x-hidden fixed inset-0";

    return (
      <IntakeScopedDiv
        className={rootClassName}
        dir={intakeLanguage.direction}
        lang={intakeLanguage.language}
        style={{
          backgroundColor: intakePageBg,
          color: intakeFgSolid,
          transition: "background-color 360ms ease, color 360ms ease"
        }}
        onKeyDown={handleTypeformKeyDown}
        styles={{
          ".public-intake-typeform-content [id^='node-'] > .border-y": {
            display: "none"
          },
          ".public-intake-typeform-content [id^='node-']": {
            border: "none",
            background: "transparent"
          },
          ".public-intake-typeform-content .grid": {
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            paddingTop: "24px",
            paddingBottom: "0"
          },
          ".public-intake-typeform-content .col-span-1, .public-intake-typeform-content .col-span-2":
            {
              width: "100%",
              paddingLeft: 0,
              paddingRight: 0
            },
          ".public-intake-typeform-content [class*='bg-fill-1']": {
            background: "transparent"
          },
          ".public-intake-typeform-content [class*='border-neutral-border']": {
            borderColor: `rgba(${intakeFgRgb}, 0.22)`
          },
          ".public-intake-typeform-content label[class*='group/']": {
            gap: 0
          },
          ".public-intake-typeform-content label[class*='group/'] > div": {
            height: "auto !important",
            minHeight: "58px",
            flexGrow: 0,
            flexShrink: 0,
            flexBasis: "auto",
            padding: "0 !important",
            background: "transparent !important",
            borderWidth: "0 !important",
            borderColor: "transparent !important",
            borderRadius: "0 !important",
            boxShadow: "none !important",
            outline: "none !important"
          },
          ".public-intake-typeform-content label[class*='group/'] > div > div":
            {
              padding: "0 !important"
            },
          ".public-intake-typeform-content div:has(> input:not([type='file']):not([type='checkbox']))":
            {
              background: "transparent !important",
              borderColor: "transparent !important",
              boxShadow: "none !important"
            },
          ".public-intake-typeform-content input:not([type='file']):not([type='checkbox']), .public-intake-typeform-content textarea":
            {
              width: "100%",
              minHeight: "58px",
              background: "transparent !important",
              borderWidth: "0 0 1px 0",
              borderStyle: "solid",
              borderColor: `rgba(${intakeFgRgb}, 0.28)`,
              borderRadius: "0 !important",
              color: `${intakeFgSolid} !important`,
              fontSize: "24px",
              lineHeight: "1.25",
              padding: "0 0 10px 0",
              boxShadow: "none !important",
              outline: "none"
            },
          ".public-intake-typeform-content input::placeholder, .public-intake-typeform-content textarea::placeholder":
            {
              color: `rgba(${intakeFgRgb}, 0.36)`,
              opacity: 1
            },
          // Read-only value display used in attorney review mode. Mirrors the
          // typeform input's underline + type scale so it reads identically.
          ".public-intake-typeform-content .typeform-review-value": {
            width: "100%",
            minHeight: "58px",
            borderWidth: "0 0 1px 0",
            borderStyle: "solid",
            borderColor: `rgba(${intakeFgRgb}, 0.28)`,
            color: intakeFgSolid,
            fontSize: "24px",
            lineHeight: "1.25",
            padding: "0 0 10px 0",
            textAlign: "left",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word"
          },
          ".public-intake-typeform-content .typeform-review-empty": {
            color: `rgba(${intakeFgRgb}, 0.4)`,
            fontStyle: "italic"
          },
          // Attorney review: recolor just the underline + value text (no box fill).
          ".public-intake-typeform-content .intake-review-accepted input:not([type='file']):not([type='checkbox']), .public-intake-typeform-content .intake-review-accepted textarea, .public-intake-typeform-content .intake-review-accepted .typeform-select-input, .public-intake-typeform-content .intake-review-accepted .typeform-review-value":
            {
              borderColor: "#22c55e !important",
              color: "#86efac !important"
            },
          ".public-intake-typeform-content .intake-review-rejected input:not([type='file']):not([type='checkbox']), .public-intake-typeform-content .intake-review-rejected textarea, .public-intake-typeform-content .intake-review-rejected .typeform-select-input, .public-intake-typeform-content .intake-review-rejected .typeform-review-value":
            {
              borderColor: "#f87171 !important",
              color: "#fca5a5 !important"
            },
          // Requested-change values get a red strikethrough on the text itself.
          ".public-intake-typeform-content .intake-review-rejected .typeform-review-value":
            {
              textDecorationLine: "line-through",
              textDecorationColor: "#f87171",
              textDecorationThickness: "2px"
            },
          ".public-intake-typeform-content input[type='date']::-webkit-calendar-picker-indicator":
            {
              filter: isDarkIntakeBg ? "invert(1) brightness(1.8)" : "none",
              opacity: 0.92
            },
          ".public-intake-typeform-content textarea": {
            minHeight: "120px",
            resize: "vertical"
          },
          ".public-intake-typeform-content input:focus, .public-intake-typeform-content textarea:focus":
            {
              borderColor: `rgba(${intakeFgRgb}, 0.75)`,
              boxShadow: "none",
              outline: "none"
            },
          ".public-intake-typeform-content [class*='text-body-bold']": {
            color: `rgba(${intakeFgRgb}, 0.88)`,
            fontSize: "17px",
            lineHeight: "1.35"
          },
          ".public-intake-typeform-content [class*='text-caption']": {
            color: `rgba(${intakeFgRgb}, 0.55)`
          },
          ".public-intake-typeform-content .text-subtext-color": {
            color: `rgba(${intakeFgRgb}, 0.56) !important`
          },
          ".public-intake-typeform-content [class*='text-success']": {
            color: `rgba(${intakeFgRgb}, 0.84) !important`
          },
          ".public-intake-typeform-content [role='combobox']": {
            minHeight: "48px",
            borderColor: `rgba(${intakeFgRgb}, 0.22)`,
            background: `rgba(${intakeFgRgb}, 0.07)`,
            color: intakeFgSolid
          },
          ".public-intake-typeform-content .typeform-select-input": {
            background: "transparent"
          },
          ".public-intake-typeform-content .typeform-select-input::placeholder":
            {
              color: `rgba(${intakeFgRgb}, 0.35)`
            },
          ".public-intake-typeform-content [class*='FileUpload'], .public-intake-typeform-content [class*='file-upload']":
            {
              borderRadius: "16px",
              color: `rgba(${intakeFgRgb}, 0.62)`
            },
          ".public-intake-typeform-content table": {
            background: `rgba(${intakeFgRgb}, 0.08)`,
            borderRadius: "16px",
            overflow: "hidden"
          },
          ".public-intake-typeform-content [class*='bg-default-background']": {
            background: `rgba(${intakeFgRgb}, 0.07)`
          },
          ".public-intake-typeform-content [class*='hover:bg-fill-1']:hover": {
            background: `rgba(${intakeFgRgb}, 0.14)`
          },
          ".public-intake-typeform-content button, .public-intake-typeform-content label":
            {
              color: intakeFgSolid
            },
          ".public-intake-typeform-content [data-disabled], .public-intake-typeform-content [disabled]":
            {
              opacity: 0.65
            },
          ".public-intake-typeform-content [class*='bg-error-']": {
            color: "#111827"
          },
          // Attorney/client review comment cards render on light surfaces, so
          // restore dark, legible text that the typeform theme would otherwise
          // recolor to the (white) intake foreground.
          ".public-intake-typeform-content .public-intake-review-comments": {
            transformOrigin: "top left",
            animation:
              "intakeReviewCommentsIn 260ms cubic-bezier(0.16, 1, 0.3, 1) both"
          },
          ".public-intake-typeform-content .public-intake-review-comments .intake-comment-card":
            {
              position: "relative",
              isolation: "isolate",
              overflow: "hidden",
              background:
                "linear-gradient(135deg, rgba(255, 255, 255, 0.82) 0%, rgba(255, 255, 255, 0.58) 46%, rgba(255, 255, 255, 0.7) 100%) !important",
              backdropFilter: "blur(20px) saturate(1.7)",
              WebkitBackdropFilter: "blur(20px) saturate(1.7)",
              border: "1px solid rgba(255, 255, 255, 0.28)",
              boxShadow:
                "0 20px 44px rgba(15, 23, 42, 0.24), 0 3px 8px rgba(15, 23, 42, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -12px 26px rgba(255, 255, 255, 0.2)",
              transformOrigin: "top left",
              animation:
                "intakeReviewCommentCardIn 320ms cubic-bezier(0.16, 1, 0.3, 1) both"
            },
          // Glossy top sheen for the chrome/glass look; sits below the content.
          ".public-intake-typeform-content .public-intake-review-comments .intake-comment-card::before":
            {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "52%",
              background:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.12) 55%, rgba(255, 255, 255, 0) 100%)",
              pointerEvents: "none",
              zIndex: 0
            },
          // Soft diagonal glare streak in the top-left, kept subtle.
          ".public-intake-typeform-content .public-intake-review-comments .intake-comment-card::after":
            {
              content: '""',
              position: "absolute",
              top: "-40%",
              left: "-10%",
              width: "55%",
              height: "160%",
              background:
                "linear-gradient(115deg, rgba(255, 255, 255, 0.35) 0%, rgba(255, 255, 255, 0) 60%)",
              transform: "rotate(8deg)",
              pointerEvents: "none",
              zIndex: 0
            },
          ".public-intake-typeform-content .public-intake-review-comments .intake-comment-card > *":
            {
              position: "relative",
              zIndex: 1
            },
          ".public-intake-typeform-content .public-intake-review-comments .intake-comment-name":
            {
              color: "#1f2937 !important"
            },
          ".public-intake-typeform-content .public-intake-review-comments .intake-comment-meta":
            {
              color: "#6b7280 !important"
            },
          ".public-intake-typeform-content .public-intake-review-comments .intake-comment-body-client":
            {
              color: "#1f2937 !important"
            },
          ".public-intake-typeform-content .public-intake-review-comments .intake-comment-body-attorney":
            {
              color: "#1f2937 !important"
            },
          // The superseded answer on a change request, called out in red so it
          // reads as the thing being replaced.
          ".public-intake-typeform-content .public-intake-review-comments .intake-comment-submitted-value":
            {
              color: "#dc2626 !important"
            },
          ".public-intake-typeform-content .public-intake-review-comments .intake-comment-textarea":
            {
              minHeight: "60px",
              fontSize: "15px !important",
              lineHeight: "1.45 !important",
              color: "#1f2937 !important",
              background: "transparent !important",
              borderWidth: "0 !important",
              borderRadius: "0 !important",
              padding: "0 !important",
              boxShadow: "none !important",
              outline: "none !important"
            },
          ".public-intake-typeform-content .public-intake-review-comments .intake-comment-textarea:focus":
            {
              borderWidth: "0 !important",
              boxShadow: "none !important",
              outline: "none !important"
            },
          ".public-intake-typeform-content .public-intake-review-comments .intake-comment-textarea::placeholder":
            {
              color: "#9ca3af !important",
              opacity: 1
            },
          // The comment cards sit on a light surface, so the "Edit" affordance
          // needs dark text/border that the typeform button theme would
          // otherwise recolor to the (white) intake foreground.
          ".public-intake-typeform-content .public-intake-review-comments .intake-comment-edit-btn":
            {
              color: "#1f2937 !important",
              background: "rgba(255, 255, 255, 0.85) !important",
              borderColor: "rgba(31, 41, 55, 0.28) !important"
            },
          ".public-intake-typeform-content .public-intake-review-comments .intake-comment-edit-btn:hover":
            {
              background: "#ffffff !important",
              borderColor: "rgba(31, 41, 55, 0.5) !important"
            },
          ".public-intake-typeform-content .public-intake-review-comments .intake-comment-cancel-btn":
            {
              color: "#4b5563 !important"
            },
          "@keyframes intakeReviewCommentsIn": {
            "0%": {
              opacity: 0,
              transform: "translateY(-6px) scale(0.985)"
            },
            "100%": {
              opacity: 1,
              transform: "translateY(0) scale(1)"
            }
          },
          "@keyframes intakeReviewCommentCardIn": {
            "0%": {
              opacity: 0,
              transform: "translateY(-8px)"
            },
            "100%": {
              opacity: 1,
              transform: "translateY(0)"
            }
          },
          // Uploaded-document cards and their inline preview live on a light
          // surface, so restore their intended slate/status colors and light
          // backgrounds that the typeform-content theme would otherwise recolor.
          ".public-intake-doc-section .text-fg": {
            color: "#0f172a !important"
          },
          ".public-intake-doc-section .text-fg-2": {
            color: "#475569 !important"
          },
          ".public-intake-doc-section .text-fg-3": {
            color: "#64748b !important"
          },
          ".public-intake-doc-section .text-default-font": {
            color: "#111827 !important"
          },
          ".public-intake-doc-section .text-subtext-color": {
            color: "#64748b !important"
          },
          ".public-intake-doc-section .text-success-600": {
            color: "#16a34a !important"
          },
          ".public-intake-doc-section .text-error-600": {
            color: "#dc2626 !important"
          },
          ".public-intake-doc-section .text-warning-700": {
            color: "#a16207 !important"
          },
          ".public-intake-doc-section [class*='bg-fill-1']": {
            background: "#f8fafc !important"
          },
          ".public-intake-doc-section [class*='bg-default-background']": {
            background: "#ffffff !important"
          },
          ".public-intake-doc-section [class*='border-neutral-border']": {
            borderColor: "#e2e8f0 !important"
          },
          "@keyframes loadingCaseBlinkFadeOut": {
            "0%": {
              opacity: 1
            },
            "45%": {
              opacity: 1
            },
            "100%": {
              opacity: 0
            }
          },
          ".loading-caseblink-fade-out": {
            animation:
              "loadingCaseBlinkFadeOut 900ms cubic-bezier(0.22, 1, 0.36, 1) both"
          },
          "@keyframes evidencePreviewGenieIn": {
            "0%": {
              opacity: 0,
              transform: "translateX(44px) scaleX(0.08) scaleY(0.14)",
              clipPath: "inset(45% 0 45% 96% round 12px)",
              filter: "blur(2px)"
            },
            "68%": {
              opacity: 1,
              transform: "translateX(-2px) scaleX(1.015) scaleY(1.01)",
              clipPath: "inset(-240px -240px -240px -240px round 16px)",
              filter: "blur(0)"
            },
            "100%": {
              opacity: 1,
              transform: "translateX(0) scaleX(1) scaleY(1)",
              clipPath: "inset(-240px -240px -240px -240px round 16px)",
              filter: "blur(0)"
            }
          },
          "@keyframes evidencePreviewGenieOut": {
            "0%": {
              opacity: 1,
              transform: "translateX(0) scaleX(1) scaleY(1)",
              clipPath: "inset(-240px -240px -240px -240px round 16px)",
              filter: "blur(0)"
            },
            "100%": {
              opacity: 0,
              transform: "translateX(44px) scaleX(0.08) scaleY(0.14)",
              clipPath: "inset(45% 0 45% 96% round 12px)",
              filter: "blur(2px)"
            }
          },
          ".evidence-preview-genie": {
            transformOrigin: "100% var(--evidence-preview-origin-y)",
            willChange: "transform, opacity, clip-path, filter"
          },
          '.evidence-preview-genie[data-state="open"]': {
            animation:
              "evidencePreviewGenieIn 260ms cubic-bezier(0.22, 1, 0.36, 1) both"
          },
          '.evidence-preview-genie[data-state="closed"]': {
            animation:
              "evidencePreviewGenieOut 220ms cubic-bezier(0.64, 0, 0.78, 0) both"
          },
          "@keyframes uploadedEvidenceInsert": {
            "0%": {
              opacity: 0,
              transform: "translateY(-18px) scale(0.96)",
              boxShadow: "0 0 0 rgba(15, 23, 42, 0)"
            },
            "55%": {
              opacity: 1,
              transform: "translateY(3px) scale(1.01)",
              boxShadow: "0 18px 42px rgba(15, 23, 42, 0.12)"
            },
            "100%": {
              opacity: 1,
              transform: "translateY(0) scale(1)",
              boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05)"
            }
          },
          ".uploaded-evidence-insert": {
            transformOrigin: "50% 0",
            animation:
              "uploadedEvidenceInsert 520ms cubic-bezier(0.22, 1, 0.36, 1) both"
          },
          "@keyframes uploadedEvidenceFadeAway": {
            "0%": {
              opacity: 1,
              transform: "translateX(0) scale(1)",
              maxHeight: "96px"
            },
            "70%": {
              opacity: 0,
              transform: "translateX(16px) scale(0.98)",
              maxHeight: "96px"
            },
            "100%": {
              opacity: 0,
              transform: "translateX(16px) scale(0.98)",
              maxHeight: 0,
              marginTop: 0,
              marginBottom: 0,
              paddingTop: 0,
              paddingBottom: 0
            }
          },
          ".uploaded-evidence-fade-away": {
            pointerEvents: "none",
            overflow: "hidden",
            transformOrigin: "50% 50%",
            animation: `uploadedEvidenceFadeAway ${INTAKE_FILE_DELETE_FADE_MS}ms cubic-bezier(0.4, 0, 0.2, 1) forwards`
          },
          "@media (prefers-reduced-motion: reduce)": {
            '.evidence-preview-genie[data-state="open"], .evidence-preview-genie[data-state="closed"], .uploaded-evidence-insert, .uploaded-evidence-fade-away':
              {
                animation: "none"
              }
          }
        }}
      >
        <style>
          {`
            [data-sonner-toaster][data-x-position="right"] {
              --offset-right: 24px !important;
              right: 24px !important;
            }
          `}
        </style>
        {showLoadingFadeOverlay ? (
          <div className="loading-caseblink-fade-out pointer-events-none absolute inset-0 z-60 flex items-center justify-center bg-default-background">
            <LoadingState size="xl" />
          </div>
        ) : null}
        {/* Page-colored scrim behind the pinned logo so content scrolling up
            fades into the background before it reaches (and overlaps) the logo,
            instead of showing through it. Anchored to the top-left and masked
            so it fades out before the right-side uploaded-files rail. */}
        {showWhitelabelLogo &&
        !isPreviewTopBarShown &&
        !isLogoInlineWithContent ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-0 top-0 z-10 h-26 w-120 max-w-full mobile:h-21 mobile:w-85"
            style={{
              background: `linear-gradient(to bottom, rgba(${intakePageBgRgb}, 1) 0%, rgba(${intakePageBgRgb}, 1) 58%, rgba(${intakePageBgRgb}, 0) 100%)`,
              WebkitMaskImage:
                "linear-gradient(to right, rgba(0,0,0,1) 60%, rgba(0,0,0,0) 100%)",
              maskImage:
                "linear-gradient(to right, rgba(0,0,0,1) 60%, rgba(0,0,0,0) 100%)"
            }}
          />
        ) : null}
        {/* Firm logo, top-left. The dedicated top bar (below) already renders it
            for non-welcome preview steps, so this overlay covers the cases the
            bar doesn't: the welcome screen and the live client view. */}
        {showWhitelabelLogo &&
        !isPreviewTopBarShown &&
        !isLogoInlineWithContent ? (
          <div className="pointer-events-none absolute left-6 top-5 z-20 flex items-center mobile:left-4 mobile:top-4">
            <img
              src={lawFirmLogoUrl}
              alt={lawFirmName}
              className={[
                "h-14 max-h-14 max-w-80 shrink-0 object-contain object-left transition-opacity duration-300 mobile:h-11 mobile:max-h-11 mobile:max-w-55",
                showResolvedFirmLogo ? "opacity-100" : "opacity-0"
              ].join(" ")}
            />
          </div>
        ) : null}
        {!isPreview ? (
          <IntakeLanguageBar
            state={intakeLanguage}
            showHint={isWelcomeStep}
            fgRgb={intakeFgRgb}
            belowBanner={isFirmViewer}
          />
        ) : null}
        {isPreviewTopBarShown && (
          <div
            className="flex h-11.5 w-full shrink-0 items-center justify-between border-b border-solid border-neutral-border bg-white px-4 py-0 shadow-sm"
            style={
              showWhitelabelLogo
                ? {
                    backgroundColor: intakePageBg,
                    borderColor: `rgba(${intakeFgRgb}, 0.16)`,
                    transition:
                      "background-color 360ms ease, border-color 360ms ease"
                  }
                : undefined
            }
          >
            {showWhitelabelLogo ? (
              <img
                src={lawFirmLogoUrl}
                alt={lawFirmName}
                className={[
                  "h-7 max-h-7 max-w-45 shrink-0 object-contain object-left transition-opacity duration-300",
                  showResolvedFirmLogo ? "opacity-100" : "opacity-0"
                ].join(" ")}
              />
            ) : (
              <div className="flex items-center gap-2">
                <BrandLogo className="h-6 max-w-35 shrink-0" />
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-body font-body text-subtext-color shrink-0">
                    ×
                  </span>
                  <span className="text-body-bold font-body-bold text-default-font truncate">
                    {lawFirmName || tr("yourFirm")}
                  </span>
                </div>
              </div>
            )}
            {isPreview ? (
              <div className="flex min-w-0 items-center justify-end">
                <StatusBadge tone="accent">
                  <Info />
                  Preview
                </StatusBadge>
              </div>
            ) : null}
          </div>
        )}
        {isFirmViewer ? (
          <div
            className="relative z-20 flex w-full shrink-0 items-center justify-center gap-2 border-0 border-b border-solid border-warn-line bg-warn-soft px-4 py-2 text-center text-base text-warn"
            role="status"
            data-testid="intake-firm-preview"
          >
            <IntakeIcon name="FeatherEye" />
            <span>
              You&apos;re previewing{" "}
              {firstName ? `${firstName}'s` : "the client's"} request as
              they&apos;ll see it. Nothing here is saved, and it isn&apos;t
              counted as {firstName || "the client"} opening it.
            </span>
          </div>
        ) : null}
        {!reviewMode && !isWelcomeStep && !showSinglePageForm ? (
          <div
            className="relative h-0.75 w-full shrink-0"
            style={{ backgroundColor: `rgba(${intakeFgRgb}, 0.15)` }}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={typeformProgressValue}
            aria-label={progressLine || undefined}
          >
            <div
              className="h-full transition-[width] duration-200 ease-out"
              style={{
                width: `${typeformProgressValue}%`,
                backgroundColor: intakeFgSolid
              }}
            />
          </div>
        ) : null}

        <div
          className={
            isNaturalEmbeddedPreview
              ? "flex w-full min-w-0 flex-col items-center"
              : "flex min-h-0 w-full min-w-0 flex-1 items-stretch justify-center overflow-hidden"
          }
        >
          <div
            className={
              isNaturalEmbeddedPreview
                ? "flex w-full min-w-0 flex-col items-center self-stretch bg-transparent"
                : "flex min-h-0 min-w-0 flex-1 flex-col items-center self-stretch bg-transparent overflow-hidden"
            }
          >
            {isWelcomeStep && resumeChoiceActive ? (
              <IntakeScopedDiv
                key={`resume-choice-${resumeChoiceActive}`}
                className="relative flex min-h-0 w-full max-w-full flex-1 flex-col items-center overflow-y-auto overflow-x-hidden px-6 py-0 [justify-content:safe_center] mobile:px-4 mobile:pb-10 mobile:pt-16"
                styles={{
                  ".typeform-step-part": {
                    opacity: 0,
                    willChange: "opacity, transform",
                    animation:
                      "typeformPartInFromBelow 760ms cubic-bezier(0.22, 1, 0.36, 1) both"
                  },
                  ".typeform-step-part-1": { animationDelay: "120ms" },
                  ".typeform-step-part-2": { animationDelay: "210ms" },
                  ".typeform-step-part-3": { animationDelay: "300ms" },
                  "@keyframes typeformPartInFromBelow": {
                    "0%": { opacity: 0, transform: "translateY(56px)" },
                    "100%": { opacity: 1, transform: "translateY(0)" }
                  },
                  "@media (prefers-reduced-motion: reduce)": {
                    ".typeform-step-part": {
                      opacity: 1,
                      animation: "none",
                      transform: "none"
                    }
                  }
                }}
              >
                <div
                  className="flex min-h-[52vh] max-w-180 flex-col items-center justify-center gap-4 text-center"
                  style={{ color: intakeFgSolid }}
                >
                  <p
                    className="typeform-step-part typeform-step-part-1 font-medium"
                    style={{
                      fontSize: isMdUp ? "28px" : "24px",
                      lineHeight: 1.2
                    }}
                  >
                    {firstName
                      ? tr("welcomeBackName", { name: firstName })
                      : tr("welcomeBack")}
                  </p>
                  <p
                    className="typeform-step-part typeform-step-part-2 max-w-140"
                    style={{
                      fontSize: isMdUp ? "20px" : "17px",
                      lineHeight: 1.35,
                      color: `rgba(${intakeFgRgb}, 0.72)`
                    }}
                  >
                    {tr("resumeBody")}
                  </p>
                  <div className="typeform-step-part typeform-step-part-3 mt-4 flex w-full max-w-105 flex-col items-center gap-3">
                    <Button
                      variant="tonal"
                      className="w-full min-h-12 rounded-xl bg-white px-6 py-3.5 text-base font-medium leading-none text-[#111827]! shadow-[0_10px_24px_rgba(0,0,0,0.20)] transition-transform duration-150 ease-out hover:-translate-y-0.5 hover:bg-white active:translate-y-0 active:bg-white **:text-[#111827]!"
                      onClick={continueWhereLeftOff}
                    >
                      {tr("continueWhereLeft")}
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full min-h-12 rounded-xl border border-solid bg-(--resume-secondary-bg) px-6 py-3.5 text-base font-medium leading-none transition-colors duration-150 ease-out hover:bg-(--resume-secondary-hover-bg) hover:border-(--resume-secondary-hover-border)! active:bg-(--resume-secondary-hover-bg) **:text-inherit!"
                      style={
                        {
                          borderColor: `rgba(${intakeFgRgb}, 0.28)`,
                          color: intakeFgSolid,
                          "--resume-secondary-bg": `rgba(${intakeFgRgb}, 0.04)`,
                          "--resume-secondary-hover-bg": `rgba(${intakeFgRgb}, 0.1)`,
                          "--resume-secondary-hover-border": `rgba(${intakeFgRgb}, 0.5)`
                        } as React.CSSProperties
                      }
                      onClick={startFromBeginning}
                    >
                      {tr("reviewFromStart")}
                    </Button>
                  </div>
                </div>
              </IntakeScopedDiv>
            ) : isWelcomeStep && hasWelcomeVideo ? (
              <IntakeScopedDiv
                className="min-h-0 w-full flex-1"
                styles={{
                  ".typeform-step-part": {
                    opacity: 0,
                    willChange: "opacity, transform",
                    animation:
                      "typeformPartInFromBelow 760ms cubic-bezier(0.22, 1, 0.36, 1) both"
                  },
                  ".typeform-step-part-1": { animationDelay: "170ms" },
                  ".typeform-step-part-2": { animationDelay: "260ms" },
                  ".typeform-step-part-3": { animationDelay: "350ms" },
                  ".typeform-step-part-4": { animationDelay: "440ms" },
                  "@keyframes typeformPartInFromBelow": {
                    "0%": { opacity: 0, transform: "translateY(56px)" },
                    "100%": { opacity: 1, transform: "translateY(0)" }
                  },
                  "@media (prefers-reduced-motion: reduce)": {
                    ".typeform-step-part": {
                      opacity: 1,
                      animation: "none",
                      transform: "none"
                    }
                  }
                }}
              >
                <div className="flex h-full min-h-auto w-full flex-col-reverse items-stretch text-white min-[62em]:min-h-140 min-[62em]:flex-row">
                  {/* Left — title / message / Start / time */}
                  <div className="flex w-full flex-none flex-col items-start justify-center px-6 py-8 md:px-10 min-[62em]:flex-1 min-[62em]:px-14 min-[62em]:py-10">
                    <div className="w-full max-w-full min-[62em]:max-w-130">
                      <p
                        className="typeform-step-part typeform-step-part-1 text-left font-medium"
                        style={{
                          fontSize: isMdUp ? "28px" : "24px",
                          lineHeight: 1.2
                        }}
                      >
                        {activeStepTitle}
                      </p>
                      {revisionWelcomeBody ?? (
                        <p
                          className="typeform-step-part typeform-step-part-2 mt-4 whitespace-pre-wrap text-left"
                          style={{
                            fontSize: isMdUp ? "20px" : "17px",
                            lineHeight: 1.35,
                            color: "rgba(255,255,255,0.78)"
                          }}
                        >
                          {welcomeNoteText}
                        </p>
                      )}
                      <Button
                        variant="tonal"
                        className="typeform-step-part typeform-step-part-3 mt-6 min-h-10 rounded-md bg-white px-4 py-2 text-base font-medium leading-none text-[#111827]! shadow-[0_8px_18px_rgba(0,0,0,0.18)] hover:bg-white/90 active:bg-white **:text-[#111827]!"
                        onClick={openResumeChoice}
                      >
                        {welcomeCtaLabel}
                      </Button>
                      <div className="typeform-step-part typeform-step-part-4 mt-4">
                        {welcomeChips}
                      </div>
                    </div>
                  </div>

                  {/* Right — full-height vertical welcome video */}
                  <div className="typeform-step-part typeform-step-part-2 relative h-[45vh] min-h-80 w-full shrink-0 self-stretch overflow-hidden bg-black min-[62em]:h-auto min-[62em]:min-h-140 min-[62em]:w-1/2">
                    <WelcomeVideoPlayer
                      src={welcomeVideoUrl}
                      trim={intake?.welcome_video_trim ?? null}
                    />
                  </div>
                </div>
              </IntakeScopedDiv>
            ) : showSinglePageForm ? (
              <div className="relative flex min-h-0 w-full max-w-full flex-1 flex-col items-center justify-start overflow-y-auto overflow-x-hidden px-8 py-10 mobile:px-6 mobile:py-8">
                {/* Sits in the page's top-left corner rather than with the
                    centered question column, and scrolls away with the form.
                    The negative offsets cancel this container's padding so the
                    logo lands where the pinned overlay does in the guided flow,
                    keeping it from jumping when the layout is toggled. */}
                {isLogoInlineWithContent ? (
                  <div className="-ml-2 -mt-5 mb-10 self-start mobile:-mt-4 mobile:mb-8">
                    <img
                      src={lawFirmLogoUrl}
                      alt={lawFirmName}
                      className={[
                        "h-14 max-h-14 max-w-80 shrink-0 object-contain object-left transition-opacity duration-300 mobile:h-11 mobile:max-h-11 mobile:max-w-55",
                        showResolvedFirmLogo ? "opacity-100" : "opacity-0"
                      ].join(" ")}
                    />
                  </div>
                ) : null}
                {renderSinglePageForm()}
              </div>
            ) : (
              <div
                ref={contentScrollRef}
                className={
                  isWelcomeStep
                    ? "relative flex min-h-0 w-full max-w-full flex-1 flex-col items-center overflow-y-auto overflow-x-hidden px-6 py-0 [justify-content:safe_center] mobile:px-4 mobile:pb-10 mobile:pt-16"
                    : isNaturalEmbeddedPreview
                    ? "relative flex w-full flex-col items-center justify-center gap-0 px-6 py-10"
                    : shouldShowDocumentRail
                    ? "relative flex min-h-0 w-full max-w-full flex-1 flex-col items-center justify-start gap-0 overflow-y-auto overflow-x-hidden px-8 py-10 pb-24 lg:justify-center lg:pb-10 mobile:px-6 mobile:py-8 mobile:pb-24"
                    : "relative flex min-h-0 w-full max-w-full flex-1 flex-col items-center justify-start gap-0 overflow-y-auto overflow-x-hidden px-8 py-10 mobile:px-6 mobile:py-8"
                }
              >
                <div
                  className={cn(
                    "my-auto w-full max-w-180 px-1 transition-[translate] duration-420 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none md:px-0",
                    (shouldShowDocumentRail || isReviewFilePreviewOpen) &&
                      "min-[62em]:-translate-x-35 xl:-translate-x-40"
                  )}
                >
                  <IntakeScopedDiv
                    key={`typeform-step-${activeStepIndex}`}
                    data-transition-direction={stepTransitionDirection}
                    className="w-full"
                    styles={{
                      willChange: "opacity, transform, filter",
                      // Review moves on by itself after each decision, so the
                      // attorney gets a short, close glide rather than the
                      // client's deliberate full-height reveal.
                      animation: reviewMode
                        ? "typeformStepInFromBelowSoft 480ms cubic-bezier(0.22, 1, 0.36, 1) both"
                        : "typeformStepInFromBelow 1000ms cubic-bezier(0.22, 1, 0.36, 1) both",
                      '&[data-transition-direction="backward"]': {
                        animation: reviewMode
                          ? "typeformStepInFromAboveSoft 480ms cubic-bezier(0.22, 1, 0.36, 1) both"
                          : "typeformStepInFromAbove 1000ms cubic-bezier(0.22, 1, 0.36, 1) both"
                      },
                      "@keyframes typeformStepInFromBelowSoft": {
                        "0%": {
                          opacity: 0,
                          transform: "translateY(48px)"
                        },
                        "100%": {
                          opacity: 1,
                          transform: "translateY(0)"
                        }
                      },
                      "@keyframes typeformStepInFromAboveSoft": {
                        "0%": {
                          opacity: 0,
                          transform: "translateY(-48px)"
                        },
                        "100%": {
                          opacity: 1,
                          transform: "translateY(0)"
                        }
                      },
                      "@keyframes typeformStepInFromBelow": {
                        "0%": {
                          opacity: 0,
                          transform: "translateY(42vh)",
                          filter: "blur(1px)"
                        },
                        "12%": {
                          opacity: 0,
                          transform: "translateY(42vh)",
                          filter: "blur(1px)"
                        },
                        "82%": {
                          opacity: 1,
                          transform: "translateY(-6px)",
                          filter: "blur(0)"
                        },
                        "100%": {
                          opacity: 1,
                          transform: "translateY(0)",
                          filter: "blur(0)"
                        }
                      },
                      "@keyframes typeformStepInFromAbove": {
                        "0%": {
                          opacity: 0,
                          transform: "translateY(-42vh)",
                          filter: "blur(1px)"
                        },
                        "12%": {
                          opacity: 0,
                          transform: "translateY(-42vh)",
                          filter: "blur(1px)"
                        },
                        "82%": {
                          opacity: 1,
                          transform: "translateY(6px)",
                          filter: "blur(0)"
                        },
                        "100%": {
                          opacity: 1,
                          transform: "translateY(0)",
                          filter: "blur(0)"
                        }
                      },
                      ".typeform-step-part": {
                        opacity: 0,
                        willChange: "opacity, transform",
                        animation: `typeformPartInFromBelow ${
                          reviewMode ? 420 : 760
                        }ms cubic-bezier(0.22, 1, 0.36, 1) both`
                      },
                      '&[data-transition-direction="backward"] .typeform-step-part':
                        {
                          animationName: "typeformPartInFromAbove"
                        },
                      ".typeform-step-part-1": {
                        animationDelay: reviewMode ? "60ms" : "170ms"
                      },
                      ".typeform-step-part-2": {
                        animationDelay: reviewMode ? "110ms" : "260ms"
                      },
                      ".typeform-step-part-3": {
                        animationDelay: reviewMode ? "160ms" : "350ms"
                      },
                      ".typeform-step-part-4": {
                        animationDelay: reviewMode ? "210ms" : "440ms"
                      },
                      ".public-intake-typeform-content .grid > *, .public-intake-typeform-content > [id^='node-'], .public-intake-typeform-content > div:not(.typeform-step-part)":
                        {
                          opacity: 0,
                          willChange: "opacity, transform",
                          animation: `typeformPartInFromBelow ${
                            reviewMode ? 420 : 760
                          }ms cubic-bezier(0.22, 1, 0.36, 1) both`,
                          animationDelay: reviewMode ? "160ms" : "350ms"
                        },
                      '&[data-transition-direction="backward"] .public-intake-typeform-content .grid > *, &[data-transition-direction="backward"] .public-intake-typeform-content > [id^="node-"], &[data-transition-direction="backward"] .public-intake-typeform-content > div:not(.typeform-step-part)':
                        {
                          animationName: "typeformPartInFromAbove"
                        },
                      ".public-intake-typeform-content .grid > *:nth-of-type(2)":
                        {
                          animationDelay: reviewMode ? "205ms" : "430ms"
                        },
                      ".public-intake-typeform-content .grid > *:nth-of-type(3)":
                        {
                          animationDelay: reviewMode ? "250ms" : "510ms"
                        },
                      ".public-intake-typeform-content .grid > *:nth-of-type(4)":
                        {
                          animationDelay: reviewMode ? "295ms" : "590ms"
                        },
                      "@keyframes typeformPartInFromBelow": {
                        "0%": {
                          opacity: 0,
                          transform: "translateY(56px)"
                        },
                        "100%": {
                          opacity: 1,
                          transform: "translateY(0)"
                        }
                      },
                      "@keyframes typeformPartInFromAbove": {
                        "0%": {
                          opacity: 0,
                          transform: "translateY(-56px)"
                        },
                        "100%": {
                          opacity: 1,
                          transform: "translateY(0)"
                        }
                      },
                      "@media (prefers-reduced-motion: reduce)": {
                        animation: "none",
                        '&[data-transition-direction="backward"]': {
                          animation: "none"
                        },
                        ".typeform-step-part": {
                          opacity: 1,
                          animation: "none",
                          transform: "none"
                        }
                      }
                    }}
                  >
                    {isWelcomeStep ? (
                      <div
                        className={[
                          "mx-auto flex min-h-[52vh] w-full max-w-160 flex-col justify-center gap-5 text-left text-white",
                          isLogoOverlayShown ? "pt-16 md:pt-20" : ""
                        ].join(" ")}
                        data-testid="intake-welcome"
                      >
                        {fromPortal ? (
                          <Link
                            to={CLIENT_PORTAL_PATH}
                            className="typeform-step-part typeform-step-part-1 inline-flex w-fit items-center gap-1 text-md font-medium text-white/70 hover:text-white"
                          >
                            <IntakeIcon name="FeatherArrowLeft" />
                            {tr("backToPortal")}
                          </Link>
                        ) : null}
                        {!showWhitelabelLogo ? (
                          <p className="typeform-step-part typeform-step-part-1 text-base font-semibold uppercase tracking-[0.06em] text-white/60">
                            {lawFirmName}
                          </p>
                        ) : null}
                        <h1 className="typeform-step-part typeform-step-part-1 text-2xl font-medium leading-[1.2] md:text-[30px]">
                          {activeStepTitle}
                        </h1>
                        {revisionWelcomeBody ?? (
                          <p className="typeform-step-part typeform-step-part-2 whitespace-pre-wrap text-lg leading-[1.4] text-white/80 md:text-xl">
                            {welcomeNoteText}
                          </p>
                        )}
                        <div className="typeform-step-part typeform-step-part-2">
                          {welcomeChips}
                        </div>
                        {welcomeOutlineList ? (
                          <div className="typeform-step-part typeform-step-part-3">
                            {welcomeOutlineList}
                          </div>
                        ) : null}
                        <div className="typeform-step-part typeform-step-part-4">
                          <Button
                            variant="tonal"
                            className="min-h-11 rounded-lg bg-white px-5 py-2 text-base font-medium leading-none text-[#111827]! shadow-[0_8px_18px_rgba(0,0,0,0.18)] hover:bg-white/90 active:bg-white **:text-[#111827]!"
                            onClick={openResumeChoice}
                          >
                            {welcomeCtaLabel}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={cn(
                          "flex flex-col gap-5",
                          isLogoOverlayShown && "pt-16 md:pt-19",
                          isLogoOverlayShown &&
                            !(finalReviewScreen || reviewMode) &&
                            "min-[62em]:pt-0"
                        )}
                      >
                        {progressLine && !reviewMode ? (
                          <p
                            className="-mb-2 text-base font-medium text-white/60"
                            data-testid="intake-progress"
                          >
                            {progressLine}
                          </p>
                        ) : null}
                        <div className="typeform-step-part typeform-step-part-1 flex items-start gap-3">
                          {activeScreen &&
                          activeScreen.kind !== "final-review" ? (
                            <span
                              className="mt-1.25 inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-md bg-white px-1 text-sm font-medium"
                              style={{ color: BRAND_BLUE }}
                            >
                              {activeScreen.stepIndex + 1}
                            </span>
                          ) : null}
                          <div className="min-w-0 flex-1 text-left">
                            {activeStepEyebrow ? (
                              <p className="mb-1 text-base font-semibold uppercase tracking-[0.06em] text-white/60 md:text-md">
                                {activeStepEyebrow}
                              </p>
                            ) : null}
                            <p className="text-xl font-medium leading-[1.25] text-white md:text-2xl">
                              {activeStepTitle}
                            </p>
                            {activeStepDescription ? (
                              <p
                                className={[
                                  "mt-2 text-lg italic text-white/[0.66] md:text-lg",
                                  isReviewFilePreviewOpen ? "max-w-140" : ""
                                ].join(" ")}
                              >
                                {activeStepDescription}
                              </p>
                            ) : null}
                          </div>
                        </div>

                        {isAccepted ? (
                          <div
                            className="border border-solid p-5"
                            style={{
                              background: GLASS_SURFACE_BG,
                              borderColor: GLASS_SURFACE_BORDER,
                              borderRadius: "18px",
                              boxShadow: GLASS_SURFACE_SHADOW
                            }}
                          >
                            <p
                              className="font-semibold"
                              style={{ fontSize: "18px", color: BRAND_BLUE }}
                            >
                              Intake Completed
                            </p>
                            <p
                              className="mt-2 text-md"
                              style={{ color: `rgba(${intakeFgRgb}, 0.72)` }}
                            >
                              This intake has already been accepted by your
                              legal team.
                            </p>
                          </div>
                        ) : (
                          <>
                            <div className="typeform-step-part typeform-step-part-2 public-intake-typeform-content flex w-full flex-col gap-0 pl-7.5 mobile:pl-0">
                              <div
                                className={[
                                  "w-full",
                                  reviewMode &&
                                  activeReviewStatus === "accepted"
                                    ? "intake-review-accepted"
                                    : reviewMode &&
                                      activeReviewStatus === "rejected"
                                    ? "intake-review-rejected"
                                    : // Revision mode reuses the attorney
                                    // review tint per answerable question:
                                    // requested changes red, everything else
                                    // (accepted) green. Once a flagged answer
                                    // has a new value it reads as normal again.
                                    // Keyed on a single question so
                                    // multi-question section screens are left
                                    // untinted.
                                    isRevisionRequested && activeCommentKey
                                    ? revisionStatusClass(activeCommentKey)
                                    : ""
                                ].join(" ")}
                              >
                                {content}
                              </div>

                              {groupStackScreen ? null : reviewMode &&
                                activeReviewKey ? (
                                <div className="typeform-step-part typeform-step-part-3 mt-6 flex w-full flex-col gap-4">
                                  {(() => {
                                    const isAccepted =
                                      activeReviewStatus === "accepted";
                                    const isRejected =
                                      activeReviewStatus === "rejected";
                                    const isDecided = isAccepted || isRejected;
                                    const btnBase =
                                      "inline-flex min-h-10 items-center gap-1.5 overflow-hidden whitespace-nowrap rounded-md border border-dotted py-2 text-md font-semibold leading-none transition-all duration-300 ease-out";
                                    const collapsed =
                                      "max-w-0 border-0 px-0 opacity-0 pointer-events-none";
                                    const acceptCollapsed = isRejected;
                                    const rejectCollapsed = isAccepted;
                                    return (
                                      <div className="flex items-center">
                                        <button
                                          type="button"
                                          tabIndex={acceptCollapsed ? -1 : 0}
                                          aria-hidden={acceptCollapsed}
                                          aria-pressed={isAccepted}
                                          onClick={() =>
                                            onReviewDecision?.(
                                              activeReviewKey,
                                              isAccepted
                                                ? "pending"
                                                : "accepted"
                                            )
                                          }
                                          className={[
                                            btnBase,
                                            acceptCollapsed
                                              ? collapsed
                                              : "max-w-70 px-4",
                                            isAccepted
                                              ? "border-[#16a34a] bg-[#16a34a] text-white"
                                              : "border-white/45 bg-transparent text-white hover:bg-white/10"
                                          ].join(" ")}
                                        >
                                          <IntakeIcon name="FeatherCheck" />
                                          {isAccepted ? "Accepted" : "Accept"}
                                        </button>
                                        <button
                                          type="button"
                                          tabIndex={rejectCollapsed ? -1 : 0}
                                          aria-hidden={rejectCollapsed}
                                          aria-pressed={isRejected}
                                          onClick={() =>
                                            onReviewDecision?.(
                                              activeReviewKey,
                                              isRejected
                                                ? "pending"
                                                : "rejected"
                                            )
                                          }
                                          className={[
                                            btnBase,
                                            rejectCollapsed
                                              ? collapsed
                                              : [
                                                  "max-w-70 px-4",
                                                  isDecided ? "ml-0" : "ml-3"
                                                ].join(" "),
                                            isRejected
                                              ? "border-[#dc2626] bg-[#dc2626] text-white"
                                              : "border-white/45 bg-transparent text-white hover:bg-white/10"
                                          ].join(" ")}
                                        >
                                          <IntakeIcon name="FeatherMessageSquare" />
                                          {isRejected
                                            ? "Requesting Changes"
                                            : "Request Changes"}
                                        </button>
                                        {isDecided ? (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              onReviewDecision?.(
                                                activeReviewKey,
                                                "pending"
                                              )
                                            }
                                            className="ml-3 inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-base font-medium leading-none text-white/70 transition hover:bg-white/10 hover:text-white"
                                          >
                                            <IntakeIcon name="FeatherRotateCcw" />
                                            Undo
                                          </button>
                                        ) : null}
                                      </div>
                                    );
                                  })()}

                                  {activeReviewThread.length > 0 ||
                                  activeReviewStatus === "rejected" ? (
                                    <div className="public-intake-review-comments flex w-full flex-col gap-2">
                                      {activeReviewThread.map((item, idx) => {
                                        const isAttorney =
                                          item.author === "attorney";
                                        const initials =
                                          item.name
                                            .split(/\s+/)
                                            .filter(Boolean)
                                            .map((p) => p[0]?.toUpperCase())
                                            .join("")
                                            .slice(0, 2) ||
                                          (isAttorney ? "AT" : "CL");
                                        return (
                                          <div
                                            key={`${item.author}-${idx}`}
                                            className="intake-comment-card flex w-full items-start gap-2.5 rounded-2xl px-4 py-3.5"
                                          >
                                            <IntakeAvatar
                                              size="xs"
                                              image={
                                                item.avatarUrl ?? undefined
                                              }
                                              variant={
                                                isAttorney ? "error" : "neutral"
                                              }
                                            >
                                              {initials}
                                            </IntakeAvatar>
                                            <div className="flex min-w-0 grow shrink-0 basis-0 flex-col items-start gap-1">
                                              <div className="flex min-w-0 flex-wrap items-center gap-2">
                                                <span className="intake-comment-name text-caption-bold font-caption-bold truncate">
                                                  {item.name}
                                                </span>
                                                <span className="intake-comment-meta text-caption font-caption">
                                                  {isAttorney
                                                    ? "Attorney"
                                                    : "Client"}
                                                </span>
                                                {typeof item.revisionNumber ===
                                                "number" ? (
                                                  <span className="intake-comment-meta text-caption font-caption">
                                                    {`Revision ${item.revisionNumber}`}
                                                  </span>
                                                ) : null}
                                                {item.timeLabel ? (
                                                  <span className="intake-comment-meta text-caption font-caption">
                                                    {item.timeLabel}
                                                  </span>
                                                ) : null}
                                              </div>
                                              <span
                                                className={[
                                                  "text-caption font-caption whitespace-pre-wrap wrap-break-word",
                                                  isAttorney
                                                    ? "intake-comment-body-attorney"
                                                    : "intake-comment-body-client"
                                                ].join(" ")}
                                              >
                                                {item.text}
                                              </span>
                                            </div>
                                          </div>
                                        );
                                      })}
                                      {activeReviewStatus === "rejected" &&
                                      activeReviewKey
                                        ? (() => {
                                            const key = activeReviewKey;
                                            const committed = activeReviewNote;
                                            const draftPresent =
                                              key in reviewNoteDrafts;
                                            const composerOpen =
                                              draftPresent ||
                                              committed.trim() === "";
                                            const draftValue =
                                              reviewNoteDrafts[key] ??
                                              committed;
                                            const openComposer = () =>
                                              setReviewNoteDrafts((prev) => ({
                                                ...prev,
                                                [key]: committed
                                              }));
                                            const closeComposer = () =>
                                              setReviewNoteDrafts((prev) => {
                                                const next = { ...prev };
                                                delete next[key];
                                                return next;
                                              });
                                            const postNote = () => {
                                              onReviewNoteChange?.(
                                                key,
                                                draftValue.trim()
                                              );
                                              closeComposer();
                                            };
                                            return (
                                              <div className="intake-comment-card flex w-full items-start gap-2.5 rounded-2xl px-4 py-3.5">
                                                <IntakeAvatar
                                                  size="xs"
                                                  image={
                                                    reviewAttorneyAvatarUrl
                                                  }
                                                  variant="error"
                                                >
                                                  {reviewAttorneyInitials}
                                                </IntakeAvatar>
                                                <div className="flex min-w-0 grow shrink-0 basis-0 flex-col items-start gap-1.5">
                                                  <span className="intake-comment-name text-caption-bold font-caption-bold truncate">
                                                    {reviewAttorneyName}
                                                  </span>
                                                  {composerOpen ? (
                                                    <>
                                                      <textarea
                                                        className="intake-comment-textarea w-full resize-y"
                                                        placeholder={tr(
                                                          "leaveComment"
                                                        )}
                                                        value={draftValue}
                                                        onChange={(e) => {
                                                          const val =
                                                            e.target.value;
                                                          setReviewNoteDrafts(
                                                            (prev) => ({
                                                              ...prev,
                                                              [key]: val
                                                            })
                                                          );
                                                        }}
                                                      />
                                                      <div className="mt-1 flex w-full items-center justify-end gap-2">
                                                        {committed.trim() ? (
                                                          <button
                                                            type="button"
                                                            onClick={
                                                              closeComposer
                                                            }
                                                            className="intake-comment-cancel-btn inline-flex items-center rounded-md px-4 py-2.5 text-md font-medium leading-none transition hover:bg-fill-2"
                                                          >
                                                            Cancel
                                                          </button>
                                                        ) : null}
                                                        <button
                                                          type="button"
                                                          disabled={
                                                            !draftValue.trim()
                                                          }
                                                          onClick={postNote}
                                                          className="inline-flex items-center gap-1.5 rounded-md bg-brand-primary px-4 py-2.5 text-md font-semibold leading-none text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-[#b6bcc6] disabled:hover:bg-[#b6bcc6]"
                                                        >
                                                          Post
                                                        </button>
                                                      </div>
                                                    </>
                                                  ) : (
                                                    <span className="intake-comment-body-attorney text-caption font-caption whitespace-pre-wrap wrap-break-word">
                                                      {committed}
                                                    </span>
                                                  )}
                                                </div>
                                                {!composerOpen ? (
                                                  <button
                                                    type="button"
                                                    onClick={openComposer}
                                                    className="intake-comment-edit-btn inline-flex shrink-0 items-center gap-1 rounded-md border border-solid px-2.5 py-1.5 text-sm font-semibold leading-none transition"
                                                  >
                                                    <IntakeIcon
                                                      name="FeatherPencil"
                                                      className="text-base"
                                                    />
                                                    Edit
                                                  </button>
                                                ) : null}
                                              </div>
                                            );
                                          })()
                                        : null}
                                    </div>
                                  ) : null}
                                </div>
                              ) : (
                                <>
                                  {showClientCommentField &&
                                  activeCommentKey &&
                                  activeStepNode
                                    ? renderClientCommentBlock({
                                        key: activeCommentKey,
                                        questionNode: activeStepNode,
                                        groupId: activeCommentGroupId,
                                        instanceId: activeCommentInstanceId,
                                        wrapperClassName:
                                          "typeform-step-part typeform-step-part-2 mt-5 flex w-full flex-col gap-3"
                                      })
                                    : null}
                                  <div className="typeform-step-part typeform-step-part-3 mt-8 flex flex-wrap items-center gap-3">
                                    <Button
                                      className="min-h-10 gap-2 rounded-md bg-white px-4 py-2 text-base font-medium leading-none text-[#111827]! shadow-[0_8px_18px_rgba(0,0,0,0.18)] hover:bg-white/90 active:bg-white disabled:bg-white/40 disabled:text-[#111827]/60! disabled:opacity-100 **:text-[#111827]! **:disabled:text-[#111827]/60!"
                                      variant="primary"
                                      disabled={isTypeformPrimaryDisabled}
                                      onClick={() => {
                                        goToNextTypeformStep();
                                      }}
                                    >
                                      {isSubmitting && isLastTypeformStep ? (
                                        <Spinner />
                                      ) : (
                                        <IntakeIcon
                                          className="text-base"
                                          name={
                                            isLastTypeformStep
                                              ? "FeatherCheckCircle"
                                              : "FeatherArrowRight"
                                          }
                                        />
                                      )}
                                      {primaryActionLabel}
                                    </Button>
                                    {!isLastTypeformStep &&
                                    !activeDocumentUploadKey ? (
                                      <span className="text-caption font-caption text-subtext-color">
                                        press Enter ↵
                                      </span>
                                    ) : isLastTypeformStep &&
                                      isRevisionRequested &&
                                      requestedKeys.length > 0 ? (
                                      <span className="text-caption font-caption text-subtext-color">
                                        {requestedKeys.length === 1
                                          ? "Only this answer is sent again"
                                          : `Only these ${requestedKeys.length} answers are sent again`}
                                      </span>
                                    ) : null}
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="pl-7.5 mobile:pl-0">
                              {renderUploadedDocumentsInlineSection()}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </IntakeScopedDiv>
                </div>
                {shouldShowDocumentRail ? (
                  <div className="absolute right-0 top-0 z-2 hidden h-full min-h-0 flex-col px-6 py-8 min-[62em]:flex min-[62em]:w-70 xl:w-80">
                    <div className="flex h-full min-h-0 w-full min-w-0 flex-col items-start gap-3">
                      <div className="flex w-full min-w-0 flex-none flex-col overflow-hidden rounded-xl border border-solid border-white/90 bg-[linear-gradient(135deg,rgba(255,255,255,0.99)_0%,rgba(255,255,255,0.96)_58%,rgba(248,250,252,0.94)_100%)] shadow-[0_18px_50px_rgba(0,0,0,0.20),inset_0_1px_0_rgba(255,255,255,1),inset_0_-1px_0_rgba(203,213,225,0.18)] backdrop-blur-xl">
                        <div className="flex w-full items-start justify-between gap-3 border-b border-solid border-slate-200/70 bg-white/70 px-3 py-2">
                          <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
                            <span className="min-w-0 max-w-full truncate text-left text-caption-bold font-caption-bold text-fg">
                              {uploadedDocumentSectionTitle}
                            </span>
                          </div>
                          <span className="flex-none text-caption-bold font-caption-bold font-semibold text-fg-2">
                            {activeDocumentFiles.length}{" "}
                            {activeDocumentFiles.length === 1
                              ? "file"
                              : "files"}
                          </span>
                        </div>
                        <div className="flex w-full items-center justify-between gap-3 px-3 py-3">
                          <span className="text-caption-bold font-caption-bold uppercase text-fg-2">
                            Sort
                          </span>
                          {renderUploadedDocumentSortMenu()}
                        </div>
                      </div>
                      <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col gap-2 overflow-y-auto pr-1">
                        {activeDocumentPendingUploads.map((p) =>
                          renderPendingUploadCard(p)
                        )}
                        {sortedActiveDocumentFiles.map((f, index) =>
                          renderUploadedIntakeFileCard(f, index)
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}
                {isReviewFilePreviewOpen &&
                previewFile &&
                isReviewPreviewDesktop ? (
                  <div
                    className="evidence-preview-genie absolute right-0 top-0 z-3 flex h-full w-[min(620px,46vw)] px-6 py-8"
                    data-state={
                      isQuickPreviewOverlayVisible ? "open" : "closed"
                    }
                    style={
                      {
                        "--evidence-preview-origin-y": "50%"
                      } as React.CSSProperties
                    }
                  >
                    {renderReviewFilePreviewCard()}
                  </div>
                ) : null}
              </div>
            )}

            {/* forceSinglePage also hides this because the only control left in
                single-page is the layout switcher, which would let the reader
                leave the mode the caller pinned. */}
            {intakeControlsLayout === "hidden" ? null : (
              <div className="absolute bottom-8 right-6 z-20 flex items-center gap-2">
                <div className="flex h-11 items-stretch overflow-hidden rounded-lg bg-white shadow-[0_12px_28px_rgba(32,28,209,0.22)]">
                  {!isAwaitingReview && !isAccepted ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          aria-label={tr("displaySettings")}
                          className="flex h-full w-11 items-center justify-center bg-fill-2 text-lg text-[#111827] transition-colors hover:bg-fill-3 active:bg-fill-4"
                        >
                          <IntakeIcon name="FeatherSettings" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        side="top"
                        align="end"
                        sideOffset={8}
                        className={INTAKE_DISPLAY_MENU_CLASSNAME}
                      >
                        <DropdownMenuLabel
                          className={INTAKE_DISPLAY_MENU_LABEL_CLASSNAME}
                        >
                          Display Settings
                        </DropdownMenuLabel>
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger className="h-10 w-full cursor-pointer gap-3 rounded-lg px-3 transition-colors hover:bg-fill-1 focus:bg-fill-1 data-open:bg-fill-1 [&>svg:last-child]:text-fg-4">
                            <IntakeIcon
                              className="text-lg text-fg-2"
                              name="FeatherLayout"
                            />
                            <span className="grow shrink-0 basis-0 text-md font-medium text-fg">
                              Layout
                            </span>
                            <span className="text-base text-fg-3">
                              {layoutMode === "single_page"
                                ? "Single Page"
                                : "Guided"}
                            </span>
                          </DropdownMenuSubTrigger>
                          <DropdownMenuPortal>
                            <DropdownMenuSubContent
                              sideOffset={8}
                              alignOffset={-6}
                              className={cn(
                                INTAKE_DISPLAY_MENU_CLASSNAME,
                                "min-w-65"
                              )}
                            >
                              <DropdownMenuLabel
                                className={INTAKE_DISPLAY_MENU_LABEL_CLASSNAME}
                              >
                                Layout
                              </DropdownMenuLabel>
                              <DropdownMenuItem
                                className={INTAKE_DISPLAY_MENU_ITEM_CLASSNAME}
                                onSelect={() => {
                                  if (layoutMode === "typeform") return;
                                  setLayoutMode("typeform");
                                  setStepTransitionDirection("forward");
                                  setActiveStepIndex(
                                    resumeTargetStepIndex + welcomeOffset
                                  );
                                }}
                              >
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-fill-1 text-fg group-data-highlighted/dropdown-menu-item:bg-elevated">
                                  <IntakeIcon
                                    className="text-lg"
                                    name="FeatherLayers"
                                  />
                                </div>
                                <div className="flex grow shrink-0 basis-0 flex-col">
                                  <span className="text-md font-medium text-fg">
                                    Guided
                                  </span>
                                  <span className="text-sm leading-tight text-fg-3">
                                    One question at a time
                                  </span>
                                </div>
                                {layoutMode === "typeform" ? (
                                  <IntakeIcon
                                    className="text-lg text-brand-600"
                                    name="FeatherCheck"
                                  />
                                ) : null}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className={INTAKE_DISPLAY_MENU_ITEM_CLASSNAME}
                                onSelect={() => {
                                  if (layoutMode === "single_page") return;
                                  autosaveProgress();
                                  setLayoutMode("single_page");
                                }}
                              >
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-fill-1 text-fg group-data-highlighted/dropdown-menu-item:bg-elevated">
                                  <IntakeIcon
                                    className="text-lg"
                                    name="FeatherFileText"
                                  />
                                </div>
                                <div className="flex grow shrink-0 basis-0 flex-col">
                                  <span className="text-md font-medium text-fg">
                                    Single Page
                                  </span>
                                  <span className="text-sm leading-tight text-fg-3">
                                    All questions on one page
                                  </span>
                                </div>
                                {layoutMode === "single_page" ? (
                                  <IntakeIcon
                                    className="text-lg text-brand-600"
                                    name="FeatherCheck"
                                  />
                                ) : null}
                              </DropdownMenuItem>
                            </DropdownMenuSubContent>
                          </DropdownMenuPortal>
                        </DropdownMenuSub>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                  {intakeControlsLayout === "with-step-arrows" ? (
                    <>
                      <Button
                        aria-label={tr("previousStep")}
                        variant="primary"
                        disabled={activeStepIndex === 0}
                        className={cn(
                          "h-full! w-11 min-w-11 rounded-none border-l border-solid border-line-3 bg-white text-[#111827] shadow-none first:border-l-0 hover:bg-fill-1 active:bg-fill-2 disabled:bg-white disabled:text-[#111827]/35 disabled:opacity-100 **:text-[#111827] **:disabled:text-[#111827]/35",
                          isResponsivePreview ? "flex" : "hidden lg:flex"
                        )}
                        onClick={goToPreviousTypeformStep}
                      >
                        <IntakeIcon
                          name="FeatherChevronUp"
                          className="text-base"
                        />
                      </Button>
                      <Button
                        aria-label={tr("nextStep")}
                        variant="primary"
                        disabled={
                          isLastTypeformStep || isTypeformPrimaryDisabled
                        }
                        className={cn(
                          "h-full! w-11 min-w-11 rounded-none border-l border-solid border-line-3 bg-white text-[#111827] shadow-none hover:bg-fill-1 active:bg-fill-2 disabled:bg-white disabled:text-[#111827]/35 disabled:opacity-100 **:text-[#111827] **:disabled:text-[#111827]/35",
                          isResponsivePreview ? "flex" : "hidden lg:flex"
                        )}
                        onClick={goToNextTypeformStep}
                      >
                        <IntakeIcon
                          name="FeatherChevronDown"
                          className="text-base"
                        />
                      </Button>
                    </>
                  ) : null}
                </div>
              </div>
            )}
            {lastSavedLabel && !isWelcomeStep ? (
              <div className="static z-20 mt-6 w-full max-w-none px-4 pb-6 lg:absolute lg:bottom-8 lg:left-6 lg:mt-0 lg:w-auto lg:max-w-[min(360px,calc(100vw-220px))] lg:px-0 lg:pb-0">
                <span className="text-caption font-caption text-white/65">
                  {lastSavedLabel}
                </span>
              </div>
            ) : null}
          </div>
        </div>

        <Dialog
          // A forced one-page record is being read as history, so the
          // non-dismissible "we're reviewing this" screen would just hide the
          // answers the reader came for.
          open={isAwaitingReview && !forceSinglePage}
          onOpenChange={() => undefined}
        >
          <DialogContent
            showCloseButton={false}
            onEscapeKeyDown={(event) => event.preventDefault()}
            onInteractOutside={(event) => event.preventDefault()}
            className="top-0 left-0 flex h-dvh max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-y-auto rounded-none p-0 shadow-none ring-0 sm:max-w-none data-open:animate-none! data-closed:animate-none!"
            style={{ background: intakePageBg, color: intakeFgSolid }}
          >
            <div className="relative flex min-h-dvh flex-1 flex-col items-center justify-center p-6">
              {showResolvedFirmLogo && lawFirmLogoUrl ? (
                <img
                  src={lawFirmLogoUrl}
                  alt={lawFirmName}
                  className="absolute left-1/2 top-6 max-h-10 max-w-55 -translate-x-1/2 object-contain opacity-95 md:top-10 md:max-h-12"
                />
              ) : null}

              <IntakeScopedDiv
                className="flex max-w-140 flex-col items-center gap-6 text-center md:gap-7"
                styles={{
                  animation:
                    "intakeDoneIn 620ms cubic-bezier(0.22, 1, 0.36, 1) both",
                  "@keyframes intakeDoneIn": {
                    "0%": { opacity: 0, transform: "translateY(16px)" },
                    "100%": { opacity: 1, transform: "translateY(0)" }
                  },
                  "@media (prefers-reduced-motion: reduce)": {
                    animation: "none"
                  }
                }}
              >
                <IntakeScopedDiv
                  className="flex size-21 items-center justify-center rounded-full border border-solid md:size-24"
                  style={{
                    color: intakeFgSolid,
                    background: `rgba(${intakeFgRgb}, 0.10)`,
                    borderColor: `rgba(${intakeFgRgb}, 0.24)`
                  }}
                  styles={{
                    boxShadow: `0 0 0 10px rgba(${intakeFgRgb}, 0.04)`,
                    animation:
                      "intakeDoneBadge 720ms cubic-bezier(0.34, 1.56, 0.64, 1) both",
                    "@keyframes intakeDoneBadge": {
                      "0%": { opacity: 0, transform: "scale(0.6)" },
                      "100%": { opacity: 1, transform: "scale(1)" }
                    },
                    "@media (prefers-reduced-motion: reduce)": {
                      animation: "none"
                    }
                  }}
                >
                  <IntakeIcon
                    className="text-[40px] leading-none"
                    name="FeatherCheck"
                  />
                </IntakeScopedDiv>

                <div className="flex flex-col items-center gap-3">
                  <DialogTitle
                    className="font-semibold"
                    style={{
                      fontSize: isMdUp ? "34px" : "28px",
                      lineHeight: 1.15
                    }}
                  >
                    {tr("thankYou")}
                  </DialogTitle>
                  <DialogDescription
                    style={{
                      fontSize: isMdUp ? "18px" : "16px",
                      lineHeight: 1.5,
                      color: `rgba(${intakeFgRgb}, 0.66)`
                    }}
                  >
                    {tr("thankYouBody")}
                  </DialogDescription>
                  {fromPortal ? (
                    <Link
                      to={CLIENT_PORTAL_PATH}
                      className="mt-2 inline-flex min-h-11 items-center gap-2 rounded-lg bg-white px-4 text-lg font-semibold text-[#111827] shadow-[0_8px_18px_rgba(0,0,0,0.18)] hover:bg-white/90"
                    >
                      {tr("backToPortal")}
                    </Link>
                  ) : null}
                </div>
              </IntakeScopedDiv>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={Boolean(attentionUploads)}
          onOpenChange={(open) => {
            if (!open) setAttentionUploads(null);
          }}
        >
          <DialogContent
            showCloseButton={false}
            className="max-w-100 sm:max-w-100"
          >
            <DialogHeader className="gap-1">
              <DialogTitle>
                {tr("attention", {
                  count: attentionUploads?.length ?? 0
                })}
              </DialogTitle>
              <DialogDescription>{tr("attentionBody")}</DialogDescription>
            </DialogHeader>
            <ul className="flex w-full flex-col gap-1.5">
              {(attentionUploads ?? []).map((u) => (
                <li
                  key={u.id}
                  className="flex w-full items-start gap-2 text-sm"
                >
                  <AlertCircle className="mt-0.5 size-3.5 shrink-0 text-danger" />
                  <span className="min-w-0">
                    <span className="font-medium text-fg">{u.name}</span>
                    <span className="text-fg-3"> · {u.reason}</span>
                  </span>
                </li>
              ))}
            </ul>
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setAttentionUploads(null)}
              >
                Go Back
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={() => {
                  setAttentionUploads(null);
                  submit({ confirmedUploads: true }).catch(() => undefined);
                }}
              >
                {tr("sendAnyway")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={Boolean(intakeFilePendingDelete)}
          onOpenChange={(open) => {
            if (!open) handleCloseDeleteIntakeFileDialog();
          }}
        >
          <DialogContent
            showCloseButton={false}
            className="max-w-90 sm:max-w-90"
          >
            <DialogHeader className="gap-1">
              <DialogTitle>
                Delete {intakeFilePendingDelete?.file.filename}?
              </DialogTitle>
              <DialogDescription>
                You can upload it again if needed.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                disabled={isDeletingIntakeFile}
                onClick={handleCloseDeleteIntakeFileDialog}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={isDeletingIntakeFile}
                onClick={handleConfirmDeleteIntakeFile}
              >
                {isDeletingIntakeFile ? <Spinner /> : null}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {shouldShowDocumentRail && previewFile
          ? (() => {
              const duplicateUrl = previewDuplicateKey
                ? duplicatePreviewUrls[previewDuplicateKey] ?? null
                : null;
              const isDuplicateLoading = previewDuplicateKey
                ? Boolean(duplicatePreviewLoadingIds[previewDuplicateKey])
                : false;
              const duplicateError = previewDuplicateKey
                ? duplicatePreviewErrors[previewDuplicateKey] ?? null
                : null;
              const showPreviewLoader =
                isQuickPreviewLoading ||
                (!quickPreviewBlobUrl && !quickPreviewError);
              const showDuplicateCompare = Boolean(previewDuplicateMatch);
              const previewReviewCheckItems = getIntakeFileReviewCheckItems(
                previewFile,
                suppressedDuplicateMatchIds
              );
              const selectedReviewWarning = previewReviewCheckItems[0];
              const duplicateReviewWarning =
                previewReviewCheckItems.find(
                  (item) => item.type === "duplicate"
                ) ?? selectedReviewWarning;
              const previewUnavailableMessage =
                quickPreviewError ||
                getIntakeFilePreviewUnavailableMessage(previewFile);
              const duplicateIntakeFile =
                previewDuplicateMatch &&
                [
                  "outgoing_intake",
                  "public_outgoing_intake",
                  "public_intake"
                ].includes(previewDuplicateMatch.source || "")
                  ? activeDocumentFiles.find(
                      (file) => file.file_id === previewDuplicateMatch.id
                    ) ?? null
                  : null;
              return (
                <div className="pointer-events-none absolute inset-0 z-2 hidden h-full w-full min-w-0 items-stretch justify-end gap-1.5 px-2 py-8 lg:flex">
                  <div
                    className={[
                      "pointer-events-auto h-full flex-none items-stretch min-w-0",
                      "evidence-preview-genie",
                      showDuplicateCompare
                        ? "w-[min(820px,55vw)]"
                        : "w-[min(620px,42vw)]"
                    ].join(" ")}
                    data-state={
                      isQuickPreviewOverlayVisible ? "open" : "closed"
                    }
                    style={
                      {
                        "--evidence-preview-origin-y": `${quickPreviewTransformOriginY}px`
                      } as React.CSSProperties
                    }
                  >
                    <div className="h-full w-full min-w-0 overflow-hidden rounded-xl border border-solid border-neutral-border bg-fill-1 shadow-[90px_0_180px_rgba(15,23,42,0.10),-90px_0_180px_rgba(15,23,42,0.08),0_34px_140px_rgba(15,23,42,0.07)]">
                      <div className="flex h-full w-full min-h-0 min-w-0 flex-col items-stretch">
                        {showDuplicateCompare ? (
                          <div className="flex h-full min-h-0 w-full flex-col">
                            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-solid border-neutral-border bg-default-background px-3 py-2">
                              <span
                                className={[
                                  "flex min-w-0 flex-1 items-start gap-1.5 text-caption font-caption",
                                  duplicateReviewWarning?.status === "failed"
                                    ? "text-error-600"
                                    : "text-warning-700"
                                ].join(" ")}
                              >
                                <IntakeIcon
                                  className="mt-0.5 shrink-0 text-caption font-caption"
                                  name={
                                    duplicateReviewWarning?.status === "failed"
                                      ? "FeatherAlertCircle"
                                      : "FeatherAlertTriangle"
                                  }
                                />
                                <span className="min-w-0 flex-1 whitespace-normal wrap-break-word">
                                  {duplicateReviewWarning?.message ||
                                    "Possible duplicate evidence."}
                                </span>
                              </span>
                              <button
                                type="button"
                                aria-label="Close preview"
                                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-fg-2 transition hover:bg-fill-1 active:bg-fill-1"
                                onClick={closeQuickPreviewOverlay}
                              >
                                <IntakeIcon name="FeatherX" />
                              </button>
                            </div>
                            <div className="flex min-h-0 flex-1 items-stretch">
                              <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                                <div className="flex shrink-0 items-center justify-between gap-3 border-b border-solid border-neutral-border bg-default-background px-3 py-2">
                                  <span className="min-w-0 truncate text-caption-bold font-caption-bold text-default-font">
                                    <strong>This file:</strong>{" "}
                                    {previewFile.filename}
                                  </span>
                                  {renderPublicPreviewDeleteAction(previewFile)}
                                </div>
                                {quickPreviewBlobUrl ? (
                                  <div className="relative min-h-0 flex-1 overflow-hidden">
                                    <PdfPreview
                                      file={quickPreviewBlobUrl}
                                      ariaLabel={`${previewFile.filename} preview`}
                                      showControls={false}
                                    />
                                  </div>
                                ) : (
                                  <div className="relative flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-5 py-6 text-center">
                                    {showPreviewLoader ? (
                                      <LoadingState text="Loading preview..." />
                                    ) : (
                                      <>
                                        <span className="text-body-bold font-body-bold text-default-font">
                                          Preview unavailable
                                        </span>
                                        <span className="text-body font-body text-subtext-color">
                                          {previewUnavailableMessage}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="flex min-h-0 min-w-0 flex-1 flex-col border-l border-solid border-neutral-border">
                                <div className="flex shrink-0 items-center justify-between gap-3 border-b border-solid border-neutral-border bg-default-background px-3 py-2">
                                  <span className="min-w-0 truncate text-caption-bold font-caption-bold text-default-font">
                                    <strong>Duplicate:</strong>{" "}
                                    {previewDuplicateMatch?.docNames ||
                                      "Matched file"}
                                  </span>
                                  {duplicateIntakeFile
                                    ? renderPublicPreviewDeleteAction(
                                        duplicateIntakeFile
                                      )
                                    : null}
                                </div>
                                {duplicateUrl ? (
                                  <div className="relative min-h-0 flex-1 overflow-hidden">
                                    <PdfPreview
                                      file={duplicateUrl}
                                      ariaLabel={`${
                                        previewDuplicateMatch?.docNames ||
                                        "Duplicate file"
                                      } preview`}
                                      showControls={false}
                                    />
                                  </div>
                                ) : (
                                  <div className="relative flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-5 py-6 text-center">
                                    {isDuplicateLoading ? (
                                      <LoadingState text="Loading duplicate preview..." />
                                    ) : (
                                      <>
                                        <span className="text-body-bold font-body-bold text-default-font">
                                          Preview unavailable
                                        </span>
                                        <span className="text-body font-body text-subtext-color">
                                          {duplicateError ||
                                            "Duplicate preview is not available."}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : quickPreviewBlobUrl ? (
                          <div className="flex h-full min-h-0 w-full flex-col">
                            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-solid border-neutral-border bg-default-background px-3 py-2">
                              <div className="flex min-w-0 flex-col gap-1">
                                <span className="min-w-0 truncate text-caption-bold font-caption-bold text-default-font">
                                  {previewFile.filename}
                                </span>
                                {selectedReviewWarning ? (
                                  <span
                                    className={[
                                      "flex min-w-0 items-start gap-1.5 text-caption font-caption",
                                      selectedReviewWarning.status === "failed"
                                        ? "text-error-600"
                                        : "text-warning-700"
                                    ].join(" ")}
                                  >
                                    <IntakeIcon
                                      className="mt-0.5 shrink-0 text-caption font-caption"
                                      name={
                                        selectedReviewWarning.status ===
                                        "failed"
                                          ? "FeatherAlertCircle"
                                          : "FeatherAlertTriangle"
                                      }
                                    />
                                    <span className="min-w-0 flex-1 whitespace-normal wrap-break-word">
                                      {selectedReviewWarning.message}
                                    </span>
                                  </span>
                                ) : null}
                              </div>
                              <button
                                type="button"
                                aria-label="Close preview"
                                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-fg-2 transition hover:bg-fill-1 active:bg-fill-1"
                                onClick={closeQuickPreviewOverlay}
                              >
                                <IntakeIcon name="FeatherX" />
                              </button>
                            </div>
                            <div className="relative min-h-0 flex-1 overflow-hidden">
                              <PdfPreview
                                file={quickPreviewBlobUrl}
                                ariaLabel={`${previewFile.filename} preview`}
                                actions={publicPreviewDeleteActions(
                                  previewFile
                                )}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="flex h-full min-h-0 w-full flex-col">
                            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-solid border-neutral-border bg-default-background px-3 py-2">
                              <div className="flex min-w-0 flex-col gap-1">
                                <span className="min-w-0 truncate text-caption-bold font-caption-bold text-default-font">
                                  {previewFile.filename}
                                </span>
                                {selectedReviewWarning ? (
                                  <span
                                    className={[
                                      "flex min-w-0 items-start gap-1.5 text-caption font-caption",
                                      selectedReviewWarning.status === "failed"
                                        ? "text-error-600"
                                        : "text-warning-700"
                                    ].join(" ")}
                                  >
                                    <IntakeIcon
                                      className="mt-0.5 shrink-0 text-caption font-caption"
                                      name={
                                        selectedReviewWarning.status ===
                                        "failed"
                                          ? "FeatherAlertCircle"
                                          : "FeatherAlertTriangle"
                                      }
                                    />
                                    <span className="min-w-0 flex-1 whitespace-normal wrap-break-word">
                                      {selectedReviewWarning.message}
                                    </span>
                                  </span>
                                ) : null}
                              </div>
                              <button
                                type="button"
                                aria-label="Close preview"
                                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-fg-2 transition hover:bg-fill-1 active:bg-fill-1"
                                onClick={closeQuickPreviewOverlay}
                              >
                                <IntakeIcon name="FeatherX" />
                              </button>
                            </div>
                            <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-5 py-6 text-center">
                              {showPreviewLoader ? (
                                <LoadingState text="Loading preview..." />
                              ) : (
                                <>
                                  <span className="text-body-bold font-body-bold text-default-font">
                                    Preview unavailable
                                  </span>
                                  <span className="text-body font-body text-subtext-color">
                                    {previewUnavailableMessage}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="h-full w-64 flex-none xl:w-74" />
                </div>
              );
            })()
          : null}
        {isCaseyAvailable ? (
          <AskCasey
            token={safeToken}
            answers={collectMeaningfulAnswers()}
            onApplyProposal={applyAssistantProposal}
            activeQuestionId={caseyFocusQuestionId}
            attorneyName={intake?.attorney_name}
            lawFirmName={intake?.law_firm_name}
            isDarkBg={isDarkIntakeBg}
            intakeControls={intakeControlsLayout}
          />
        ) : null}
      </IntakeScopedDiv>
    );
  })();
}
